/**
 * Capital — one module, no build step, no jQuery.
 *
 * Custom elements:
 *   <reveal-on-scroll>   reveal system (IntersectionObserver, threshold 0.4)
 *   <product-card>       card behaviour hooks (quick add)
 *   <countdown-timer>    timer block
 *   <count-up>           stat count-up (900ms, shared easing)
 *   <cart-drawer>        slide-in cart (Section Rendering API refresh)
 *
 * Events:
 *   document 'cart:changed'  detail: { cart? } — fired after any cart
 *   mutation; the drawer and [data-cart-count] badges listen.
 *
 * All motion respects prefers-reduced-motion and the theme's
 * `motion_reduce` escape hatch (html.motion-reduced).
 */

const routes = window.Capital?.routes ?? {
  cart: '/cart',
  cartAdd: '/cart/add',
  cartChange: '/cart/change',
  cartUpdate: '/cart/update',
};

// Liquid's `t` filter HTML-escapes translations, so French `d'ajouter`
// arrives as `d&#39;ajouter`. Every string here is written with textContent,
// which would show the entity, so decode each one once on the way in.
const decodeEntities = (text) =>
  typeof text === 'string' ? new DOMParser().parseFromString(text, 'text/html').documentElement.textContent : text;
const strings = Object.fromEntries(
  Object.entries(window.Capital?.strings ?? {}).map(([key, text]) => [key, decodeEntities(text)])
);

export const motionReduced = () =>
  document.documentElement.classList.contains('motion-reduced') ||
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* --------------------------------------------------------------------------
   Motion contract (WCAG 2.2.2) — shared persistent pause/play control.
   Every indefinite auto-motion surface renders snippets/motion-toggle.liquid
   (a [data-motion-toggle] button, hidden until JS binds it). This wires that
   button to a surface callback, owning aria-pressed and the swapped label,
   and returns a handle so a surface can drive the state itself (e.g. start
   paused under reduced motion). No-JS: the button stays hidden and motion
   simply never runs. Reuses the hero's persistent-pause pattern for every
   surface, so a pause survives pointerleave (unlike hover/focus, which fails
   on touch).
   -------------------------------------------------------------------------- */
export function bindMotionToggle(button, onChange) {
  if (!button) return null;
  const labelEl = button.querySelector('[data-motion-toggle-label]');
  const pauseLabel = button.dataset.pauseLabel || labelEl?.textContent || 'Pause';
  const playLabel = button.dataset.playLabel || 'Play';
  let paused = false;
  const render = () => {
    button.setAttribute('aria-pressed', String(paused));
    if (labelEl) labelEl.textContent = paused ? playLabel : pauseLabel;
  };
  const set = (next) => {
    if (next === paused) return;
    paused = next;
    render();
    onChange(paused);
  };
  button.hidden = false;
  button.addEventListener('click', () => set(!paused));
  render();
  return { set, toggle: () => set(!paused), get paused() { return paused; } };
}

/* --------------------------------------------------------------------------
   Reveal system
   -------------------------------------------------------------------------- */
class RevealOnScroll extends HTMLElement {
  static observer = null;
  static editorWatched = false;

  // Fraction of a section that must be on screen before it reveals.
  static RATIO = 0.4;

  connectedCallback() {
    // Reduced motion, or the Theme Editor: reveal immediately. The editor
    // re-renders the whole section on every setting change, tearing this
    // element down and rebuilding it in its hidden state; a scroll-gated
    // reveal then leaves the freshly-rendered content invisible until the
    // merchant scrolls it back past the ratio (QA #227 — a landscape video
    // sitting only partly in view stayed blank on each edit). Showing it at
    // once keeps what they're editing on screen. The storefront path below is
    // untouched — Shopify.designMode is undefined outside the editor.
    if (motionReduced() || (window.Shopify && window.Shopify.designMode)) {
      this.classList.add('is-revealed');
      // Some editor re-renders reuse this element in place and reset its class
      // list to the server HTML (which has no is-revealed) WITHOUT running
      // connectedCallback again, leaving the content stuck hidden — QA #278 saw
      // the section blank out mid-edit with a tall image above the stream.
      // Re-assert is-revealed on every section reload (editor only) to cover
      // that path. The storefront is untouched — designMode is undefined there.
      if (window.Shopify && window.Shopify.designMode) RevealOnScroll.watchEditorReloads();
      return;
    }

    // Above the fold (#521): section-frame put this group in on-load mode, so
    // capital.css already animated it in from a keyframe at first paint —
    // observing it would only add is-revealed to content that is on screen
    // and settled. Leaving the class off also keeps it free for the
    // hero-slideshow / product-showcase replays, which drive their re-entrance
    // by toggling it.
    if (this.closest('.reveal-on-load')) return;

    if (!RevealOnScroll.observer) {
      RevealOnScroll.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            // A section taller than 1 / RATIO viewports (a portrait video is
            // ~2.5 screens tall, #212) can never cover RATIO of its own area,
            // so it would sit at opacity 0 forever. Reveal those the moment
            // they enter (the threshold-0 notification) rather than waiting for
            // a ratio they cannot reach; shorter sections still wait for RATIO.
            const rootHeight = entry.rootBounds?.height || window.innerHeight;
            const reachesRatio =
              entry.boundingClientRect.height <= rootHeight / RevealOnScroll.RATIO;
            if (reachesRatio && entry.intersectionRatio < RevealOnScroll.RATIO) {
              return;
            }
            entry.target.classList.add('is-revealed');
            RevealOnScroll.observer.unobserve(entry.target);
          });
        },
        { threshold: [0, RevealOnScroll.RATIO], rootMargin: '0px 0px -5% 0px' }
      );
    }

    // Already in view (above the fold on load): reveal on next frame so the
    // transition still runs once.
    requestAnimationFrame(() => RevealOnScroll.observer.observe(this));
  }

  disconnectedCallback() {
    RevealOnScroll.observer?.unobserve(this);
  }

  // Registered once, in the editor only: on any section reload, re-reveal every
  // stream in the reloaded section so an in-place re-render that reset the class
  // list can't leave content hidden (QA #278).
  static watchEditorReloads() {
    if (RevealOnScroll.editorWatched) return;
    RevealOnScroll.editorWatched = true;
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll('reveal-on-scroll').forEach((reveal) => {
        reveal.classList.add('is-revealed');
      });
    });
  }
}

/**
 * Replay a reveal group's entrance — the slide-change choreography shared by
 * <hero-slideshow> and <product-showcase>. Drops is-revealed, forces a
 * reflow, then puts it back.
 *
 * On its own that toggle doesn't visibly replay anything: the hidden state
 * is never painted, so when is-revealed returns the browser finds the running
 * transition already at its end value and cancels it (measured in Chromium,
 * Firefox and WebKit). Content that never left the screen just stays settled.
 * `restart: true` is for a group coming back on screen (a hero copy layer,
 * #372): it commits the hidden state with transitions off (is-replaying in
 * capital.css §10) before is-revealed returns, so the entrance really plays.
 *
 * Above the fold the section is in CSS on-load mode (#521): its entrance comes
 * from a keyframe, and a keyframe out-ranks the transition this replay depends
 * on — the content would snap in instead of animating. So hand the section over
 * to the JS reveal first. Every group in it is marked revealed before the frame
 * class goes, because the observer skipped them all while they were on-load;
 * without that they would fall back to the hidden rules and blank out (a
 * product-showcase panel other than the one being replayed would vanish).
 * One-way per section: after the first slide change, JS owns the reveal.
 */
function replayReveal(reveal, { restart = false } = {}) {
  if (!reveal || motionReduced()) return;
  const frame = reveal.closest('.reveal-on-load');
  if (frame) {
    frame.querySelectorAll('reveal-on-scroll').forEach((group) => group.classList.add('is-revealed'));
    frame.classList.remove('reveal-on-load');
  }
  if (restart) reveal.classList.add('is-replaying');
  reveal.classList.remove('is-revealed');
  void reveal.offsetWidth;
  reveal.classList.remove('is-replaying');
  reveal.classList.add('is-revealed');
}

/**
 * <contact-success> — announces the contact form success on reload.
 *
 * The form uses Shopify's full-reload {% form 'contact' %} path, which redirects
 * back to a URL ending in a #fragment. Browsers skip the `autofocus` attribute
 * on a fragment-navigated load, and a role="status" region present at initial
 * load is never announced, so the success rendered but focus stayed on <body>
 * and a screen reader heard silence. This element only exists in the DOM right
 * after a successful submit, so moving focus to itself as the page arrives is
 * always the right moment: it lands focus on the status message (announcing it
 * to assistive tech) and scrolls it into view. Degrades to a visible,
 * already-rendered success message without JS.
 */
class ContactSuccess extends HTMLElement {
  connectedCallback() {
    // Show the message wherever the page lands. The redirect comes back
    // scrolled to the form, below the section's intro, and the scroll reveal
    // waits for RevealOnScroll.RATIO of the whole section to be on screen.
    // The message is far shorter than the form it replaces, so on a phone the
    // section could stay under that ratio and the message sat at opacity 0
    // until the customer scrolled back up (#700).
    this.closest('reveal-on-scroll')?.classList.add('is-revealed');

    // Focus once the page has finished loading, as <newsletter-success> does
    // (QA #265): until then the browser is still jumping to the redirect's
    // #fragment, which can take focus back to <body> after an earlier frame.
    const focusStatus = () => requestAnimationFrame(() => this.focus());
    if (document.readyState === 'complete') {
      focusStatus();
    } else {
      window.addEventListener('load', focusStatus, { once: true });
    }
  }
}

/**
 * <contact-fields> — progressively enhances the contact form's fields.
 *
 * Two enhancements, both pure add-ons over markup that already works with no
 * JS (the form is Shopify's full-reload {% form 'contact' %}):
 *   1. Dropdown — swaps each field's native <select> for the theme's custom
 *      dropdown (a <details> panel mirroring the announcement-bar locale
 *      pickers, #256). The native <select> stays in the DOM as the value
 *      carrier and the no-JS / validation fallback: it is only visually
 *      hidden (clip, still focusable) so a required select still shows its
 *      validation bubble. Selecting a custom option writes the value back to
 *      it, so form submission is unchanged.
 *   2. Phone — constrains the Phone field to phone characters (digits, spaces
 *      and + ( ) - ), stripping anything else as it is typed. The input keeps
 *      a matching `pattern` guard so letters are rejected on submit with no JS.
 *
 * Open/close of the custom dropdowns (one-open-at-a-time, outside-click and
 * Escape) is handled here for the whole form, mirroring <announcement-bar>.
 */
class ContactFields extends HTMLElement {
  connectedCallback() {
    this.dropdowns = [];
    this.querySelectorAll('[data-contact-phone]').forEach((input) => this.enhancePhone(input));
    this.querySelectorAll('[data-contact-select]').forEach((field) => this.enhanceSelect(field));

    if (!this.dropdowns.length) return;

    // Only one open at a time (matches <announcement-bar> locale dropdowns).
    this.dropdowns.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        this.dropdowns.forEach((other) => {
          if (other !== details) other.open = false;
        });
      });
    });
    this.onDocClick = (event) => {
      this.dropdowns.forEach((details) => {
        if (details.open && !details.contains(event.target)) details.open = false;
      });
    };
    this.onDocKeydown = (event) => {
      if (event.key !== 'Escape') return;
      this.dropdowns.forEach((details) => {
        if (!details.open) return;
        details.open = false;
        details.querySelector('summary')?.focus();
      });
    };
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onDocKeydown);
  }

  disconnectedCallback() {
    if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
    if (this.onDocKeydown) document.removeEventListener('keydown', this.onDocKeydown);
  }

  enhancePhone(input) {
    if (input.dataset.enhanced) return;
    input.dataset.enhanced = 'true';
    // Allowed: digits, spaces and + ( ) - — the set the pattern attribute lists.
    input.addEventListener('input', () => {
      const cleaned = input.value.replace(/[^0-9+() -]/g, '');
      if (cleaned === input.value) return;
      const dropped = input.value.length - cleaned.length;
      const caret = Math.max(0, (input.selectionStart || cleaned.length) - dropped);
      input.value = cleaned;
      input.setSelectionRange(caret, caret);
    });
  }

  enhanceSelect(field) {
    const select = field.querySelector('select');
    if (!select || select.dataset.enhanced) return;
    select.dataset.enhanced = 'true';

    const options = [...select.options];
    const placeholder = options.find((option) => option.disabled) || null;
    const choices = options.filter((option) => !option.disabled && option.value !== '');
    if (!choices.length) return;

    const details = document.createElement('details');
    details.className = 'contact-select';

    const summary = document.createElement('summary');
    summary.className = 'contact-select__summary contact-field__control';
    const value = document.createElement('span');
    value.className = 'contact-select__value';
    value.id = `${select.id}-value`;
    summary.appendChild(value);
    summary.insertAdjacentHTML(
      'beforeend',
      '<svg class="contact-select__caret" viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>'
    );

    // Name the disclosure trigger with the field label + the current value.
    const label = field.querySelector('.contact-field__label');
    if (label) {
      if (!label.id) label.id = `${select.id}-label`;
      summary.setAttribute('aria-labelledby', `${label.id} ${value.id}`);
    }
    details.appendChild(summary);

    const panel = document.createElement('div');
    panel.className = 'contact-select__panel';
    const list = document.createElement('ul');
    list.className = 'contact-select__list';
    list.setAttribute('role', 'list');
    panel.appendChild(list);
    details.appendChild(panel);

    const render = () => {
      const chosen = select.value;
      const selected = choices.find((option) => option.value === chosen);
      value.textContent = selected ? selected.textContent : placeholder ? placeholder.textContent : '';
      details.toggleAttribute('data-empty', !selected);
      list.querySelectorAll('.contact-select__option').forEach((button) => {
        button.setAttribute('aria-selected', String(button.dataset.value === chosen && chosen !== ''));
      });
    };

    choices.forEach((option) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'contact-select__option';
      button.dataset.value = option.value;
      button.textContent = option.textContent;
      button.addEventListener('click', () => {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        render();
        details.open = false;
        summary.focus();
      });
      item.appendChild(button);
      list.appendChild(item);
    });

    render();

    // Keep the native <select> as the value carrier + no-JS/validation fallback,
    // but out of sight and out of the tab/AT order now the custom UI is present.
    select.classList.add('visually-hidden');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    select.after(details);
    this.dropdowns.push(details);
  }
}

/**
 * Contact forms come back to the page exactly as the customer had it (#700).
 *
 * Shopify answers a {% form 'contact' %} post by redirecting to the page's path
 * with ?contact_posted=true, dropping the rest of the query string. A page shown
 * through ?view= (an alternate template) then comes back on its own template,
 * which has no form and so no thank-you, and a product page loses ?variant=,
 * the sold-out variant a back-in-stock request was sent from. So the theme's
 * own contact forms, marked data-return-here, name the address to return to
 * when it carries a query: this page's path and query, with the outcome flag
 * Shopify would have added. A plain address already comes back right, so
 * Shopify's own redirect is left to it. Either way the redirect keeps the
 * action's #form-id fragment, so the page lands on the form.
 *
 * Only marked forms. Shopify's spam protection can send a post to its own
 * /challenge page, whose captcha form re-sends it as form_type=contact and
 * returns the customer to the page they came from by itself. Naming the address
 * there sent them back to the spent challenge instead, a 404 (#700 QA).
 *
 * Delegated from the document in the capture phase: forms the editor or a
 * variant change swap in are covered, and the address is set before anything
 * listening further along (Shopify's form protection among them) reads the
 * form. Without JS, Shopify's own redirect stands, which is right for a form on
 * the page's own template.
 */
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-return-here')) return;

    const url = new URL(window.location.href);
    // An outcome already in the address must not ride along: a newsletter's
    // would show its thank-you again, and ours is added once, below.
    url.searchParams.delete('contact_posted');
    url.searchParams.delete('customer_posted');

    let returnTo = form.querySelector('input[name="return_to"][data-page-return]');
    if (!url.search) {
      returnTo?.remove();
      return;
    }
    url.searchParams.append('contact_posted', 'true');
    if (!returnTo) {
      returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      returnTo.dataset.pageReturn = '';
      form.append(returnTo);
    }
    returnTo.value = `${url.pathname}${url.search}`;
  },
  true
);

/**
 * <newsletter-success> — announces the newsletter opt-in success on reload.
 *
 * The form uses Shopify's full-reload {% form 'customer' %} path, which
 * redirects back to a URL ending in a #fragment. Browsers skip the `autofocus`
 * attribute on a fragment-navigated load, so the role="status" region rendered
 * but was never announced and focus stayed on <body>. This element only exists
 * in the DOM right after a successful submit, so moving focus to itself on
 * connect is always the right moment: it lands focus on the status message
 * (announcing it to assistive tech) and scrolls it into view. Degrades to a
 * visible, already-rendered success message without JS.
 */
class NewsletterSuccess extends HTMLElement {
  connectedCallback() {
    // One deferred frame isn't enough on the customer-form *redirect*: the
    // browser performs its own jump to the response URL's #fragment (the form)
    // *after* that frame and resets focus back to <body>, so the success went
    // unannounced (QA #265). Reloading the success page already works because
    // the jump runs before our frame — only the redirect races us. Wait until
    // the page has finished loading (the fragment jump is done by then), then
    // defer one frame so our focus + scroll-into-view lands last. If we connect
    // after load (e.g. an editor re-render), focus on the next frame straight away.
    const focusStatus = () => requestAnimationFrame(() => this.focus());
    if (document.readyState === 'complete') {
      focusStatus();
    } else {
      window.addEventListener('load', focusStatus, { once: true });
    }
  }
}

/* --------------------------------------------------------------------------
   Cart helpers
   -------------------------------------------------------------------------- */
async function fetchCart() {
  const response = await fetch(`${routes.cart}.js`);
  return response.json();
}

/**
 * Lazily create (and reuse) a single visually-hidden polite live region so
 * dynamic cart changes are announced to assistive tech. Shopify requires
 * dynamic cart mutations be surfaced via aria-live; the count bubble is
 * aria-hidden, so it can't do this on its own.
 */
const liveRegions = new Map();
function getLiveRegion(name) {
  const existing = liveRegions.get(name);
  if (existing?.isConnected) return existing;
  const region = document.createElement('div');
  region.setAttribute('role', 'status');
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  // Inline equivalent of the .visually-hidden pattern so the text is
  // announced but never rendered.
  region.style.cssText =
    'position:absolute;width:1px;height:1px;overflow:hidden;' +
    'clip:rect(0 0 0 0);white-space:nowrap;';
  document.body.appendChild(region);
  liveRegions.set(name, region);
  return region;
}

function getCartLiveRegion() {
  return getLiveRegion('cart');
}

/**
 * Announce a cart change to screen readers. `type` ('added' | 'removed' |
 * 'updated') describes what happened so the message matches the action instead
 * of always saying "Added"; an emptied cart always announces the empty state.
 * Clearing then setting on the next tick guarantees repeated identical messages
 * (e.g. adding the same item twice) are re-announced.
 */
function announceCartToScreenReader(cart, type = 'added', override) {
  const region = getCartLiveRegion();
  const count = cart?.item_count;
  let message;
  // A refused change still re-syncs the cart, but "Cart updated" would be a
  // lie — the caller passes the reason so that is what gets announced.
  if (override) {
    message = override;
  } else if (count === 0) {
    message = strings.cartEmpty ?? 'Your cart is empty';
  } else if (type === 'removed') {
    message = strings.cartRemoved ?? 'Item removed from your cart';
  } else if (type === 'updated') {
    message = strings.cartUpdated ?? 'Cart updated';
  } else {
    message = strings.cartAdded ?? 'Item added to your cart';
  }
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 100);
}

function announceCartChange(cart, type = 'added', override) {
  announceCartToScreenReader(cart, type, override);
  document.dispatchEvent(new CustomEvent('cart:changed', { detail: { cart } }));
}

/**
 * Speak the spend goal (snippets/cart-goal.liquid) crossing its line. The bar
 * is server-rendered into both cart surfaces and arrives with their refresh,
 * so this draws nothing: it only says the moment the goal is reached, or lost
 * again. The amount still to spend changes with every quantity tap and is
 * left to the bar's own accessible name rather than read out each time.
 *
 * Its own region, not the cart's: the refresh lands a beat after "Item added
 * to your cart" went out, and writing into that region would cut it off. The
 * state is shared because the cart page can also have the drawer behind it,
 * and both refresh — whichever sees the crossing first says it, once.
 */
let cartGoalState = document.querySelector('[data-cart-goal]')?.dataset.goalState ?? null;
function announceCartGoal(root) {
  const goal = root.querySelector('[data-cart-goal]');
  const state = goal?.dataset.goalState ?? null;
  const previous = cartGoalState;
  cartGoalState = state;
  // Appearing, disappearing and standing still are not crossings.
  if (!previous || !state || previous === state) return;
  const message = goal.querySelector('[data-cart-goal-message]')?.textContent.trim();
  if (!message) return;
  const region = getLiveRegion('cart-goal');
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 100);
}

/**
 * The cart page is the cart already: an add made on it — its suggestions'
 * Quick add — shows up in the page's own lines, so the drawer stays shut there
 * rather than sliding a second copy of the cart over it.
 */
function cartDrawerForAdd() {
  return document.querySelector('cart-lines') ? null : document.querySelector('cart-drawer');
}

/**
 * A cart line that comes back short of the quantity asked for was capped by
 * the stock available. Returns the sentence that says so, or null when the
 * change landed in full (#461, item 12).
 */
function capNote(cart, key, wanted, index) {
  // A discount can re-hash the key in the response, so fall back to the line's
  // stable rendered position when the old key no longer exists.
  const line =
    cart?.items?.find((item) => item.key === key) ??
    cart?.items?.[Number(index) - 1];
  if (!(wanted > 0) || !line || line.quantity >= wanted) return null;
  return (strings.cartQuantityLimited ?? 'Only __COUNT__ available').replace(/__COUNT__/g, line.quantity);
}

// Only a shopper-facing Shopify description may override the translated
// fallback. Browser/network exception messages are not storefront copy.
class CartAddError extends Error {}

function cartAddErrorMessage(error) {
  return (error instanceof CartAddError && error.message) || strings.addError || 'Could not add to cart';
}

async function addToCart(variantId, quantity = 1) {
  const response = await fetch(`${routes.cartAdd}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: Number(variantId), quantity }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new CartAddError(error.description || strings.addError || 'Could not add to cart');
  }
  const cart = await fetchCart();
  announceCartChange(cart);
  return cart;
}

document.addEventListener('cart:changed', (event) => {
  const count = event.detail?.cart?.item_count;
  if (typeof count !== 'number') return;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count > 0 ? String(count) : '';
  });
});

/* --------------------------------------------------------------------------
   Product card
   -------------------------------------------------------------------------- */
class ProductCard extends HTMLElement {
  static swatchGroups = 0;

  connectedCallback() {
    /* Moving a card in the DOM (the product quiz re-orders its results on
       every run) re-runs this on the same instance, stacking another Quick
       add listener each time. A section re-render in the editor builds fresh
       instances, so those still wire normally. */
    if (this.wired) return;
    this.wired = true;
    this.quickAdd = this.querySelector('[data-quick-add]');
    this.quickAdd?.addEventListener('click', (event) => this.onQuickAdd(event));
    this.wireSwatches();
  }

  /* Colour swatches (#647, snippets/card-swatches.liquid). A preview is a
     layer over the media link, faded in once its image has decoded, so the
     media box never changes size and a slow image never flashes blank. What
     shows: the colour under the pointer or focus, else the chosen colour,
     else nothing (the card's own image). Touch has no hover, so a tap simply
     chooses. Choosing points the card's links and its Choose options drawer
     at that colour's variant (/products/x?variant=id, which the product page
     and the quick-add section both open on). */
  wireSwatches() {
    const group = this.querySelector('[data-card-swatches]');
    if (!group) return;
    this.swatchGroup = group;
    // One group per card INSTANCE: the same product can sit at the same place
    // in two grids on a page, and a shared name would link their radios.
    ProductCard.swatchGroups += 1;
    const name = `card-swatch-${ProductCard.swatchGroups}`;
    group.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.name = name;
    });

    group.addEventListener('pointerover', (event) => {
      if (event.pointerType === 'touch') return;
      const input = event.target.closest('label')?.querySelector('input');
      if (input) this.showSwatch(input);
    });
    group.addEventListener('pointerleave', () => this.showSwatch(null));
    group.addEventListener('focusin', (event) => {
      if (event.target.matches('input')) this.showSwatch(event.target);
    });
    group.addEventListener('focusout', (event) => {
      if (!group.contains(event.relatedTarget)) this.showSwatch(null);
    });
    group.addEventListener('change', (event) => this.chooseSwatch(event.target));
  }

  chooseSwatch(input) {
    const id = input.dataset.variantId;
    if (id) {
      const toVariant = (href) => {
        const url = new URL(href, window.location.origin);
        url.searchParams.set('variant', id);
        return `${url.pathname}${url.search}${url.hash}`;
      };
      this.querySelectorAll('.product-card__title a, .product-card__media-link, a.product-card__quick-bar').forEach(
        (link) => link.setAttribute('href', toVariant(link.href))
      );
      const opener = this.querySelector('[data-quick-add-open]');
      if (opener) opener.dataset.quickAddOpen = toVariant(opener.dataset.quickAddOpen);
    }
    this.showSwatch(input);
  }

  // null puts back the chosen colour, or the card's own image.
  showSwatch(input) {
    const shown = input ?? this.swatchGroup.querySelector('input:checked');
    const src = shown?.dataset.mediaSrc;
    const token = (this.swatchToken = (this.swatchToken ?? 0) + 1);
    if (!src) {
      this.removeAttribute('data-swatch-media');
      return;
    }
    let image = this.swatchImage;
    if (!image) {
      const link = this.querySelector('.product-card__media-link');
      if (!link) return;
      image = document.createElement('img');
      image.className = 'product-card__img--swatch';
      image.alt = '';
      image.decoding = 'async';
      link.appendChild(image);
      this.swatchImage = image;
    }
    if (image.dataset.src !== src) {
      image.dataset.src = src;
      image.sizes = this.swatchGroup.dataset.sizes || '';
      image.srcset = shown.dataset.mediaSrcset || '';
      image.src = src;
    }
    image.decode().then(
      () => {
        // A later hover, focus or leave owns the layer now.
        if (token === this.swatchToken) this.setAttribute('data-swatch-media', '');
      },
      () => {}
    );
  }

  async onQuickAdd(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    if (!variantId || button.hasAttribute('aria-disabled')) return;
    event.preventDefault();

    const label = button.querySelector('[data-quick-add-label]');
    const restingText = label?.textContent;
    button.setAttribute('aria-disabled', 'true');
    if (label) label.textContent = strings.adding ?? 'Adding…';

    try {
      await addToCart(variantId);
      if (label) label.textContent = strings.added ?? 'Added';
      // If the add came from the open search drawer, hand off to the cart
      // drawer as the single active modal — the two share z-index 90 and would
      // otherwise stack. Guarded on isOpen so a quick add anywhere else (a grid,
      // recommendations) never disturbs a closed search drawer.
      const searchDrawer = document.querySelector('search-drawer');
      if (searchDrawer?.isOpen?.()) searchDrawer.close();
      // A product offer in the open offers drawer (#659) steps aside in the
      // beat the cart drawer slides in, as the quick-add drawer does; it
      // would otherwise cover the cart. With no cart drawer it stays open.
      const offersDrawer = this.closest('offers-drawer');
      await cartDrawerForAdd()?.openAfterRefresh(undefined, () => {
        if (offersDrawer?.isOpen?.()) offersDrawer.close();
      });
    } catch (error) {
      if (label) label.textContent = cartAddErrorMessage(error);
    } finally {
      window.setTimeout(() => {
        button.removeAttribute('aria-disabled');
        if (label && restingText) label.textContent = restingText;
      }, 2000);
    }
  }
}

/* --------------------------------------------------------------------------
   Product form — AJAX add-to-cart so the PDP opens the cart drawer
   -------------------------------------------------------------------------- */
/**
 * <product-form> wraps the PDP buy form ({% form 'product' %}). That form
 * natively POSTs to /cart/add, which redirects to the cart page — so with the
 * drawer cart enabled, "Add to cart" left the page instead of sliding the drawer
 * in (unlike Quick add on product cards). When a <cart-drawer> is present we
 * intercept the submit, add via fetch (FormData carries the variant, quantity
 * and any line-item properties), then open the drawer. Without a drawer
 * (cart_type: page) the native submit stands, so the shopper still reaches the
 * cart page. The sticky ATC bar submits this same form (button[form=…]), so it
 * is covered too. Degrades to a native submit without JS.
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.wireRecipient();
    this.guardDynamicCheckout();
  }

  /* Buy it now checks out from the form's values without submitting it, so
     the browser's own validation never runs on that path, and a required
     personalisation field (<product-field>, #372) left empty would sail
     through. Hold its click, in the capture phase so it lands before the
     button's own handler, until the form passes: reportValidity() raises the
     same `invalid` events a submit would, so the field at fault is shown and
     focused exactly as it is for Add to cart. */
  guardDynamicCheckout() {
    const dynamic = this.form?.querySelector('[data-dynamic-checkout]');
    dynamic?.addEventListener(
      'click',
      (event) => {
        if (this.form.noValidate || this.form.reportValidity()) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  /* Gift-card recipient enhancement. The reveal itself is pure CSS
     (:checked), so without JS everything still posts; with JS we (a) disable
     the collapsed fieldset so values typed, then unchecked, never reach the
     cart as stray line-item properties, and (b) stamp the shopper's timezone
     offset so a scheduled send lands on the recipient's calendar date. */
  wireRecipient() {
    const toggle = this.form?.querySelector('[data-recipient-toggle]');
    const fields = this.form?.querySelector('[data-recipient-fields]');
    if (!toggle || !fields) return;
    const offset = this.form.querySelector('[data-recipient-offset]');
    if (offset) {
      offset.value = new Date().getTimezoneOffset().toString();
      offset.disabled = false;
    }
    const sync = () => {
      fields.disabled = !toggle.checked;
    };
    toggle.addEventListener('change', sync);
    sync();
  }

  async onSubmit(event) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    event.preventDefault();
    if (this.loading) return;
    this.loading = true;
    const label = this.form.querySelector('[data-atc-label]');
    const restingText = label?.textContent;
    if (label) label.textContent = strings.adding ?? 'Adding…';
    try {
      const response = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new CartAddError(error.description || strings.addError || 'Could not add to cart');
      }
      announceCartChange(await fetchCart());
      if (label) label.textContent = strings.added ?? 'Added';
      // Added from the quick-add drawer (#603): it steps aside in the same
      // beat the cart drawer slides in, so two modals never stack and focus
      // goes back to the card the shopper started from. Only ever on this
      // success path — a refused add leaves the drawer open on its message.
      // On the cart page the page's own lines take the add instead.
      const quickAdd = this.closest('quick-add-drawer');
      const cartDrawer = cartDrawerForAdd();
      if (cartDrawer) await cartDrawer.openAfterRefresh(undefined, () => quickAdd?.close());
      else quickAdd?.close();
    } catch (error) {
      if (label) label.textContent = cartAddErrorMessage(error);
    } finally {
      window.setTimeout(() => {
        this.loading = false;
        if (label && restingText) label.textContent = restingText;
      }, 2000);
    }
  }
}

/* --------------------------------------------------------------------------
   Product field — a personalisation answer posted with the buy form (#372)
   -------------------------------------------------------------------------- */
/**
 * <product-field> (snippets/product-field.liquid) wraps one custom_field
 * block's control(s), which join the product form through form="…" and post
 * as a line-item property. The browser does the validating (required,
 * maxlength) and refuses the submit itself, so an empty required field stops
 * the add with or without this element. What it changes is the report: in
 * place of the browser's bubble, the message is written under the field, and
 * the first field at fault in the form is scrolled to the middle of the
 * screen and focused — the sticky add-to-cart bar submits this form from
 * off-screen, where a bubble would point at nothing the shopper can see.
 *
 * It also refuses a file over Shopify's cart-upload cap (data-max-size, 20 MB)
 * the moment it is picked, through setCustomValidity, so the same report
 * covers it at submit.
 */
class ProductField extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    this.error = this.querySelector('[data-field-error]');
    // `invalid` does not bubble; the capture phase still passes through here.
    this.addEventListener('invalid', (event) => this.onInvalid(event), true);
    this.addEventListener('input', (event) => this.onEdit(event));
    this.addEventListener('change', (event) => this.onEdit(event));
  }

  get controls() {
    return [...this.querySelectorAll('.pdp-field__control')];
  }

  onEdit(event) {
    const control = event.target;
    if (!control.matches?.('.pdp-field__control')) return;
    if (control.type === 'file' && this.checkFileSize(control)) {
      this.show(control.validationMessage);
      return;
    }
    // Reads validity as state: checkValidity() would raise `invalid` again.
    if (this.controls.every((field) => field.validity.valid)) this.clear();
  }

  /** Returns true when a chosen file is over the cap (and marks it invalid). */
  checkFileSize(input) {
    const max = Number(this.dataset.maxSize) || 20 * 1024 * 1024;
    const tooBig = [...(input.files || [])].some((file) => file.size > max);
    input.setCustomValidity(tooBig ? this.dataset.errorSize || 'File too large' : '');
    return tooBig;
  }

  onInvalid(event) {
    const control = event.target;
    if (!control.matches?.('.pdp-field__control')) return;
    // Cancelling only suppresses the bubble; the submission stays refused.
    event.preventDefault();
    this.show(control.validity.customError ? control.validationMessage : this.dataset.errorRequired);
    // Every invalid control in the form raises its own event, in tree order;
    // only the first one at fault anywhere in the form is brought to the
    // shopper. When that is some other field (the gift-card email), its own
    // report already takes focus.
    const first = control.form
      ? [...control.form.elements].find((element) => element.willValidate && !element.validity.valid)
      : control;
    if (first === control) this.reveal(control);
  }

  reveal(control) {
    // A radio group comes into view whole, legend and all.
    const target = control.closest('.pdp-field__set') ?? control;
    target.scrollIntoView({ block: 'center', behavior: motionReduced() ? 'auto' : 'smooth' });
    control.focus({ preventScroll: true });
  }

  show(message) {
    if (!this.error) return;
    // Unhidden before it is written, so the text is there to be read the
    // moment focus lands on a control it describes.
    this.error.hidden = false;
    this.error.textContent = message || '';
    this.controls.forEach((field) => field.setAttribute('aria-invalid', 'true'));
  }

  clear() {
    if (!this.error) return;
    this.error.hidden = true;
    this.error.textContent = '';
    this.controls.forEach((field) => field.removeAttribute('aria-invalid'));
  }
}

/* --------------------------------------------------------------------------
   Quick-add drawer — "Choose options" in place (#603)
   -------------------------------------------------------------------------- */
/**
 * <quick-add-drawer> (snippets/quick-add-drawer.liquid) — what a product
 * card's Choose options opens. A product with more than one variant can't go
 * straight into the cart, and the button used to be a link to the product
 * page, so choosing a size cost the shopper their place in the collection.
 * It now opens this drawer on the card's product: sections/quick-add.liquid,
 * rendered for that product through the Section Rendering API, carrying the
 * product page's own gallery, variant picker, quantity and buy form. Adding
 * hands the screen to the cart drawer (see ProductForm).
 *
 * One drawer serves every card, so each open first puts the loading state
 * back — the last product is never on screen, or in the tab order, while the
 * next one loads — and only the newest request may paint. Reopening the
 * product already shown keeps it as the shopper left it. Without JS the card
 * shows its link to the product page instead of the button.
 */
const quickAddScripts = new Set();

class QuickAddDrawer extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-drawer-root]');
    this.panel = this.querySelector('[data-quick-add-panel]');
    this.body = this.querySelector('[data-quick-add-body]');
    this.label = this.panel?.getAttribute('aria-label') ?? '';
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
    });
    // Delegated from the document: cards arrive with every filter, sort,
    // search and editor re-render, and none of them has to wire itself up.
    this.onDocClick = (event) => {
      const opener = event.target.closest('[data-quick-add-open]');
      if (!opener) return;
      event.preventDefault();
      this.open(opener);
    };
    this.onDocKeydown = (event) => {
      if (!this.isOpen()) return;
      if (event.key === 'Escape') this.close();
      else if (event.key === 'Tab') trapDrawerFocus(this.root, event);
    };
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onDocKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onDocKeydown);
    this.controller?.abort();
    if (this.isOpen()) document.body.style.overflow = '';
  }

  isOpen() {
    return Boolean(this.root?.classList.contains('is-open'));
  }

  open(opener) {
    const url = opener?.dataset.quickAddOpen;
    if (!this.root || !this.body || !url) return;
    this.lastFocus = this.handOff(opener);
    // Named for the product from the moment it opens, loaded or not.
    this.panel?.setAttribute('aria-label', opener.getAttribute('aria-label') || this.label);
    this.root.classList.add('is-open');
    this.root.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    // After the frame that makes the panel visible — focus() on a
    // visibility:hidden element is a silent no-op.
    requestAnimationFrame(() => this.querySelector('.quick-add-drawer__close')?.focus());
    this.load(url);
  }

  close() {
    if (!this.isOpen()) return;
    // Focus leaves first: inert would otherwise drop it on <body>.
    this.lastFocus?.focus();
    this.root.classList.remove('is-open');
    this.root.setAttribute('inert', '');
    document.body.style.overflow = '';
    // Nothing keeps playing in a closed drawer.
    this.body.querySelector('media-gallery')?.pauseMediaExcept?.(null);
  }

  /**
   * A card can sit inside another overlay — a search-drawer result, a
   * shop-the-look card. Two modals must never be open at once (both would
   * answer Esc and Tab), so that overlay closes first, as the search drawer
   * already does for a card's Quick add. The card goes with it, so focus is
   * owed to whatever opened THAT overlay.
   */
  handOff(opener) {
    // The cart drawer's suggestions are cards too. Adding hands the screen
    // straight back to it (ProductForm reopens it on the new line). So are
    // the offers drawer's product offers (#659).
    const overlay =
      opener.closest('search-drawer') ?? opener.closest('cart-drawer') ?? opener.closest('offers-drawer');
    if (overlay?.isOpen()) {
      overlay.close();
      return document.activeElement;
    }
    // A look's card is only on screen while its spot is open.
    const spot = opener.closest('[data-spot]');
    const look = spot?.closest('shop-the-look');
    if (look) {
      look.closeAll();
      return spot.querySelector('[data-spot-toggle]');
    }
    return opener;
  }

  async load(url) {
    // Already showing, or already fetching, this product.
    if (url === this.url) return;
    this.url = url;
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.render(this.template('[data-quick-add-loading]'));
    this.body.setAttribute('aria-busy', 'true');
    try {
      // The card's URL can already carry a query (search results add their
      // tracking parameters), so the section id is added, never concatenated.
      const request = new URL(url, window.location.origin);
      request.searchParams.delete('section_id');
      request.searchParams.append('section_id', this.dataset.section);
      const response = await fetch(request, { signal: controller.signal });
      if (!response.ok) throw new Error(`Quick add could not load ${url}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = doc.querySelector('[data-quick-add-content]');
      if (!content) throw new Error(`Quick add found no product at ${url}`);
      // The section's own wrapper comes too: the product page's elements
      // scope themselves to closest('.shopify-section').
      this.render(content.closest('.shopify-section') ?? content);
      this.loadScripts();
    } catch (error) {
      if (controller.signal.aborted) return;
      // Fetched afresh next time, rather than short-circuited to the error.
      this.url = null;
      const failure = this.template('[data-quick-add-error]');
      failure?.querySelector('[data-quick-add-error-link]')?.setAttribute('href', url);
      this.render(failure);
    } finally {
      if (!controller.signal.aborted) this.body.removeAttribute('aria-busy');
    }
  }

  template(selector) {
    return this.querySelector(selector)?.content.cloneNode(true) ?? null;
  }

  render(node) {
    this.body.replaceChildren(...(node ? [node] : []));
  }

  // Script tags that arrive in fetched markup never run. The section only
  // carries Shopify's 3D-model libraries, for a product with a model — load
  // each for real, once, unless the page already has it (a product page).
  loadScripts() {
    this.body.querySelectorAll('script[src]').forEach((inert) => {
      const { src } = inert;
      const present = [...document.scripts].some(
        (script) => script.src === src && !this.contains(script)
      );
      if (present || quickAddScripts.has(src)) return;
      quickAddScripts.add(src);
      const script = document.createElement('script');
      script.src = src;
      document.head.appendChild(script);
    });
  }
}

/* --------------------------------------------------------------------------
   Countdown timer
   -------------------------------------------------------------------------- */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    // data-deadline is an absolute instant in epoch milliseconds, emitted by
    // block-timer.liquid from the store-timezone end date. Parsing it as a
    // number (rather than a timezone-less datetime string) means every visitor
    // resolves the SAME end moment regardless of their local timezone.
    const deadline = Number(this.dataset.deadline);
    if (!Number.isFinite(deadline) || deadline <= 0) {
      this.hidden = true;
      return;
    }
    this.deadline = deadline;
    this.cells = {
      days: this.querySelector('[data-timer-days]'),
      hours: this.querySelector('[data-timer-hours]'),
      minutes: this.querySelector('[data-timer-minutes]'),
      seconds: this.querySelector('[data-timer-seconds]'),
    };
    this.tick();
    this.interval = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.interval);
  }

  tick() {
    const remaining = Math.max(0, this.deadline - Date.now());
    if (remaining === 0) {
      window.clearInterval(this.interval);
      // An ended message (#665) takes the digits' place and wins over hiding,
      // matching block-timer's server render of an already-ended timer.
      const message = this.querySelector('[data-timer-message]');
      if (message) {
        message.hidden = false;
        this.classList.add('is-complete');
        return;
      }
      if (this.dataset.hideOnComplete === 'true') this.hidden = true;
    }
    const seconds = Math.floor(remaining / 1000);
    const parts = {
      days: Math.floor(seconds / 86400),
      hours: Math.floor((seconds % 86400) / 3600),
      minutes: Math.floor((seconds % 3600) / 60),
      seconds: seconds % 60,
    };
    Object.entries(parts).forEach(([key, value]) => {
      const cell = this.cells[key];
      if (cell) cell.textContent = String(value).padStart(2, '0');
    });
  }
}

/* --------------------------------------------------------------------------
   Count-up stat (Proof Band). Renders final value under reduced motion.
   -------------------------------------------------------------------------- */
class CountUp extends HTMLElement {
  connectedCallback() {
    this.target = parseFloat(this.dataset.value ?? this.textContent) || 0;
    this.decimals = (String(this.dataset.value ?? '').split('.')[1] || '').length;
    if (motionReduced()) {
      this.render(this.target);
      return;
    }
    this.render(0);
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.observer.disconnect();
          this.animate900();
        });
      },
      { threshold: 0.4 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    // Disconnect if torn out before it ever intersected (e.g. a Theme-Editor
    // re-render), so the observer doesn't retain a detached node.
    this.observer?.disconnect();
  }

  render(value) {
    this.textContent = value.toFixed(this.decimals);
  }

  animate900() {
    const start = performance.now();
    const duration = 900;
    // Matches --ease: cubic-bezier(0.16, 1, 0.3, 1) closely enough for text.
    const easeOut = (t) => 1 - Math.pow(1 - t, 4);
    const frame = (now) => {
      const t = Math.min(1, (now - start) / duration);
      this.render(this.target * easeOut(t));
      if (t < 1) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }
}

/* --------------------------------------------------------------------------
   Cart line editing — shared by the drawer and the cart page

   Both surfaces render the same line controls (snippets/cart-quantity.liquid
   plus a [data-line-remove] button), so the mutation logic lives here once.
   A subclass only decides how it re-renders itself once the cart answers.
   -------------------------------------------------------------------------- */
class CartLineEditor extends HTMLElement {
  /**
   * Delegate the three line controls. Subclasses call this from their own
   * connectedCallback. The listeners are bound to `this`, not the document,
   * so a drawer and a cart page can both be mounted without stealing each
   * other's events.
   */
  bindLineControls() {
    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-line-quantity]');
      if (input) this.commitQuantity(input);
    });
    // Any further typing makes the field worth sending again — see the guard
    // in commitQuantity.
    this.addEventListener('input', (event) => {
      const input = event.target.closest('[data-line-quantity]');
      if (input) delete input.dataset.lineSent;
    });
    // Enter in the quantity field must commit that line, nothing else. On the
    // cart page the field sits inside the checkout form, whose first submit
    // button is "Check out", so implicit submission takes a keyboard shopper
    // who types a quantity and presses Enter to confirm it straight to
    // checkout. Committing here keeps them on the cart, focus still in the
    // field. The drawer's field is in no form, so Enter did nothing there —
    // now it commits on both surfaces alike.
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      const input = event.target.closest('[data-line-quantity]');
      if (!input) return;
      event.preventDefault();
      this.commitQuantity(input);
    });
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-line-remove]');
      if (!remove) return;
      // On the cart page Remove is a real link to the line's /cart/change URL,
      // so the line can still be emptied without JavaScript. Here the ajax
      // path takes over, so the navigation must not also happen. (The drawer's
      // Remove is a plain button, where this is a no-op.)
      event.preventDefault();
      this.beforeLineRemove?.(remove);
      this.changeLine(remove, 0);
    });
    // Stepper buttons drive the same line quantity the number input does.
    // Setting .value programmatically does not fire `change`, so the mutation
    // is dispatched here directly — changeLine already serializes a burst of
    // taps, and the field is updated first so the control stays responsive
    // while the request is in flight.
    this.addEventListener('click', (event) => {
      const step = event.target.closest('[data-line-step]');
      if (!step) return;
      const field = step.parentElement.querySelector('[data-line-quantity]');
      if (!field) return;
      const next = Math.max(0, Number(field.value || 0) + Number(step.dataset.lineStep));
      if (next === Number(field.value)) return;
      field.value = String(next);
      this.commitQuantity(field);
    });
  }

  /**
   * Send whatever quantity the field is now showing, however it got there —
   * typed and committed, or stepped by the +/− buttons.
   */
  commitQuantity(input) {
    // A cleared or nonsense field must never read as zero: Number('') is 0,
    // which would delete the line on a stray backspace. Removing is
    // [data-line-remove]'s job, so anything below one snaps back to the
    // quantity the server last rendered.
    const next = Number.parseInt(input.value, 10);
    if (!Number.isFinite(next) || next < 1) {
      input.value = input.defaultValue;
      return;
    }
    // The Enter handler in bindLineControls commits the field, and the browser
    // fires its own `change` for that same keypress, so remember what went
    // out: the line must not be posted — nor announced — twice for one press.
    // Typing clears this, and a re-render replaces the element outright.
    if (input.dataset.lineSent === String(next)) return;
    input.dataset.lineSent = String(next);
    this.changeLine(input, next);
  }

  changeLine(origin, quantity) {
    // Dim the stepper the change came from, so a slow network reads as
    // "working" rather than "ignored". Deliberately visual only — blocking
    // pointer events here would break tapping + three times in a row, which
    // the serialized queue below exists to support.
    const stepper = origin?.closest?.('.qty-stepper');
    stepper?.classList.add('is-busy');
    // Serialize line changes: chain each mutation after the previous one so a
    // burst of +/- taps can't interleave into a wrong cart state (every
    // /cart/change is relative to the cart the prior call produced). The
    // .catch keeps one failed change from stalling the whole queue.
    this.lineQueue = (this.lineQueue ?? Promise.resolve())
      .catch(() => {})
      .then(() => this.postLineChange(origin, quantity))
      .finally(() => stepper?.classList.remove('is-busy'));
    return this.lineQueue;
  }

  async postLineChange(origin, quantity) {
    // Resolve the line's key HERE rather than when the change was queued. Put
    // a discount code on the cart and Shopify re-hashes every line's key on
    // each change, so a burst of + taps queued behind one another all carried
    // the key the FIRST tap had already invalidated: the second and third were
    // refused with "no valid id or line parameter" and the quantity climbed on
    // screen and sprang back. Only a microtask separates the previous change
    // landing from this line, so nothing can repaint in between — the markup
    // read here is the freshest cart the server has given us.
    const index = origin?.closest?.('[data-line-index]')?.dataset.lineIndex;
    const line = index ? this.querySelector(`[data-line-index="${index}"]`) : null;
    const key =
      line?.querySelector('[data-line-key]')?.dataset.lineKey ?? origin?.dataset?.lineKey;
    const changeType = Number(quantity) === 0 ? 'removed' : 'updated';
    // Only ever shown to a shopper if the SERVER wrote it. A thrown fetch
    // carries strings like "Failed to fetch", and Shopify's sibling `message`
    // field is the internal label "Cart Error" — neither belongs on screen.
    let reason = '';
    try {
      const response = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(quantity) }),
      });
      if (!response.ok) {
        // `description` is the shopper-facing reason — almost always a stock
        // limit ("You can only add 3 of this item…"). Without it the quantity
        // just springs back with no explanation at all.
        const body = await response.json().catch(() => ({}));
        reason = body.description ?? '';
        throw new Error('Cart change refused');
      }
      // A removal renumbers every line below it, so a message still
      // outstanding no longer belongs to the line now standing in that place.
      if (changeType === 'removed') this.lineErrors?.clear();
      const cart = await response.json();
      // Shopify can answer 200 while capping the requested quantity to stock.
      // Treat that as an explained adjustment, not an ordinary success.
      const limitMessage = capNote(cart, key, Number(quantity), index);
      this.setLineError(index, limitMessage);
      // The body of a successful change IS the cart it produced, and it lands
      // before the next queued change goes out — refresh() is a separate fetch
      // that does not. So this is the one chance to hand that change a key the
      // cart will still accept.
      this.syncLineKeys(cart, changeType === 'removed' ? line : null);
      announceCartChange(cart, changeType, limitMessage);
    } catch {
      const message =
        reason || strings.cartError || 'We could not update that item.';
      this.setLineError(index, message);
      // Re-sync to the server's authoritative cart so a failed change reverts
      // to the real quantity instead of leaving a stale/optimistic value, and
      // announce the reason rather than a success that did not happen.
      try {
        announceCartChange(await fetchCart(), changeType, message);
      } catch {
        /* Offline: no re-sync possible, so the inline message stands alone. */
      }
    }
  }

  /**
   * Rewrite the markup's line keys from the cart a change just returned, so
   * whatever is queued behind it posts a key the cart still recognises.
   *
   * `removed` is the line the change just emptied, if any: it stays in the
   * markup until the next refresh lands, so it must not be mapped onto a key.
   */
  syncLineKeys(cart, removed) {
    const lines = [...this.querySelectorAll('[data-line-index]')].filter(
      (line) => line !== removed
    );
    // Mapping by position is only sound line-for-line. Anything else — a
    // product page added something while the drawer sat open, say — waits for
    // refresh() to bring markup the server itself rendered.
    if (!Array.isArray(cart?.items) || cart.items.length !== lines.length) return;
    lines.forEach((line, i) => {
      line.querySelectorAll('[data-line-key]').forEach((el) => {
        el.dataset.lineKey = cart.items[i].key;
      });
    });
  }

  /**
   * Record a shopper-facing failure against one line and paint it now. The
   * message lives on the element rather than in the markup because every
   * mutation re-renders the region it sat in — refresh() re-applies whatever
   * is still current once the new markup lands.
   *
   * Filed under the line's POSITION, not its key: the key the refusal was
   * sent with is by then the dead one, while the markup carries the fresh one,
   * so a key-filed message matched no slot and the shopper saw the quantity
   * spring back with no explanation at all.
   */
  setLineError(index, message) {
    if (!this.lineErrors) this.lineErrors = new Map();
    if (message) this.lineErrors.set(index, message);
    else this.lineErrors.delete(index);
    this.renderLineErrors();
  }

  renderLineErrors() {
    if (!this.lineErrors) return;
    this.querySelectorAll('[data-line-error]').forEach((slot) => {
      const index = slot.closest('[data-line-index]')?.dataset.lineIndex;
      const message = this.lineErrors.get(index);
      slot.textContent = message ?? '';
      slot.hidden = !message;
    });
  }

  /**
   * Claim the right to paint a re-render, and report whether that claim still
   * stands once the markup arrives. A burst of +/− taps fires one refresh per
   * change and those fetches can land out of order — an older one painting
   * last would put back the quantity (and the line keys) of a cart two changes
   * ago, which reads to a shopper exactly like a tap being dropped.
   */
  beginRefresh() {
    const token = (this.refreshToken = (this.refreshToken ?? 0) + 1);
    return () => token === this.refreshToken;
  }

  /**
   * Describe the focused line control as a selector that will still match its
   * replacement after a region is swapped.
   *
   * A line key is NOT stable. Put a discount code on the cart and Shopify
   * re-hashes every line's key on each quantity change, so anything derived
   * from one — the stepper ids and the label/aria-controls pairing built on
   * them, Remove's data-line-key — matches nothing in the fresh markup and
   * the keyboard user is dropped on <body> mid-edit. A line's position and a
   * control's role both survive the re-render, so identify by those instead.
   */
  describeFocus() {
    const active = document.activeElement;
    if (!active || !this.contains(active)) return null;
    const line = active.closest('[data-line-index]');
    if (line) {
      const scope = `[data-line-index="${line.dataset.lineIndex}"]`;
      const step = active.closest('[data-line-step]');
      if (step) return `${scope} [data-line-step="${step.dataset.lineStep}"]`;
      if (active.closest('[data-line-remove]')) return `${scope} [data-line-remove]`;
      if (active.closest('[data-line-quantity]')) return `${scope} [data-line-quantity]`;
    }
    // Nothing else focusable in a swapped region carries an id today, but the
    // drawer replaces its whole body, so keep the id path for anything that
    // grows one later.
    return active.id ? `#${CSS.escape(active.id)}` : null;
  }

  /**
   * Hand focus back to the replacement of whatever describeFocus() recorded.
   * `fallback` names a control to land on when the original is gone entirely —
   * the drawer passes one because focus escaping an open dialog leaves its
   * trap with nothing to cycle; a page can safely leave focus where it is.
   */
  restoreFocus(selector, fallback) {
    if (!selector) return;
    const next = this.querySelector(selector);
    if (!next) {
      if (fallback) this.querySelector(fallback)?.focus();
      return;
    }
    // Stepping down to one disables the decrement, and focusing a disabled
    // control is a silent no-op that would strand the keyboard user — hand
    // focus to the field that button drives instead.
    if (next.disabled) {
      const controls = next.getAttribute('aria-controls');
      if (controls) this.querySelector(`#${CSS.escape(controls)}`)?.focus();
      return;
    }
    next.focus();
  }
}

/* --------------------------------------------------------------------------
   Cart drawer
   -------------------------------------------------------------------------- */
class CartDrawer extends CartLineEditor {
  connectedCallback() {
    this.root = this.querySelector('[data-drawer-root]');
    this.sectionId = this.dataset.sectionId;
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
    });
    // Store the document-level handlers so disconnectedCallback can remove them:
    // in the Theme Editor a section disconnects/reconnects repeatedly, so
    // anonymous listeners would leak and double-fire on every re-render.
    // Hold the newest refresh() promise so an add-to-cart flow can await the
    // drawer catching up to the cart before it opens (see openAfterRefresh).
    this.onCartChanged = () => {
      this.lastRefresh = this.refresh();
    };
    this.onDocKeydown = (event) => {
      if (!this.isOpen()) return;
      if (event.key === 'Escape') this.close();
      else if (event.key === 'Tab') trapDrawerFocus(this.root, event);
    };
    this.onDocClick = (event) => {
      const opener = event.target.closest('[data-cart-drawer-open]');
      if (!opener) return;
      event.preventDefault();
      this.open(opener);
    };
    document.addEventListener('cart:changed', this.onCartChanged);
    document.addEventListener('keydown', this.onDocKeydown);
    document.addEventListener('click', this.onDocClick);
    this.bindLineControls();
    // Persist the order note so it reaches both normal and express checkout,
    // and survives the footer's next server-rendered refresh.
    this.addEventListener('change', (event) => {
      const note = event.target.closest('[data-cart-note]');
      if (note) this.saveNote(note.value);
    });

    // Theme editor: open when this section is selected — and re-open after a
    // setting re-render (shopify:section:load fires on the fresh element) so a
    // colour-scheme change repaints the drawer instead of leaving it shut behind
    // a scroll-locked page. Mirrors the offers/search drawers.
    if (window.Shopify && window.Shopify.designMode) {
      this.onSectionSelect = (event) => {
        if (event.detail?.sectionId === this.sectionId) this.open();
      };
      this.onSectionDeselect = (event) => {
        if (event.detail?.sectionId === this.sectionId) this.close();
      };
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:load', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('cart:changed', this.onCartChanged);
    document.removeEventListener('keydown', this.onDocKeydown);
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:load', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    // An editor re-render tears this instance out while it may be open; release
    // the body scroll-lock it applied so the page behind can scroll again. The
    // reconnected instance re-applies it when it reopens.
    if (this.isOpen()) document.body.style.overflow = '';
  }

  isOpen() {
    return this.root?.classList.contains('is-open');
  }

  open(opener) {
    if (!this.root) return;
    // A Quick add on one of the drawer's own suggestions re-opens it while it
    // is open: whatever opened it in the first place still gets focus back.
    if (!this.isOpen()) this.lastFocus = opener || document.activeElement;
    this.root.classList.add('is-open');
    this.root.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    // The first [data-drawer-close] is the invisible overlay button; focus the
    // visible close control so the ring lands somewhere the shopper can see.
    this.root.querySelector('.cart-drawer__close')?.focus();
  }

  // Open the drawer showing the item that was just added. The add flow opens
  // the drawer right after dispatching cart:changed, but refresh() (which the
  // event kicks off) is a fetch that is still in flight — so a plain open()
  // slides the drawer in on the PRE-add markup, and on a first add into an
  // empty cart that is the empty state, the new line only appearing a beat
  // later when the fetch lands (#374, QA on #331). Awaiting that refresh first
  // makes the line present the instant the drawer opens. The timeout is a
  // backstop: a slow/stuck refresh must not keep the drawer from opening — it
  // opens anyway and the line swaps in when the refresh eventually lands.
  // `beforeOpen` lets the surface the add came from step aside in that same
  // beat — the quick-add drawer closes as this one slides in (#603).
  async openAfterRefresh(opener, beforeOpen) {
    await Promise.race([
      this.lastRefresh,
      new Promise((resolve) => window.setTimeout(resolve, 800)),
    ]);
    beforeOpen?.();
    this.open(opener);
  }

  close() {
    if (!this.root) return;
    this.root.classList.remove('is-open');
    this.root.setAttribute('inert', '');
    document.body.style.overflow = '';
    this.lastFocus?.focus();
  }

  beforeLineRemove(remove) {
    this.collapseLine(remove.closest('.cart-drawer__line'));
  }

  collapseLine(line) {
    if (!line) return;
    // Pin the current pixel height first: height:auto is not animatable, so the
    // transition needs a concrete start value before we set it to 0. The reflow
    // read locks that start in before the class flips the target to zero.
    const height = line.offsetHeight;
    line.style.height = `${height}px`;
    line.classList.add('is-removing');
    void line.offsetHeight;
    line.style.height = '0px';
  }

  async saveNote(note) {
    try {
      await fetch(`${routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
    } catch {
      // Offline: the note still posts with the checkout form on submit.
    }
  }
  async refresh() {
    if (!this.sectionId) return;
    const isCurrent = this.beginRefresh();
    try {
      const response = await fetch(
        `${window.location.pathname}?section_id=${this.sectionId}`
      );
      const text = await response.text();
      if (!isCurrent()) return;
      const doc = new DOMParser().parseFromString(text, 'text/html');
      // Replacing the body destroys whatever had focus inside it. In a dialog
      // that is worse than on a page: focus lands on <body>, outside the
      // drawer, and the Tab trap has nothing to cycle. Record it first.
      const focusTarget = this.describeFocus();
      const fresh = doc.querySelector('[data-drawer-body]');
      const current = this.querySelector('[data-drawer-body]');
      if (fresh && current) current.replaceWith(fresh);
      const freshFooter = doc.querySelector('[data-drawer-footer]');
      const currentFooter = this.querySelector('[data-drawer-footer]');
      if (freshFooter && currentFooter) currentFooter.replaceWith(freshFooter);
      // The body that just landed is server-rendered and therefore blank of
      // any refusal message — put back the ones still outstanding.
      this.renderLineErrors();
      announceCartGoal(this);
      // Only while open: a background refresh (the shopper added something
      // from a product page) must not yank focus into a closed drawer.
      // Removing the last of a line falls back to the close button, which is
      // always present and keeps focus inside the dialog.
      if (this.isOpen()) this.restoreFocus(focusTarget, '.cart-drawer__close');
    } catch {
      /* Network hiccup: leave the current markup in place. */
    }
  }
}

/* --------------------------------------------------------------------------
   Cart page

   Mirrors the drawer's line controls, but re-renders itself in place: the
   [data-cart-refresh] regions are swapped from a fresh render of this section
   so the lines, the summary and the mobile bar move together without a
   navigation. The order note is deliberately NOT among them — a shopper can
   be mid-sentence when a quantity change lands, and swapping the textarea
   would throw away what they had typed.
   -------------------------------------------------------------------------- */
class CartLines extends CartLineEditor {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.bindLineControls();
    this.onCartChanged = () => this.refresh();
    document.addEventListener('cart:changed', this.onCartChanged);
    // Nothing here is submitted, so the note persists itself: the page's only
    // "Update cart" button is the no-JS fallback, which never shows with JS
    // running. `change` fires on blur, which is enough: checking out submits
    // the form, which posts the field natively even if it was never blurred.
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-cart-note]')) this.saveNote(event.target.value);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('cart:changed', this.onCartChanged);
  }

  async saveNote(note) {
    try {
      await fetch(`${routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
    } catch {
      /* Offline: the note still posts with the checkout form. */
    }
  }

  async refresh() {
    if (!this.sectionId) return;
    const isCurrent = this.beginRefresh();
    try {
      const response = await fetch(
        `${window.location.pathname}?section_id=${this.sectionId}`
      );
      const text = await response.text();
      if (!isCurrent()) return;
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const fresh = doc.querySelector('cart-lines');
      if (!fresh) return;
      // Either side missing its regions means the section crossed the
      // empty/filled boundary (last line removed, or something added while
      // the page sat empty) — there is nothing to patch, so take it whole.
      const regions = fresh.querySelectorAll('[data-cart-refresh]');
      if (!regions.length || !this.querySelector('[data-cart-refresh]')) {
        // Move the parsed nodes rather than re-serializing them through
        // innerHTML, which would parse the same markup a second time.
        this.replaceChildren(...fresh.childNodes);
        announceCartGoal(this);
        return;
      }
      // Swapping a region blows away whatever had focus inside it, dropping a
      // keyboard user back to the top of the document mid-edit. Remember the
      // control first and hand focus back to its replacement.
      const focusTarget = this.describeFocus();
      regions.forEach((region) => {
        const current = this.querySelector(
          `[data-cart-refresh="${region.dataset.cartRefresh}"]`
        );
        if (current) current.replaceWith(region);
      });
      // Fresh markup is server-rendered and therefore blank of any refusal
      // message — put back the ones still outstanding.
      this.renderLineErrors();
      announceCartGoal(this);
      this.restoreFocus(focusTarget);
    } catch {
      /* Network hiccup: leave the current markup in place. */
    }
  }
}

/* --------------------------------------------------------------------------
   Cart discount code (snippets/cart-discount.liquid)
   -------------------------------------------------------------------------- */
/**
 * <cart-discount> — apply and remove discount codes without leaving the cart.
 *
 * /cart/update.js takes a `discount` parameter carrying the FULL set of codes
 * the cart should end up with (comma separated; an empty string clears them
 * all), and answers with `discount_codes: [{ code, applicable }]`. That
 * `applicable` flag is the whole reason this can exist in a theme: it is how a
 * code the cart refused is told apart from one it took, so an invalid, expired
 * or unmet-minimum code can be reported here instead of silently doing nothing
 * (#485). Codes are never held in JS state — the applied set is read back off
 * the server-rendered [data-discount-code] rows, so a reload, a re-render and
 * a second tab all agree.
 *
 * This element never re-renders anything itself: it dispatches `cart:changed`
 * and the surface hosting it — the drawer, or the cart page — swaps in fresh
 * markup exactly as it does for any other cart change.
 */
class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-discount-input]');
    this.error = this.querySelector('[data-discount-error]');
    if (!this.input) return;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-discount-apply]')) {
        this.submit();
        return;
      }
      const button = event.target.closest('[data-discount-remove]');
      if (!button) return;
      const code = button.closest('[data-discount-code]')?.dataset.discountCode;
      if (code) this.remove(code);
    });
    // Enter must apply the code, never implicitly submit a surrounding form —
    // on the cart page this input sits inside the checkout form, whose first
    // submit button is "Check out".
    this.input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.submit();
    });
  }

  submit() {
    const code = this.input.value.trim();
    if (code) this.apply(code);
  }

  // The applied set, straight from the rendered pills.
  get codes() {
    return [...this.querySelectorAll('[data-discount-code]')]
      .map((row) => row.dataset.discountCode)
      .filter(Boolean);
  }

  // Where focus goes after a successful change. The re-render replaces this
  // whole component and the host's restoreFocus finds the replacement by id —
  // but a host can render the field inside a <details> that comes back closed
  // (the drawer does, on the drawer branch of #485), and focusing a control in
  // a closed details is a silent no-op that strands the keyboard user. The
  // disclosure's always-visible summary is the anchor there; a host with no
  // [data-discount-toggle] — this page — keeps the field itself.
  focusAnchor() {
    (this.querySelector('[data-discount-toggle]') ?? this.input).focus();
  }

  // Codes are case-insensitive to the shopper but echoed back by the server in
  // their canonical case, so every comparison here folds case.
  static same(a, b) {
    return String(a).toLowerCase() === String(b).toLowerCase();
  }

  async apply(code) {
    // Already on the cart: re-sending it would be a no-op that still costs a
    // round trip, and re-rendering would flash the surface for no reason.
    if (this.codes.some((applied) => CartDiscount.same(applied, code))) {
      this.input.value = '';
      this.showError(null);
      return;
    }
    const cart = await this.post([...this.codes, code]);
    if (!cart) return;
    // The cart accepted the request but not necessarily the code.
    const refused = cart.discount_codes?.some(
      (entry) => CartDiscount.same(entry.code, code) && entry.applicable === false
    );
    if (refused) {
      this.showError(strings.discountInvalid, code);
      this.input.select();
      return;
    }
    this.input.value = '';
    this.focusAnchor();
    this.settle(cart, strings.discountApplied, code);
  }

  async remove(code) {
    const cart = await this.post(this.codes.filter((applied) => !CartDiscount.same(applied, code)));
    if (!cart) return;
    // The rendered row — and its Remove button, which likely holds focus — is
    // about to be swapped away by the re-render.
    this.focusAnchor();
    this.settle(cart, strings.discountRemoved, code);
  }

  // POST the whole set. A network failure leaves the cart untouched and says so
  // rather than re-rendering into a state the server never confirmed.
  async post(codes) {
    this.busy(true);
    this.showError(null);
    try {
      const response = await fetch(`${routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') }),
      });
      if (!response.ok) throw new Error('Cart discount update failed');
      return await response.json();
    } catch {
      this.showError(strings.discountError);
      return null;
    } finally {
      this.busy(false);
    }
  }

  // Announce through the shared cart live region (the error line has its own
  // role="alert"), then let the host surface catch up.
  settle(cart, template, code) {
    // Deliberately NOT announceCartChange(): that would announce a generic
    // "Cart updated" into the same region a beat later and be the message
    // actually read out, losing which code was applied or removed. Only the
    // event is wanted here — the drawer and the cart page both listen for it
    // and re-render themselves.
    document.dispatchEvent(new CustomEvent('cart:changed', { detail: { cart } }));
    if (template) {
      const region = getCartLiveRegion();
      const message = template.replace('__CODE__', code);
      // Clear first so applying the same code twice is still re-announced.
      region.textContent = '';
      window.setTimeout(() => {
        region.textContent = message;
      }, 100);
    }
  }

  showError(template, code) {
    if (!this.error) return;
    if (!template) {
      this.error.hidden = true;
      this.error.textContent = '';
      return;
    }
    this.error.textContent = code ? template.replace('__CODE__', code) : template;
    this.error.hidden = false;
  }

  busy(state) {
    this.querySelector('[data-discount-apply]')?.toggleAttribute('disabled', state);
    this.toggleAttribute('data-busy', state);
  }
}

/* --------------------------------------------------------------------------
   Cart order note limit (main-cart.liquid)
   -------------------------------------------------------------------------- */
/**
 * <cart-note> — the cart page's order note, bounded to Shopify's own limit.
 *
 * A note over 5000 characters is rejected by Shopify with a bare error page,
 * off the shop entirely and with no way back but the browser button (#485). The
 * textarea's maxlength is the real guard — it stops the characters being typed
 * or pasted, and it holds without JS. This element is what tells the shopper
 * why the field stopped taking input, and holds the submit in the one case
 * maxlength cannot cover: a value that was already over the limit when the
 * field was rendered (maxlength only bounds user edits, never the initial
 * value). On this cart the surrounding form's submit IS checkout, so the guard
 * sits exactly where the error page would have been reached.
 */
class CartNote extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('textarea[name="note"]');
    this.note = this.querySelector('[data-note-limit]');
    if (!this.input) return;
    this.limit = Number(this.input.maxLength);
    if (!Number.isFinite(this.limit) || this.limit <= 0) return;

    this.input.addEventListener('input', () => this.check());
    // A plain POST of the checkout form is what would land on the error page,
    // so the guard belongs on the form's submit rather than on the button.
    this.form = this.input.form;
    this.form?.addEventListener('submit', (event) => {
      if (this.input.value.length <= this.limit) return;
      event.preventDefault();
      this.check();
      this.input.focus();
    });
    // An over-long note already in the field (set before this ran) is reported
    // straight away rather than waiting for an edit that may never come.
    if (this.input.value.length >= this.limit) this.check();
  }

  // Show the limit once the field is full, and clear it as soon as it is not.
  check() {
    if (!this.note) return;
    if (this.input.value.length < this.limit) {
      this.note.hidden = true;
      this.note.textContent = '';
      return;
    }
    const text = (strings.cartNoteLimit ?? 'Order notes are limited to __COUNT__ characters.')
      .replace('__COUNT__', String(this.limit));
    // Re-setting identical text does not re-announce it, and would also flicker
    // the line on every further keystroke against the ceiling.
    if (this.note.textContent !== text) this.note.textContent = text;
    this.note.hidden = false;
  }
}

/* --------------------------------------------------------------------------
   Header height token (consumed by sticky offsets and full-height media)
   -------------------------------------------------------------------------- */
function trackHeaderHeight() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const update = () =>
    document.documentElement.style.setProperty(
      '--header-height',
      `${header.offsetHeight}px`
    );
  update();
  new ResizeObserver(update).observe(header);
}

/* --------------------------------------------------------------------------
   Shared focus trap for body-level drawers (cart / offers / search). Mirrors
   HeaderDrawer.trapFocus so a keyboard/screen-reader user can't Tab out of an
   open drawer into the inert page behind it (WCAG 2.4.3 / 2.1.2).
   -------------------------------------------------------------------------- */
function trapDrawerFocus(container, event) {
  if (!container) return;
  const focusable = [
    ...container.querySelectorAll(
      'a[href], button:not([disabled]):not([tabindex="-1"]), select, input:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])'
    ),
  ].filter(
    (el) =>
      // The dim overlay is a [data-drawer-close] button carrying tabindex="-1"
      // — a click target, never a tab stop. `button:not([disabled])` still
      // matches it, so drop tabindex="-1" here or Tab cycles onto the invisible
      // overlay and the focus ring vanishes for a stop.
      el.getAttribute('tabindex') !== '-1' &&
      (el.offsetParent !== null || el === document.activeElement)
  );
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/* --------------------------------------------------------------------------
   Register
   -------------------------------------------------------------------------- */
const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

define('reveal-on-scroll', RevealOnScroll);
define('contact-success', ContactSuccess);
define('contact-fields', ContactFields);
define('newsletter-success', NewsletterSuccess);
define('product-card', ProductCard);
define('product-form', ProductForm);
define('product-field', ProductField);
define('quick-add-drawer', QuickAddDrawer);
define('countdown-timer', CountdownTimer);
define('count-up', CountUp);
define('cart-drawer', CartDrawer);
define('cart-lines', CartLines);
define('cart-discount', CartDiscount);
define('cart-note', CartNote);

trackHeaderHeight();

/* --------------------------------------------------------------------------
   Statement heading — reveal line by line (each line masked, rising in
   sequence). Splits after fonts load so wrapping is accurate; the accent
   word stays one unit; any failure restores the original heading intact.
   -------------------------------------------------------------------------- */
function splitStatementLines() {
  if (motionReduced()) return;
  document.querySelectorAll('.statement .block-heading.reveal-item--rise').forEach((heading) => {
    if (heading.dataset.split) return;
    // Above the fold the heading has already risen in from a CSS keyframe by
    // the time this runs (#521) — fonts.ready is well after first paint. The
    // split re-hides every line at translateY(112%) and waits for is-revealed
    // to raise them, which would blank settled content and replay the
    // entrance. Leave it whole: it keeps the masked rise, just not per line.
    if (heading.closest('.reveal-on-load')) return;
    if (!heading.classList.contains('type-oversize') && !heading.classList.contains('type-display')) return;
    const original = heading.innerHTML;
    try {
      const units = [];
      heading.childNodes.forEach((node) => {
        if (node.nodeType === 3) {
          node.textContent.split(/(\s+)/).forEach((tok) => {
            if (tok && !/^\s+$/.test(tok)) units.push(document.createTextNode(tok));
          });
        } else if (node.nodeType === 1) {
          units.push(node.cloneNode(true));
        }
      });
      if (units.length < 2) return;
      heading.textContent = '';
      const spans = units.map((u) => {
        const s = document.createElement('span');
        s.style.display = 'inline-block';
        s.appendChild(u);
        heading.appendChild(s);
        heading.appendChild(document.createTextNode(' '));
        return s;
      });
      const lines = [];
      let top = null;
      spans.forEach((s) => {
        if (top === null || s.offsetTop - top > 4) { lines.push([]); top = s.offsetTop; }
        lines[lines.length - 1].push(s);
      });
      const frag = document.createDocumentFragment();
      lines.forEach((group, i) => {
        const line = document.createElement('span');
        line.className = 'sr-line';
        const inner = document.createElement('span');
        inner.className = 'sr-line__in';
        inner.style.setProperty('--sr-i', String(i));
        group.forEach((s, j) => {
          while (s.firstChild) inner.appendChild(s.firstChild);
          if (j < group.length - 1) inner.appendChild(document.createTextNode(' '));
        });
        line.appendChild(inner);
        frag.appendChild(line);
      });
      heading.textContent = '';
      heading.appendChild(frag);
      heading.classList.remove('reveal-item--rise');
      heading.dataset.split = '1';
    } catch (err) {
      heading.innerHTML = original;
    }
  });
}
if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(splitStatementLines);
} else {
  splitStatementLines();
}

/* ==========================================================================
   <background-video> (snippets/content-over-media.liquid, #663).
   A decorative background video over its still: a hosted <video> or a
   YouTube / Vimeo embed. It never plays on its own; the surface that owns it
   (the hero's <hero-slideshow>) calls play() / pause(), so that surface's one
   pause control (WCAG 2.2.2), reduced motion and its off-screen suspend
   govern both kinds. An embed has no src until the first play(), so it never
   loads under reduced motion or on a slide nobody has reached; after that it
   is driven by postMessage. `is-playing` (which lifts the poster, capital.css)
   follows what the player reports, not what was asked, so a blocked autoplay
   keeps the poster up. `data-desktop-only` (a mobile image replaces the
   video) holds playback below 768px.
   ========================================================================== */
const BG_EMBED_RETRY_MS = 750;
const BG_EMBED_RETRIES = 4;

class BackgroundVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.frame = this.querySelector('iframe[data-src]');
    this.wanted = false;
    this.phone = this.hasAttribute('data-desktop-only')
      ? window.matchMedia('(max-width: 767.98px)')
      : null;
    this.apply = this.apply.bind(this);
    this.phone?.addEventListener('change', this.apply);

    if (this.video) {
      this.video.addEventListener('playing', () => this.setPlaying(true));
      this.video.addEventListener('pause', () => this.setPlaying(false));
    }
    if (this.frame) {
      this.onMessage = (event) => this.receive(event);
      window.addEventListener('message', this.onMessage);
      this.frame.addEventListener('load', () => this.handshake());
    }
    // Tell an owning surface that connected first (the editor's re-render
    // upgrades parents before children) that this one can now be driven.
    this.dispatchEvent(new CustomEvent('background-video:connect', { bubbles: true }));
  }

  disconnectedCallback() {
    this.phone?.removeEventListener('change', this.apply);
    if (this.onMessage) window.removeEventListener('message', this.onMessage);
    window.clearTimeout(this.revealTimer);
  }

  play() {
    this.wanted = true;
    this.apply();
  }

  pause() {
    this.wanted = false;
    this.apply();
  }

  apply() {
    const run = this.wanted && !this.phone?.matches;
    if (this.video) {
      if (run) {
        const played = this.video.play();
        if (played && played.then) played.then(() => this.setPlaying(!this.video.paused), () => {});
      } else {
        this.video.pause();
      }
      return;
    }
    if (!this.frame) return;
    if (!run) this.setPlaying(false);
    if (!this.frame.getAttribute('src')) {
      // First play: load the embed, which autoplays muted (its URL says so).
      if (run) this.frame.src = this.frame.dataset.src;
      return;
    }
    this.command(run);
  }

  command(run) {
    const target = this.frame.contentWindow;
    if (!target) return;
    // Each provider ignores the other's message, so send both.
    target.postMessage(JSON.stringify({ event: 'command', func: run ? 'playVideo' : 'pauseVideo', args: '' }), '*');
    target.postMessage(JSON.stringify({ method: run ? 'play' : 'pause' }), '*');
  }

  /* Subscribe to the player's state once it has loaded (YouTube's widget
     handshake; Vimeo's play / pause events plus a getPaused probe, since an
     autoplay may already have started), then restate the wanted state: a
     pause asked for while the embed was loading would otherwise be lost. A
     player still starting up can miss all of it, so it repeats until the
     player reports; one that never does (a changed protocol) gets the video
     shown rather than hidden behind the poster forever. */
  handshake(attempt = 0) {
    const target = this.frame.contentWindow;
    if (!this.frame.getAttribute('src') || !target) return;
    if (attempt === 0) this.reported = false;
    target.postMessage(JSON.stringify({ event: 'listening', id: 'background-video', channel: 'widget' }), '*');
    ['play', 'playing', 'pause', 'ended'].forEach((value) => {
      target.postMessage(JSON.stringify({ method: 'addEventListener', value }), '*');
    });
    target.postMessage(JSON.stringify({ method: 'getPaused' }), '*');
    this.command(this.wanted && !this.phone?.matches);
    window.clearTimeout(this.revealTimer);
    this.revealTimer = window.setTimeout(() => {
      if (this.reported) return;
      if (attempt < BG_EMBED_RETRIES) this.handshake(attempt + 1);
      else this.setPlaying(true);
    }, BG_EMBED_RETRY_MS);
  }

  receive(event) {
    if (!this.frame || event.source !== this.frame.contentWindow) return;
    let data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }
    if (!data || typeof data !== 'object') return;
    // YouTube: playerState 1 is playing; 3 (buffering) keeps the current state.
    let state;
    if (data.event === 'onStateChange') state = data.info;
    else if (data.info && typeof data.info === 'object') state = data.info.playerState;
    if (typeof state === 'number') {
      this.reported = true;
      if (state !== 3) this.setPlaying(state === 1);
      return;
    }
    // Vimeo.
    if (data.event === 'ready') {
      this.handshake();
    } else if (data.event === 'play' || data.event === 'playing') {
      this.reported = true;
      this.setPlaying(true);
    } else if (data.event === 'pause' || data.event === 'ended') {
      this.reported = true;
      this.setPlaying(false);
    } else if (data.method === 'getPaused') {
      this.reported = true;
      this.setPlaying(data.value === false);
    }
  }

  setPlaying(on) {
    this.classList.toggle('is-playing', Boolean(on) && this.wanted);
  }
}

define('background-video', BackgroundVideo);

/* ==========================================================================
   WAVE 2 ADDITION — <hero-slideshow> (sections/hero.liquid).
   Slide rotation for the Gradient Hero. Autoplay is driven by the progress
   rule's CSS fill animation (semantic pace values off/calm/brisk map to the
   --hero-interval token): the fill's animationend advances the slide, so
   pausing the animation (hover/focus/hidden tab — class `is-held`) pauses
   rotation in perfect sync. Under reduced motion the slideshow does NOT
   auto-advance and no fill runs; manual tab/arrow navigation still works and
   incoming content renders settled. Any hero video (incl. a single video
   slide) gets a persistent pause toggle + inactive/off-screen suspend.
   Per-slide copy (#372): a slide's copy is its own content layer
   ([data-hero-copy-layer]); a slide change swaps layers in step with the
   media and re-runs the incoming layer's reveal. The theme editor can re-render
   the section in place without reconnecting this element, so the slide being
   edited is put back after each re-render and slides added that way navigate
   (#620).
   ========================================================================== */
class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.refresh();
    this.index = 0;
    this.userPaused = false;
    this.visible = true;
    this.holds = new Set();
    // A slide's video is a bare hosted <video> or a <background-video> (a
    // YouTube / Vimeo embed, or a hosted video with a poster or mobile
    // image, #663); both answer play() / pause().
    this.hasVideo = this.slides.some((slide) => slide.querySelector('video, background-video'));
    this.syncVideos = this.syncVideos.bind(this);
    // A <background-video> upgraded after this element (the editor's re-render
    // upgrades parents first) announces itself; bring it into step.
    this.addEventListener('background-video:connect', this.syncVideos);

    const autoplays =
      this.slides.length > 1 &&
      (this.dataset.pace === 'calm' || this.dataset.pace === 'brisk') &&
      !motionReduced();
    this.autoplays = autoplays;

    /* Media suspend applies to any hero carrying slide video(s) — even a
       single slide: inactive slides never play, and reduced motion, a hidden
       tab, a scrolled-off-screen hero or a user pause suspend the active video. */
    this.syncVideos();
    document.addEventListener('visibilitychange', this.syncVideos);
    /* One observer for the whole off-screen suspend: a hero scrolled out of
       view stops any slide video AND holds rotation. The Autoplay help text
       promises an off-screen pause and rotating a carousel nobody can see is
       wasted motion, so rotation gets the same hold as a hidden tab. Built
       whenever there is indefinite motion to suspend (rotation or a hero
       video); the rotation hold only applies when the slideshow autoplays. */
    if ((autoplays || this.hasVideo) && 'IntersectionObserver' in window) {
      this.io = new IntersectionObserver((entries) => {
        this.visible = entries[entries.length - 1].isIntersecting;
        if (autoplays) this.hold('offscreen', !this.visible);
        this.syncVideos();
      }, { threshold: 0.2 });
      this.io.observe(this);
    }

    /* Persistent, keyboard-reachable pause/play (WCAG 2.2.2). Bind it whenever
       there's indefinite motion to stop: auto-advancing rotation OR an
       autoplaying hero video (incl. a single video slide). Reduced motion
       auto-runs nothing, so the toggle stays hidden. hold('user') keeps
       rotation paused past the pointer; the same toggle suspends the video.
       syncPlaypause() then shows it only while something on screen moves. */
    if ((autoplays || this.hasVideo) && !motionReduced()) {
      this.playpause = this.querySelector('[data-hero-playpause]');
      bindMotionToggle(this.playpause, (paused) => {
        this.userPaused = paused;
        this.classList.toggle('is-user-paused', paused);
        if (autoplays) this.hold('user', paused);
        this.syncVideos();
      });
      // A mobile image stands in for a video slide below 768px, the same
      // query <background-video> holds playback on.
      this.phone = window.matchMedia('(max-width: 767.98px)');
      this.syncPlaypause = this.syncPlaypause.bind(this);
      this.phone.addEventListener('change', this.syncPlaypause);
      this.syncPlaypause();
    }

    /* Tabs, arrows and arrow keys are delegated from the host. The theme
       editor can re-render this section in place without reconnecting it
       (#309), so a slide added that way brings a tab and arrows this element
       never bound — a hero that started with one slide had no controls bound
       at all (#620). Visible prev/next arrows for mouse users (#273); goTo()
       wraps at the ends. */
    this.onNavClick = (event) => {
      const tab = event.target.closest('[data-hero-tab]');
      if (tab) {
        const i = this.tabs.indexOf(tab);
        if (i > -1) this.goTo(i);
      } else if (event.target.closest('[data-hero-prev]')) {
        this.goTo(this.index - 1);
      } else if (event.target.closest('[data-hero-next]')) {
        this.goTo(this.index + 1);
      }
    };
    this.onNavKeydown = (event) => {
      if (!event.target.closest('[data-hero-nav]')) return;
      if (event.key === 'ArrowRight') this.goTo(this.index + 1);
      if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
    };
    this.addEventListener('click', this.onNavClick);
    this.addEventListener('keydown', this.onNavKeydown);
    /* Theme editor: selecting a slide brings it on screen, and the rotation
       holds until it is deselected, so it can't rotate away mid-edit
       (mirrors <product-showcase>, #246; deselect checks the tracked block,
       so switching A -> B stays held whatever order the editor fires the
       events in). The select the editor fires again after re-rendering the
       section (detail.load) puts the slide straight back, with no crossfade
       and no reveal replay, so each edit doesn't re-animate the copy being
       edited. */
    this.onBlockSelect = (event) => {
      this.refresh();
      const selected = this.slideForEditorBlock(event.target);
      if (selected < 0) return;
      this.editorBlock = event.target;
      this.hold('editor', true);
      if (event.detail && event.detail.load) this.jumpTo(selected);
      else this.goTo(selected);
    };
    this.onBlockDeselect = (event) => {
      if (event.target === this.editorBlock) this.hold('editor', false);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    /* An in-place editor re-render resets this subtree to the server's first
       slide, and strips the host's state classes, without reconnecting this
       element — while the merchant may be editing another slide. Put the
       current slide back whenever that happens. render() only writes what
       differs, so its own writes settle on the next pass. Editor only. */
    if (window.Shopify && window.Shopify.designMode) {
      this.editorObserver = new MutationObserver(() => {
        this.refresh();
        this.render();
      });
      this.editorObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'inert', 'aria-hidden', 'aria-selected'],
      });
    }

    if (autoplays) {
      this.classList.add('is-autoplaying');
      this.addEventListener('animationend', (event) => {
        if (event.target.closest('[data-hero-tab]')) this.goTo(this.index + 1);
      });
      // No hover-pause — the persistent pause/play button is the control (review).
      /* Focus holds the rotation only inside what moves (a slide, or the copy
         on screen), so a keyboard shopper's button never rotates away under
         them. Never the nav: the focus a click leaves on Play held the
         rotation Play had just released, so it never resumed (#693). */
      const moving = (event) => Boolean(event.target.closest('[data-hero-slide], .hero__content-layer'));
      this.addEventListener('focusin', (event) => this.hold('focus', moving(event)));
      this.addEventListener('focusout', () => this.hold('focus', false));
      this.onVisibilityHold = () => this.hold('hidden', document.hidden);
      document.addEventListener('visibilitychange', this.onVisibilityHold);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.syncVideos);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    if (this.onVisibilityHold) document.removeEventListener('visibilitychange', this.onVisibilityHold);
    this.phone?.removeEventListener('change', this.syncPlaypause);
    this.removeEventListener('click', this.onNavClick);
    this.removeEventListener('keydown', this.onNavKeydown);
    this.io?.disconnect();
    this.editorObserver?.disconnect();
  }

  /* Slides, copy layers, tabs and the counter, re-read on demand: the theme
     editor can add, remove or reorder slides in place, so the connect-time
     lists go stale. */
  refresh() {
    this.slides = [...this.querySelectorAll('[data-hero-slide]')];
    this.copyLayers = [...this.querySelectorAll('[data-hero-copy-layer]')];
    this.tabs = [...this.querySelectorAll('[data-hero-tab]')];
    this.counter = this.querySelector('[data-hero-current]');
  }

  /* The slide an editor block selection belongs to, -1 when it isn't ours. */
  slideForEditorBlock(target) {
    return this.slides.findIndex((slide) => slide.contains(target));
  }

  /* Slide i's copy layer (its data-hero-copy names the layer's id), or null
     for a slide with no copy. */
  copyFor(i) {
    const id = this.slides[i]?.dataset.heroCopy;
    return this.copyLayers.find((layer) => layer.id === id) || null;
  }

  /* When a slide change actually changes the copy, the incoming layer's
     entrance re-runs (the restart form: it was hidden, so a plain replay
     would show nothing). Under reduced motion replayReveal does nothing and
     the copy simply appears settled. */
  replayCopy(outgoing) {
    const incoming = this.copyFor(this.index);
    if (incoming && incoming !== outgoing) {
      replayReveal(incoming.querySelector('reveal-on-scroll'), { restart: true });
    }
  }

  hold(key, on) {
    if (on) this.holds.add(key);
    else this.holds.delete(key);
    this.classList.toggle('is-held', this.holds.size > 0);
  }

  /* A hero <video> plays only while it is the active slide and the surface is
     running (not user-paused, reduced-motion, or a hidden tab). Rotation is
     NOT gated on video playback: hero videos loop and so never fire `ended`,
     which previously wedged the slideshow forever via hold('video'). */
  syncVideos() {
    const canPlay = !motionReduced() && !this.userPaused && !document.hidden && this.visible;
    this.slides.forEach((slide, i) => {
      const video = slide.querySelector('background-video') || slide.querySelector('video');
      // A <background-video> not upgraded yet has no play(); its connect
      // event calls back here once it has.
      if (!video || typeof video.play !== 'function') return;
      if (i === this.index && canPlay) {
        const played = video.play();
        if (played && played.catch) played.catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  /* The pause control shows while something on screen moves: on every slide
     while the slideshow rotates, but at Pace Off only on a slide playing its
     own video, so an image slide hides it (#695). In the nav rail it keeps
     its slot (capital.css), so nothing else moves. Arrow keys step slides
     from it, so focus goes to the next arrow before it hides. */
  syncPlaypause() {
    const button = this.playpause;
    if (!button) return;
    const hide = !this.autoplays && !this.slideMoves(this.index);
    if (hide && button === document.activeElement && document.hasFocus()) {
      this.querySelector('[data-hero-next]')?.focus();
    }
    if (button.hidden !== hide) button.hidden = hide;
  }

  /* Whether slide i has motion of its own: a video, unless a mobile image
     replaces it at this width (data-desktop-only). */
  slideMoves(i) {
    const slide = this.slides[i];
    const bg = slide?.querySelector('background-video');
    if (bg) return !(bg.hasAttribute('data-desktop-only') && this.phone?.matches);
    return Boolean(slide?.querySelector('video'));
  }

  goTo(next) {
    const total = this.slides.length;
    if (total < 2) return;
    const incoming = ((next % total) + total) % total;
    if (incoming === this.index) return;
    const outgoingCopy = this.copyFor(this.index);
    this.index = incoming;
    this.classList.add('is-swapped');
    this.render();
    this.replayCopy(outgoingCopy);
    this.syncVideos();
  }

  /* Editor re-render: put the slide straight back — no crossfade and no
     reveal replay (is-instant suspends the slide and copy transitions for
     this one swap). */
  jumpTo(index) {
    this.index = index;
    this.classList.add('is-instant');
    this.render();
    void this.offsetWidth;
    this.classList.remove('is-instant');
    this.syncVideos();
  }

  /* Write the current slide into the DOM: the slides, the copy layer it
     shows (the rest inert and aria-hidden, so neither focus nor a screen
     reader reaches copy that isn't on screen), the tabs and the counter. It
     only touches what differs, so the editor observer can re-assert it after
     a re-render without feeding itself. */
  render() {
    if (!this.slides.length) return;
    this.index = Math.min(this.index, this.slides.length - 1);
    const set = (el, name, value) => {
      if (el.getAttribute(name) !== value) el.setAttribute(name, value);
    };
    const show = (el, active) => {
      el.classList.toggle('is-active', active);
      el.toggleAttribute('inert', !active);
      set(el, 'aria-hidden', active ? 'false' : 'true');
    };
    this.slides.forEach((slide, i) => show(slide, i === this.index));
    const copy = this.copyFor(this.index);
    this.copyLayers.forEach((layer) => show(layer, layer === copy));
    this.tabs.forEach((tab, i) => set(tab, 'aria-selected', i === this.index ? 'true' : 'false'));
    const current = String(this.index + 1).padStart(2, '0');
    if (this.counter && this.counter.textContent !== current) this.counter.textContent = current;
    // The same in-place re-render strips the host's state classes.
    this.classList.toggle('is-autoplaying', Boolean(this.autoplays));
    this.classList.toggle('is-held', this.holds.size > 0);
    this.classList.toggle('is-user-paused', this.userPaused);
    this.syncPlaypause();
  }
}

define('hero-slideshow', HeroSlideshow);
/* ===================== END WAVE 2 ADDITION — <hero-slideshow> ============ */

/* ==========================================================================
   WAVE 2 ADDITION — PDP (main-product.liquid): <variant-picker> resolves
   option radios or dropdowns (snippets/variant-picker.liquid, #644) to a
   variant and refreshes price/inventory/button state via
   the Section Rendering API; <media-gallery> tracks the scroll-snap slides
   and syncs thumbnail/dot controls. ≤120 lines, no libraries.
   ========================================================================== */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variant-data]')?.textContent || '[]');
    this.sectionEl = this.closest('.shopify-section') ?? document;
    this.hideSoldOut = this.hasAttribute('data-hide-sold-out');
    // Dropdown style (#644): every value each <select> can offer, in the
    // product's order. A hidden value leaves the list altogether (iOS shows a
    // hidden <option> regardless), so this is where it waits to come back.
    this.lists = new Map([...this.querySelectorAll('.pdp-option__select')].map((select) => [select, [...select.options]]));
    this.addEventListener('change', (event) => {
      if (event.target.matches?.('.pdp-option__input, .pdp-option__select')) this.onChange();
    });
    this.paintAvailability(this.selectedOptions());
  }

  // One entry per option: its <select>, or its radios. Scoped to the option
  // controls themselves: an option can also hold the size guide's <dialog>
  // (#372), whose merchant-written page may carry inputs of its own.
  optionGroups() {
    return [...this.querySelectorAll('[data-option-fieldset]')].map((set) => {
      const select = set.querySelector('.pdp-option__select');
      return { set, select, values: select ? this.lists.get(select) : [...set.querySelectorAll('.pdp-option__input')] };
    });
  }

  selectedOptions() {
    return this.optionGroups().map(({ set, select }) => (select ?? set.querySelector('.pdp-option__input:checked'))?.value);
  }

  // Availability reads top-down, as Shopify's option values do: a value is
  // available when an in-stock variant has it with the values chosen above it.
  inStock(values) {
    return this.variants.some((v) => v.available && values.every((o, j) => v.options[j] === o));
  }

  // With sold-out values hidden, a choice can leave a later option on a value
  // it has just hidden. Move that option to its first value still in stock,
  // so the shopper always lands on something they can buy.
  settle(selected) {
    if (!this.hideSoldOut) return selected;
    this.optionGroups().forEach(({ values }, i) => {
      const prefix = selected.slice(0, i);
      if (this.inStock(prefix.concat(selected[i]))) return;
      const next = values.find((control) => this.inStock(prefix.concat(control.value)));
      if (next) selected[i] = next.value;
    });
    return selected;
  }

  // Marks each value unavailable or not (struck pills; "(unavailable)" in a
  // dropdown entry) and, with sold-out values hidden, hides the unavailable
  // ones, except the chosen value and except in an option where nothing is
  // available, which keeps every value so the shopper is never left with
  // nothing to choose. Then sets each control to `selected`.
  paintAvailability(selected) {
    this.optionGroups().forEach(({ select, values }, i) => {
      const prefix = selected.slice(0, i);
      const ok = values.map((control) => this.inStock(prefix.concat(control.value)));
      const hide = this.hideSoldOut && ok.includes(true);
      const shown = values.filter((control, k) => !hide || ok[k] || control.value === selected[i]);
      if (select) {
        const pattern = this.dataset.valueUnavailable;
        values.forEach((option, k) => {
          option.textContent = ok[k] || !pattern ? option.value : pattern.replace('__VALUE__', option.value);
        });
        const listed = [...select.options];
        if (shown.length !== listed.length || shown.some((option, k) => option !== listed[k])) select.replaceChildren(...shown);
        shown.forEach((option) => {
          option.hidden = false;
          option.disabled = false;
        });
        select.value = selected[i];
        return;
      }
      values.forEach((input, k) => {
        const hidden = !shown.includes(input);
        input.classList.toggle('is-unavailable', !ok[k]);
        input.checked = input.value === selected[i];
        input.hidden = hidden;
        if (input.labels?.[0]) input.labels[0].hidden = hidden;
      });
    });
  }

  onChange() {
    const selected = this.settle(this.selectedOptions());
    const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
    this.optionGroups().forEach(({ set }, i) => {
      const label = set.querySelector('[data-option-label]');
      if (label) label.textContent = selected[i] ?? '';
    });
    this.paintAvailability(selected);
    if (variant) {
      // data-update-url="false" marks a picker that isn't on its product's own
      // page — the quick-add drawer (#603) — whose address bar belongs to the
      // collection behind it, not to this product.
      if (this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      if (variant.featured_media_id) this.sectionEl.querySelector('media-gallery')?.goTo(variant.featured_media_id);
    }
    // A small, bubbling seam so dependent blocks (e.g. <pickup-availability>)
    // can react to the selected variant without reaching into this class.
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    this.refresh(variant);
  }

  // The dynamic-checkout button (Shop Pay / Apple Pay) submits the hidden
  // [data-variant-id-input] straight to checkout, so it must never stay live
  // for a selection that no longer resolves to a variant. Hide + disable it
  // alongside the main Add button when there is no matching variant.
  setDynamicCheckout(enabled) {
    this.sectionEl.querySelectorAll('[data-buy-buttons] [data-dynamic-checkout], [data-buy-buttons] .shopify-payment-button').forEach((el) => {
      el.hidden = !enabled;
      el.querySelectorAll('button').forEach((b) => b.toggleAttribute('disabled', !enabled));
    });
  }

  async refresh(variant) {
    const q = (selector) => this.sectionEl.querySelector(selector);
    const button = q('[data-atc-button]');
    const label = q('[data-atc-label]');
    // Abort any in-flight refresh so a slow response for an earlier selection
    // can't land after — and overwrite — a later one (latest request wins).
    this.controller?.abort();
    const idInput = q('[data-variant-id-input]');
    if (!variant) {
      // No matching variant: disable the Add button AND neutralise the buy
      // form so the previous variant's id can't be dynamic-checked-out. Clear
      // the hidden id input and hide/disable the dynamic-checkout button.
      button?.setAttribute('disabled', '');
      if (label) label.textContent = this.dataset.unavailableText ?? '';
      if (idInput) idInput.value = '';
      this.setDynamicCheckout(false);
      return;
    }
    if (idInput) idInput.value = variant.id;
    this.setDynamicCheckout(variant.available);
    this.controller = new AbortController();
    const { signal } = this.controller;
    q('[data-buy-buttons]')?.classList.add('is-loading');
    try {
      const response = await fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${this.dataset.sectionId}`, { signal });
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      // [data-payment-terms] is in this list because the Shop Pay Installments
      // figure is derived from the variant's PRICE — leave it behind and a £20
      // variant's "4 payments of £5" survives the switch to the £200 one, which
      // is a false claim about what the shopper would pay. Replacing the node
      // re-upgrades <shopify-payment-terms> on insert, so it re-renders itself.
      ['[data-product-price]', '[data-inventory]', '[data-payment-terms]'].forEach((selector) => {
        const fresh = doc.querySelector(selector);
        if (fresh && q(selector)) q(selector).replaceWith(fresh);
      });
      const freshButton = doc.querySelector('[data-atc-button]');
      if (freshButton && button) {
        button.toggleAttribute('disabled', freshButton.hasAttribute('disabled'));
        if (label) label.textContent = doc.querySelector('[data-atc-label]')?.textContent ?? '';
      }
    } catch (error) {
      /* network hiccup, or superseded by a later selection (aborted): keep state */
      if (error.name === 'AbortError') return;
    } finally {
      // Only clear loading if we are still the latest request — an abort means a
      // newer refresh has already taken over the buy column's loading state.
      if (this.controller?.signal === signal) q('[data-buy-buttons]')?.classList.remove('is-loading');
    }
  }
}

class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.nav = this.querySelector('[data-gallery-nav]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.controls = [...this.querySelectorAll('[data-gallery-goto]')];
    this.controls.forEach((control, index) => control.addEventListener('click', () => this.show(index)));
    // The PDP stacks every image on desktop and the PAGE scrolls through them
    // ([data-stacked]); mobile — and the Featured product gallery at every
    // viewport — is a horizontal scroll-snap carousel. The active-thumb
    // observer and the thumb-click scroll both switch on this, and rebuild if
    // the mode changes.
    this.desktop = matchMedia('(min-width: 990px)');
    this.stacksOnDesktop = this.hasAttribute('data-stacked');
    // "Natural" ratio: every item keeps its own shape, so the carousel's height
    // follows the item on screen rather than the tallest one.
    this.natural = this.hasAttribute('data-natural');
    // A thumbnail press walks the carousel across every slide between here and
    // there, and each one crosses the observer's threshold on the way. Marking
    // them drags the rail to each in turn and back — the jiggle (#482). The
    // press marks its own destination, so the rail is held from the moment it
    // is pressed until the main image lands on it. A swipe holds nothing, so
    // the further thumbnails still come into view as the shopper goes.
    this.holdIndex = null;
    this.holdLeft = 0;
    if (this.track && this.slides.length > 1) this.buildObserver();
    // Crossing the mobile/desktop line is a significant resize: the gallery is
    // rebuilt around it (stack ↔ carousel), and anything playing stops rather
    // than carrying on inside a frame that has just changed shape (#469, item
    // 7). Bound whatever the media count, so a single video pauses too.
    this.onModeChange = () => {
      this.clearHold();
      this.pauseMediaExcept(null);
      if (this.track && this.slides.length > 1) this.buildObserver();
      this.syncHeight();
    };
    this.desktop.addEventListener('change', this.onModeChange);
    if (this.track) {
      this.onTrackScroll = () => {
        if (this.natural) this.syncHeight();
        this.releaseHold();
      };
      this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
      // Taking hold of the carousel mid-travel cancels the browser's smooth
      // scroll, so the press will never reach the slide it was waiting on:
      // the rail goes back to following the shopper there and then.
      this.onTrackGrab = () => this.clearHold();
      ['pointerdown', 'touchstart'].forEach((type) =>
        this.track.addEventListener(type, this.onTrackGrab, { passive: true })
      );
    }
    if (this.natural && this.track) {
      // Slides settle late — a lazy image decoding, a font landing, a resize —
      // and each one changes the height the track should be wearing.
      this.slideResize = new ResizeObserver(() => this.syncHeight());
      this.slides.forEach((slide) => this.slideResize.observe(slide));
    }
    this.openInitialMedia();
    this.syncHeight();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.slideResize?.disconnect();
    this.desktop?.removeEventListener('change', this.onModeChange);
    this.track?.removeEventListener('scroll', this.onTrackScroll);
    ['pointerdown', 'touchstart'].forEach((type) =>
      this.track?.removeEventListener(type, this.onTrackGrab)
    );
  }

  // True only where the media really is a vertical stack the page scrolls.
  get stacked() {
    return this.stacksOnDesktop && this.desktop.matches;
  }

  buildObserver() {
    this.observer?.disconnect();
    // Stack: watch the viewport with a centre focus-line so the active thumb
    // tracks the page scroll. Carousel: watch WITHIN the horizontal track —
    // every slide in a carousel sits on the same viewport centre-line, so
    // watching the viewport marks them all at once and the LAST one wins,
    // which is how a video thumbnail came to look selected on load (#461).
    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) this.mark(this.slides.indexOf(entry.target));
      }),
      this.stacked
        ? { root: null, rootMargin: '-45% 0px -45% 0px', threshold: 0 }
        : { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  // Open on the media the section nominated — the selected variant's own image,
  // or the product's featured image — rather than whatever happens to be first
  // (#461). The carousel is moved there without animation, so it is simply
  // already there on load; a stack is left alone, since the page opens at its
  // top and the observer marks whatever the shopper scrolls to.
  openInitialMedia() {
    const id = this.dataset.initialMediaId;
    if (!id || this.stacked || !this.track) return;
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === id);
    if (index <= 0) return;
    this.track.scrollTo({ left: this.slides[index].offsetLeft - this.track.offsetLeft, behavior: 'auto' });
    this.mark(index);
  }

  // Natural ratio in a carousel: the track wears the height of the item ON
  // SCREEN, interpolated across a swipe between the two either side of the
  // scroll position, so the frame grows and shrinks with the media instead of
  // sitting at the tallest item's height with a band of empty surface around
  // the short ones (#469, items 4 and 6). A stack needs none of this — every
  // item is laid out at its own height already.
  syncHeight() {
    if (!this.track) return;
    if (!this.natural || this.stacked) {
      this.track.style.removeProperty('height');
      return;
    }
    const step = this.track.clientWidth;
    if (!step || !this.slides.length) return;
    const position = this.track.scrollLeft / step;
    const first = Math.min(this.slides.length - 1, Math.max(0, Math.floor(position)));
    const next = Math.min(this.slides.length - 1, first + 1);
    const between = Math.min(1, Math.max(0, position - first));
    const height =
      this.slides[first].offsetHeight * (1 - between) + this.slides[next].offsetHeight * between;
    if (height > 0) this.track.style.height = `${Math.round(height)}px`;
  }

  // Nothing off-screen keeps playing: hosted <video> pauses directly, and a
  // YouTube/Vimeo embed is asked to pause by postMessage (the YouTube src opts
  // into the JS API for exactly this, so no reload is needed). Both providers
  // ignore the other's message, so send both rather than sniffing the src.
  // A null slide means nothing is spared — stop everything.
  pauseMediaExcept(activeSlide) {
    this.slides.forEach((slide) => {
      if (slide === activeSlide) return;
      slide.querySelectorAll('video').forEach((video) => {
        if (!video.paused) video.pause();
      });
      slide.querySelectorAll('iframe').forEach((frame) => {
        frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        frame.contentWindow?.postMessage('{"method":"pause"}', '*');
      });
    });
  }

  mark(index) {
    // Held by a thumbnail press: everything it travels past is ignored until it
    // lands, so the rail moves once, to the thumbnail that was pressed (#482).
    if (this.holdIndex !== null && index !== this.holdIndex) return;
    this.controls.forEach((control, i) => {
      control.classList.toggle('is-active', i === index);
      if (i === index) control.setAttribute('aria-current', 'true');
      else control.removeAttribute('aria-current');
    });
    if (index !== this.active) {
      this.active = index;
      this.trackControl(this.controls[index]);
      // Swiping the carousel IS switching media, so stop what was playing. A
      // stack is different: every image is on screen at once and a nudge of
      // page scroll must not pause a video the shopper is watching — there,
      // only an explicit thumbnail press (show()) counts as a switch.
      if (!this.stacked) this.pauseMediaExcept(this.slides[index]);
    }
  }

  // Keep the active control in view inside the nav: on mobile the thumbnail
  // strip follows the main image as it is swiped (so the further thumbnails come
  // into view), and the desktop rail follows the page scroll. Scoped to the
  // nav's own scroll — computed from client rects so it never moves the page.
  trackControl(control) {
    const nav = this.nav;
    if (!control || !nav) return;
    const behavior = motionReduced() ? 'auto' : 'smooth';
    const navBox = nav.getBoundingClientRect();
    const controlBox = control.getBoundingClientRect();
    if (nav.scrollWidth > nav.clientWidth + 1) {
      const delta = controlBox.left - navBox.left - (nav.clientWidth - controlBox.width) / 2;
      nav.scrollTo({ left: nav.scrollLeft + delta, behavior });
    } else if (nav.scrollHeight > nav.clientHeight + 1) {
      const delta = controlBox.top - navBox.top - (nav.clientHeight - controlBox.height) / 2;
      nav.scrollTo({ top: nav.scrollTop + delta, behavior });
    }
  }

  // Hold the rail for a press with somewhere to travel; one on the slide already
  // on screen has nothing to wait for. The destination is clamped to the track's
  // own scroll range, so a hold always has an arrival it can actually reach.
  holdRail(index, left) {
    const limit = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const target = Math.min(Math.max(left, 0), limit);
    if (Math.abs(this.track.scrollLeft - target) <= 2) {
      this.clearHold();
      return;
    }
    this.holdIndex = index;
    this.holdLeft = target;
  }

  // Released the moment the main image reaches the slide that was pressed.
  releaseHold() {
    if (this.holdIndex === null) return;
    if (Math.abs(this.track.scrollLeft - this.holdLeft) <= 2) this.clearHold();
  }

  clearHold() {
    this.holdIndex = null;
  }

  goTo(mediaId) {
    this.show(this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId)));
  }

  show(index) {
    const slide = this.slides[index];
    if (!slide || !this.track) return;
    const behavior = motionReduced() ? 'auto' : 'smooth';
    if (this.stacked) {
      /* Desktop stack: walk the PAGE to the image (scroll-margin-top clears the
         sticky header). The buy column is sticky, so it stays in view. */
      slide.scrollIntoView({ behavior, block: 'start' });
    } else {
      /* Carousel: nudge the main image across; the page stays put. */
      const left = slide.offsetLeft - this.track.offsetLeft;
      this.holdRail(index, left);
      this.track.scrollTo({ left, behavior });
    }
    this.mark(index);
    // A carousel switch is already covered by mark(); a stack's is not (there,
    // mark() deliberately keeps its hands off playback — see above), so a
    // thumbnail press is where the stack stops what was playing.
    if (this.stacked) this.pauseMediaExcept(slide);
  }
}

/**
 * <sticky-atc> — the mobile scroll-triggered add-to-cart bar.
 *
 * Reveals once the main [data-atc-button] scrolls out of view, mirrors the
 * live price + sold-out state from the buy column (which <variant-picker>
 * refreshes via the Section Rendering API), and adds the CURRENTLY selected
 * variant by submitting the shared product form (button[form=…] — no
 * duplicate form). Never steals focus; degrades to nothing without JS.
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') ?? document;
    this.info = this.section.querySelector('.pdp__info');
    this.mainButton = this.section.querySelector('[data-atc-button]');
    this.priceMirror = this.querySelector('[data-sticky-price]');
    this.addButton = this.querySelector('[data-sticky-add]');
    this.addLabel = this.querySelector('[data-sticky-add-label]');
    if (!this.mainButton) return;

    // Two gates decide visibility, both feeding update(): the shopper has
    // scrolled PAST the main Add button, AND the footer is not yet on screen.
    this.scrolledPast = false;
    this.footerOnScreen = false;

    // Reveal only once the main Add button has scrolled ABOVE the viewport (the
    // shopper has passed it) — not while it's still below the fold on load. No
    // focus is moved.
    this.io = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        this.scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.update();
      },
      { threshold: 0 }
    );
    this.io.observe(this.mainButton);

    // Hide again once the footer reaches the viewport, so the fixed bar never
    // sits over the footer's "Back to top" link. The bar is pinned to the
    // viewport bottom and the footer scrolls up into it from the bottom, so the
    // instant any footer pixel shows is the instant the bar would cover it.
    this.observeFooter();

    this.sync();
    // The buy column mutates on variant change: the price node is replaced
    // wholesale and the Add button's [disabled] toggles. Watch the container.
    if (this.info) {
      this.mutation = new MutationObserver(() => this.sync());
      this.mutation.observe(this.info, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.footerIo?.disconnect();
    if (this.onSectionLoad) document.removeEventListener('shopify:section:load', this.onSectionLoad);
    this.mutation?.disconnect();
  }

  // Watch the site footer for viewport entry. The footer is re-resolved lazily
  // rather than captured once: the Theme Editor swaps in a fresh
  // .shopify-section node for the footer on every setting change, which would
  // orphan a held reference and freeze the gate — mirrors the header's
  // transparent-offset re-observe idiom.
  observeFooter() {
    this.footerIo = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        this.footerOnScreen = entry.isIntersecting;
        this.update();
      },
      { threshold: 0 }
    );
    const observe = () => {
      this.footerIo.disconnect();
      const footer = document.querySelector('.site-footer');
      if (footer) this.footerIo.observe(footer);
      else this.footerOnScreen = false;
      this.update();
    };
    observe();
    if (window.Shopify && window.Shopify.designMode) {
      this.onSectionLoad = observe;
      document.addEventListener('shopify:section:load', observe);
    }
  }

  update() {
    this.classList.toggle('is-visible', this.scrolledPast && !this.footerOnScreen);
  }

  sync() {
    this.syncPrice();
    this.syncAvailability();
  }

  syncPrice() {
    if (!this.priceMirror) return;
    const priceEl = this.section.querySelector('[data-product-price]');
    if (!priceEl) return;
    const current = priceEl.querySelector('.pdp-price__current');
    let text;
    if (current) {
      const clone = current.cloneNode(true);
      clone.querySelectorAll('.visually-hidden').forEach((n) => n.remove());
      text = clone.textContent.trim();
    } else {
      text = priceEl.textContent.replace(/\s+/g, ' ').trim();
    }
    this.priceMirror.textContent = text;
  }

  syncAvailability() {
    const button = this.section.querySelector('[data-atc-button]');
    if (!button || !this.addButton) return;
    this.addButton.toggleAttribute('disabled', button.hasAttribute('disabled'));
    const label = this.section.querySelector('[data-atc-label]');
    if (label && this.addLabel) this.addLabel.textContent = label.textContent.trim();
  }
}

/**
 * <pickup-availability> — optional PDP store-pickup block (Wave 2B.2).
 *
 * Server-rendered per variant (snippets/pickup-availability.liquid). Renders
 * nothing when the variant has no pickup locations, so it is safe on stores
 * with none. On variant change it listens for the <variant-picker>
 * 'variant:change' seam and re-fetches the block markup for the new variant via
 * the Section Rendering API (mirroring VariantPicker/CartDrawer's fetch +
 * DOMParser idiom). The "other stores" list is a native modal <dialog> — its
 * focus trap, Esc and focus-return are the platform's.
 */
class PickupAvailability extends HTMLElement {
  connectedCallback() {
    this.url = this.dataset.url;
    this.sectionId = this.dataset.sectionId;
    // Scope to THIS product's section. 'variant:change' bubbles, so a
    // <variant-picker> in another section (e.g. a <featured-product> on the
    // same page) reaches document too — listening there would refresh this
    // PDP's pickup panel with the wrong product's variant. Listen on our own
    // section wrapper and guard the event's source section defensively.
    this.section = this.closest('.shopify-section');
    this.eventTarget = this.section ?? document;
    this.onVariant = (event) => {
      if (this.section && event.target.closest('.shopify-section') !== this.section) return;
      this.refresh(event.detail?.variant);
    };
    this.eventTarget.addEventListener('variant:change', this.onVariant);
    this.bindModal();
  }

  disconnectedCallback() {
    this.eventTarget?.removeEventListener('variant:change', this.onVariant);
    this.controller?.abort();
  }

  bindModal() {
    const opener = this.querySelector('[data-pickup-more]');
    const dialog = this.querySelector('[data-pickup-modal]');
    if (!opener || !dialog || typeof dialog.showModal !== 'function') return;
    opener.addEventListener('click', () => dialog.showModal());
    dialog.querySelector('[data-pickup-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  }

  async refresh(variant) {
    if (!variant || !this.url || !this.sectionId) return;
    // Latest-wins: abort a prior in-flight fetch so a slow response for an
    // earlier variant can't land after and overwrite a later selection.
    this.controller?.abort();
    this.controller = new AbortController();
    try {
      const response = await fetch(`${this.url}?variant=${variant.id}&section_id=${this.sectionId}`, { signal: this.controller.signal });
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('pickup-availability');
      // Swap the inner markup (empty when the new variant has no pickup
      // locations — the element then collapses via CSS :empty). Root attributes
      // and shopify_attributes stay put.
      if (fresh) {
        this.innerHTML = fresh.innerHTML;
        this.bindModal();
      }
    } catch {
      /* Network hiccup: leave the current pickup state in place. */
    }
  }
}

/**
 * <quantity-input> — the PDP pre-add quantity field, flanked by −/+ steppers so
 * it is a custom control rather than the browser's default number spinner.
 *
 * Unlike the cart's stepper (in <cart-drawer>, which mutates a line over ajax),
 * this only bounds a form value before add-to-cart: a tap adjusts the field,
 * clamps to its min/max, disables − at the minimum, and fires `change` so any
 * listener reacts. No JS → the field is still a usable number input. The cart's
 * shared quantity component (#424) is the eventual single home for this.
 */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    if (!this.input) return;
    // Sibling stock-ceiling message (lives in the block, outside this element so
    // it doesn't sit inside the inline-flex stepper row).
    this.note = this.parentElement?.querySelector('[data-quantity-note]') ?? null;
    this.addEventListener('click', (event) => {
      const step = event.target.closest('[data-quantity-step]');
      if (!step) return;
      const changed = this.setValue(Number(this.input.value || 0) + Number(step.dataset.quantityStep));
      if (changed) this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    // Live-clamp typed values so an over-stock quantity can never reach the cart;
    // `change` (blur/Enter) also floors an emptied field back to the minimum.
    this.input.addEventListener('input', () => this.setValue(this.input.value, false));
    this.input.addEventListener('change', () => this.setValue(this.input.value));
    // The buy column re-renders price/inventory on a variant switch but not this
    // field, so adopt the newly selected variant's stock ceiling here (#449).
    this.closest('.shopify-section')?.addEventListener('variant:change', (event) => this.onVariantChange(event));
    this.update();
  }

  get min() {
    return this.input.min === '' ? 1 : Number(this.input.min);
  }

  get max() {
    return this.input.max === '' ? Infinity : Number(this.input.max);
  }

  /**
   * Clamp a requested quantity into [min, max] and reflect it in the field.
   * `commit` floors an empty field to the minimum (on blur/Enter); mid-edit an
   * empty field is left alone so it stays retypable. Surfaces the stock note
   * whenever the request was above the available stock. Returns whether the
   * field value changed.
   */
  setValue(value, commit = true) {
    const empty = value === '' || value == null;
    if (empty && !commit) {
      this.update();
      return false;
    }
    const requested = empty ? this.min : Number(value);
    if (Number.isNaN(requested)) {
      this.update();
      return false;
    }
    const next = Math.min(this.max, Math.max(this.min, requested));
    const changed = String(next) !== this.input.value;
    if (changed) this.input.value = String(next);
    this.showLimit(requested > this.max ? next : null);
    this.update();
    return changed;
  }

  // Adopt the selected variant's stock ceiling (carried on the variant JSON as
  // `stock`), then re-clamp so a switch to a lower-stock variant can't leave an
  // over-stock value behind.
  onVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant) return;
    const stock = Number(variant.stock);
    if (Number.isFinite(stock) && stock > 0) this.input.max = String(stock);
    else this.input.removeAttribute('max');
    this.setValue(this.input.value || this.min);
  }

  // Show (and fill) the stock note when a ceiling was hit, hide it otherwise.
  // Built here rather than server-side because the count changes with the
  // selected variant.
  showLimit(available) {
    if (!this.note) return;
    if (available == null) {
      this.note.hidden = true;
      this.note.textContent = '';
      return;
    }
    this.note.textContent = (strings.quantityMax ?? 'Only __COUNT__ available').replace('__COUNT__', available);
    this.note.hidden = false;
  }

  // Keep − disabled at the minimum and + disabled at the maximum, so the
  // steppers can't push the field outside the available range.
  update() {
    const value = Number(this.input.value || 0);
    const dec = this.querySelector('[data-quantity-step="-1"]');
    const inc = this.querySelector('[data-quantity-step="1"]');
    if (dec) dec.toggleAttribute('disabled', value <= this.min);
    if (inc) inc.toggleAttribute('disabled', value >= this.max);
  }
}

/**
 * <share-button> — the PDP share control. Prefers the browser's native share
 * sheet (navigator.share); where that is unavailable it falls back to a
 * copy-link disclosure — a read-only URL field and a copy button that writes to
 * the clipboard (with the field left selected if the clipboard is blocked). No
 * JS → the disclosure still opens to a selectable link. Native-first like
 * Dawn's, but its own implementation.
 */
class ShareButton extends HTMLElement {
  connectedCallback() {
    this.shareUrl = this.dataset.shareUrl || window.location.href;
    this.shareTitle = this.dataset.shareTitle || document.title;
    this.toggle = this.querySelector('[data-share-toggle]');
    this.copyButton = this.querySelector('[data-share-copy]');
    // Native share: intercept the disclosure summary so it opens the OS share
    // sheet instead of revealing the copy fallback.
    if (navigator.share && this.toggle) {
      this.toggle.addEventListener('click', (event) => {
        event.preventDefault();
        navigator.share({ title: this.shareTitle, url: this.shareUrl }).catch(() => {});
      });
    }
    this.copyButton?.addEventListener('click', () => this.copy());
  }

  async copy() {
    try {
      await navigator.clipboard.writeText(this.shareUrl);
    } catch {
      this.querySelector('[data-share-url-field]')?.select();
      return; /* clipboard blocked: leave the link selected to copy by hand */
    }
    const label = this.copyButton.querySelector('[data-share-copy-label]');
    if (!label || this.copyButton.dataset.copyResting) return;
    this.copyButton.dataset.copyResting = label.textContent;
    label.textContent = this.copyButton.dataset.copiedLabel || 'Copied';
    window.setTimeout(() => {
      label.textContent = this.copyButton.dataset.copyResting;
      delete this.copyButton.dataset.copyResting;
    }, 2000);
  }
}

/**
 * <size-guide> — the variant picker's "Size guide" (#372). A text button
 * beside the option the merchant names opens a native modal <dialog> holding a
 * page they wrote, rendered server-side. The platform owns the focus trap, Esc
 * and the inert page behind; a click on the backdrop closes it as well, and
 * focus goes back to the button that opened it — the same native-dialog idiom
 * as the pickup "other stores" list.
 */
class SizeGuide extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.opener = this.querySelector('[data-size-guide-open]');
    this.dialog = this.querySelector('[data-size-guide-dialog]');
    if (!this.opener || !this.dialog || typeof this.dialog.showModal !== 'function') return;
    this.wired = true;
    this.opener.addEventListener('click', () => {
      if (!this.dialog.open) this.dialog.showModal();
    });
    this.dialog.querySelector('[data-size-guide-close]')?.addEventListener('click', () => this.dialog.close());
    // The panel fills the dialog box, so a click that lands on the <dialog>
    // itself landed on the backdrop.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    // Browsers restore focus on close; say so explicitly for the ones that
    // don't, so a keyboard user is never dropped on <body>.
    this.dialog.addEventListener('close', () => {
      if (this.isConnected) this.opener.focus();
    });
  }
}

define('variant-picker', VariantPicker);
define('media-gallery', MediaGallery);
define('sticky-atc', StickyAtc);
define('pickup-availability', PickupAvailability);
define('quantity-input', QuantityInput);
define('share-button', ShareButton);
define('size-guide', SizeGuide);
/* ================= END WAVE 2 ADDITION — PDP custom elements ============= */

/* ==========================================================================
   WAVE 2 ADDITION — product-grid (The Intro-Tile Grid) — START
   <intro-tile-grid>: the tab rail (real tablist, 350ms panel crossfade) and
   the carousel controls (progress rule + prev/next arrows). Carousel
   scrolling, snap and the pinned intro tile are pure CSS (scroll-snap +
   position: sticky) — the arrows just page the native scroller.
   ========================================================================== */
class IntroTileGrid extends HTMLElement {
  // The words the intro tiles' heading clamps cut short (see fitTileHeading).
  static clamped = null;

  connectedCallback() {
    // Tabs and panels are queried fresh on demand (see tabs()/panels()) rather
    // than cached here: the editor can re-render this section IN PLACE —
    // morphing this element's subtree without reconnecting it — so a snapshot
    // taken now goes stale (and starts EMPTY when the section connects with a
    // single collection block and no tab rail, killing tab switching once a
    // second block is added). Click/keydown are DELEGATED from the host so tabs
    // added by a later re-render work without connectedCallback re-running
    // (#309, QA on #269).
    //
    // Which tab is active is owned by JS and seeded from the server-rendered
    // selection. The editor morphs this section's subtree IN PLACE, so this
    // instance (and this field) survive a re-render while the markup itself is
    // reset to Liquid's defaults each time — first tab selected, first panel
    // visible. Re-asserting THIS stored index after every morph is what keeps
    // the block you're editing showing on its own instead of stacked over the
    // reset first panel (#320, QA on #309).
    this.activeIndex = Math.max(
      0,
      this.tabs().findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-grid-tab]');
      if (tab) this.select(this.tabs().indexOf(tab));
    });
    this.addEventListener('keydown', (event) => {
      const tab = event.target.closest('[data-grid-tab]');
      if (tab) this.onTabKeydown(event, this.tabs().indexOf(tab));
    });
    // Remember which block the editor last selected so a following re-render
    // can't lose it (enforceSingle re-asserts this.activeIndex). Applied via
    // enforceSingle rather than select() so editing a block doesn't replay the
    // crossfade on every keystroke.
    this.onBlockSelect = (event) => {
      const index = this.tabs().indexOf(event.target);
      if (index > -1) {
        this.activeIndex = index;
        this.enforceSingle();
      }
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    // Enforce exactly one visible panel now, and again on every editor
    // re-render. That render can REUSE this element (no reconnect, so
    // connectedCallback never re-runs) and still leave a previously-shown panel
    // un-hidden — stacking both tabs' collections until save (#269, QA #231).
    // shopify:section:load doesn't reliably fire for every re-render, so ALSO
    // observe the subtree directly. Watch BOTH childList (a block add/remove or
    // a collection pick swaps panels/cards) AND the hidden/aria-selected
    // attributes: a re-render commonly resets the first panel to visible by
    // simply STRIPPING its `hidden` attribute, with no child mutation — a
    // childList-only observer never saw that, so the reset panel stayed stacked
    // under the one JS had selected (#320, QA on #309). enforceSingle only
    // writes attributes that actually change, so re-asserting from inside the
    // observer can't loop. Both paths are editor-only — the storefront never
    // fires section:load and this observer never starts there.
    this.enforceSingle();
    if (window.Shopify && window.Shopify.designMode) {
      this.onSectionLoad = (event) => {
        if (event.target.contains(this)) this.enforceSingle();
      };
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      this.tabObserver = new MutationObserver(() => {
        this.enforceSingle();
        this.watchTileHeadings();
      });
      this.tabObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'aria-selected'],
      });
    }

    // Tab rail: fade only the edge(s) that actually have tabs scrolled past,
    // never the resting first tab (#231). Overflow-driven so it self-manages
    // at any width — the mask stops at the ends.
    this.rail = this.querySelector('.intro-grid-tabs');
    if (this.rail) {
      const railFade = () => {
        const max = this.rail.scrollWidth - this.rail.clientWidth;
        const pos = Math.abs(this.rail.scrollLeft);
        this.rail.classList.toggle('is-overflow-start', pos > 1);
        this.rail.classList.toggle('is-overflow-end', max > 1 && pos < max - 1);
      };
      this.rail.addEventListener('scroll', railFade, { passive: true });
      this.railResize = new ResizeObserver(railFade);
      this.railResize.observe(this.rail);
    }

    // The intro tile's heading gets room under its last line for descenders,
    // which its three-line clamp otherwise cuts off (#702), once nothing past
    // the clamp can show in it (see fitTileHeading). Size-driven like the rail
    // fade, so it follows the tile's width. Its words can also rewrap while
    // its size stays put: when a webfont lands (Face Value loads a sturdier
    // cut on phones) or a script rewrites them (an editor morph, a page
    // translator), so check again then too.
    this.clampTails = new Map();
    this.headingFit = new ResizeObserver((entries) => {
      entries.forEach(({ target }) => this.fitTileHeading(target));
    });
    this.headingEdit = new MutationObserver(() => this.watchTileHeadings());
    this.watchTileHeadings();
    document.fonts?.forEach((face) => {
      if (face.status === 'loaded') return;
      face.loaded.then(() => {
        if (this.isConnected) this.watchTileHeadings();
      }, () => {});
    });

    // Carousel: progress rule + prev/next controls, revealed only when the row
    // overflows. Arrows page the native scroller by ~one viewport of cards, so
    // desktop gets a usable control and the native swipe still drives mobile.
    this.resizeObservers = [];
    this.panels().forEach((panel) => {
      const scroller = panel.querySelector('[data-grid-scroller]');
      const bar = panel.querySelector('[data-grid-progress]');
      if (!scroller || !bar) return;
      const nav = panel.querySelector('[data-grid-nav]');
      const prev = panel.querySelector('[data-grid-prev]');
      const next = panel.querySelector('[data-grid-next]');
      const dir = getComputedStyle(scroller).direction === 'rtl' ? -1 : 1;
      const page = () => Math.max(scroller.clientWidth * 0.85, 240);
      const update = () => {
        const max = scroller.scrollWidth - scroller.clientWidth;
        if (nav) nav.hidden = max < 1;
        if (max < 1) return;
        const pos = Math.abs(scroller.scrollLeft);
        bar.style.setProperty(
          '--grid-progress',
          String(Math.min(1, (pos + scroller.clientWidth) / scroller.scrollWidth))
        );
        if (prev) prev.disabled = pos <= 1;
        if (next) next.disabled = pos >= max - 1;
      };
      // dir points towards the row's end (flipped under RTL); prev is the
      // opposite way.
      const paceScroll = (toEnd) =>
        scroller.scrollBy({
          left: (toEnd ? dir : -dir) * page(),
          behavior: motionReduced() ? 'auto' : 'smooth',
        });
      prev?.addEventListener('click', () => paceScroll(false));
      next?.addEventListener('click', () => paceScroll(true));
      scroller.addEventListener('scroll', update, { passive: true });
      const resize = new ResizeObserver(update);
      resize.observe(scroller);
      this.resizeObservers.push(resize);

      // Make the progress rule a real scrollbar: click or drag along the track
      // to scrub the row. The pure-CSS scroll-snap still owns swipe, trackpad
      // and keyboard; this adds the one input the rule was missing — a mouse,
      // which can't pan a horizontal overflow, so without it the far slides
      // were unreachable on desktop. One pointer path covers mouse, touch, pen.
      const track = bar.parentElement;
      if (track) {
        let scrubbing = false;
        const seek = (clientX) => {
          const max = scroller.scrollWidth - scroller.clientWidth;
          const rect = track.getBoundingClientRect();
          if (max < 1 || rect.width <= 0) return;
          const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
          const rtl = getComputedStyle(scroller).direction === 'rtl';
          scroller.scrollLeft = (rtl ? fraction - 1 : fraction) * max;
        };
        track.addEventListener('pointerdown', (event) => {
          if (scroller.scrollWidth - scroller.clientWidth < 1) return;
          scrubbing = true;
          // Suspend snap so the row tracks the pointer; it re-snaps to the
          // nearest slide when the drag ends.
          scroller.style.scrollSnapType = 'none';
          track.setPointerCapture?.(event.pointerId);
          seek(event.clientX);
          event.preventDefault();
        });
        track.addEventListener('pointermove', (event) => {
          if (scrubbing) seek(event.clientX);
        });
        const endScrub = (event) => {
          if (!scrubbing) return;
          scrubbing = false;
          scroller.style.scrollSnapType = '';
          track.releasePointerCapture?.(event.pointerId);
        };
        track.addEventListener('pointerup', endScrub);
        track.addEventListener('pointercancel', endScrub);
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    if (this.onSectionLoad) document.removeEventListener('shopify:section:load', this.onSectionLoad);
    this.tabObserver?.disconnect();
    this.resizeObservers?.forEach((observer) => observer.disconnect());
    this.railResize?.disconnect();
    this.headingFit?.disconnect();
    this.headingEdit?.disconnect();
    this.clampTails?.forEach((tail) => tail.forEach((range) => IntroTileGrid.clamped?.delete(range)));
  }

  // Observe every intro-tile heading and check each now: an editor morph can
  // swap a heading for a new one, or change what fits without resizing it.
  watchTileHeadings() {
    if (!this.headingFit) return;
    const headings = [...this.querySelectorAll('.intro-grid__tile .block-heading')];
    // A heading a morph replaced takes its clamped words with it.
    this.clampTails.forEach((tail, heading) => {
      if (headings.includes(heading)) return;
      tail.forEach((range) => IntroTileGrid.clamped?.delete(range));
      this.clampTails.delete(heading);
    });
    headings.forEach((heading) => {
      this.headingFit.observe(heading);
      this.headingEdit.observe(heading, { childList: true, characterData: true, subtree: true });
      this.fitTileHeading(heading);
    });
  }

  // Room under the 3rd line (is-clear) once nothing past the clamp can show
  // in it. The words the clamp cuts are still laid out right under that line,
  // and at these tight line-heights their tops reach higher than its
  // descenders reach down, so they're painted transparent: a custom highlight
  // (::highlight(intro-tile-clamped) in capital.css), which leaves the DOM the
  // editor morphs, and what a screen reader hears, alone. A marked word among
  // them hides its mark too, as a circle's ring rises into the 3rd line.
  // Without highlights, only a heading that fits gets the room. Only toggles
  // classes, which neither observer watches, so it can't loop.
  fitTileHeading(heading) {
    const highlight = IntroTileGrid.clampHighlight();
    this.clampTails.get(heading)?.forEach((range) => highlight?.delete(range));
    const tail = this.clampedWords(heading);
    tail.forEach((range) => highlight?.add(range));
    this.clampTails.set(heading, tail);
    heading.querySelectorAll('.accent-word').forEach((word) => {
      word.classList.toggle('is-clamped', tail.some((range) => word.contains(range.startContainer)));
    });
    heading.classList.toggle('is-clear', !tail.length || Boolean(highlight));
  }

  // The words the clamp cut short: one range per text node, from the first
  // character laid out below the heading's 3rd line to the end, or none when
  // it all fits. The clamp only clips, so every line is still laid out, and a
  // character whose middle sits past the heading's content edge is on the 4th
  // line or later. Each range stays inside its text node: Safari won't paint
  // a static range that ends on an element.
  clampedWords(heading) {
    const edge = heading.getBoundingClientRect().bottom - parseFloat(getComputedStyle(heading).paddingBottom);
    const probe = document.createRange();
    const clamped = (node, offset) => {
      probe.setStart(node, offset);
      probe.setEnd(node, offset + 1);
      const { top, bottom, height } = probe.getBoundingClientRect();
      return height > 0 && (top + bottom) / 2 > edge;
    };
    const tail = [];
    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      // Look for the first clamped character; every node after it is clamped whole.
      let start = 0;
      while (!tail.length && start < node.length && !clamped(node, start)) start += 1;
      if (start < node.length) {
        tail.push(new StaticRange({ startContainer: node, startOffset: start, endContainer: node, endOffset: node.length }));
      }
    }
    return tail;
  }

  // One highlight holds the clamped words of every intro tile on the page;
  // null in a browser without custom highlights.
  static clampHighlight() {
    if (!window.Highlight || !window.CSS?.highlights) return null;
    if (!IntroTileGrid.clamped) {
      IntroTileGrid.clamped = new window.Highlight();
      CSS.highlights.set('intro-tile-clamped', IntroTileGrid.clamped);
    }
    return IntroTileGrid.clamped;
  }

  // Tabs and panels, queried fresh every call. The editor can morph this
  // element's subtree in place (block add/remove, collection pick, settings
  // edit) without reconnecting it, so a cached snapshot goes stale — and one
  // taken with a single block, before any tab rail exists, would stay empty
  // for the element's lifetime (#309).
  tabs() {
    return [...this.querySelectorAll('[data-grid-tab]')];
  }

  panels() {
    return [...this.querySelectorAll('[data-grid-panel]')];
  }

  // Show exactly the panel whose tab is active (this.activeIndex, owned by JS)
  // and hide every other, keeping the tab rail's aria-selected/tabindex in
  // sync. Re-queries the DOM each call so it stays correct after an in-place
  // editor morph, and drives visibility from the STORED index rather than the
  // DOM's aria-selected — a morph resets aria-selected to the first tab, which
  // would otherwise snap the view off the block being edited or leave both
  // panels stacked (#269, #320). Only writes attributes that actually change,
  // so it is a no-op on the storefront and can't feed back into the
  // MutationObserver that calls it.
  enforceSingle(animate = false) {
    const panels = this.panels();
    if (!panels.length) return;
    const active = Math.min(Math.max(this.activeIndex || 0, 0), panels.length - 1);
    this.tabs().forEach((tab, i) => {
      const selected = String(i === active);
      if (tab.getAttribute('aria-selected') !== selected) tab.setAttribute('aria-selected', selected);
      const tabindex = i === active ? 0 : -1;
      if (tab.tabIndex !== tabindex) tab.tabIndex = tabindex;
    });
    panels.forEach((panel, i) => {
      const hide = i !== active;
      if (panel.hidden !== hide) panel.hidden = hide;
      if (!animate) return;
      panel.classList.remove('is-entering');
      if (!hide && !motionReduced()) {
        void panel.offsetWidth;
        panel.classList.add('is-entering');
      }
    });
  }

  select(index) {
    this.activeIndex = index;
    this.enforceSingle(true);
  }

  onTabKeydown(event, index) {
    const tabs = this.tabs();
    const last = tabs.length - 1;
    const step = { ArrowRight: 1, ArrowLeft: -1, Home: -index, End: last - index }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const next = (index + step + tabs.length) % tabs.length;
    tabs[next].focus();
    this.select(next);
  }
}

define('intro-tile-grid', IntroTileGrid);
/* == WAVE 2 ADDITION — product-grid (The Intro-Tile Grid) — END =========== */

/* ============ Wave 4: announcement-bar ============ */
/* ==========================================================================
   WAVE 4 FRAGMENT — <announcement-bar> (sections/announcement-bar.liquid).
   Rotation on the semantic pace vocabulary (calm 7s / brisk 5s — the hero's
   intervals), session dismissal, and localization-form auto-submit.
   Rotation holds on hover/focus/hidden tab; content rotation continues
   under reduced motion (the CSS crossfade collapses to 1ms).
   ========================================================================== */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    const key = `capital:announcement-dismissed:${this.dataset.sectionId}`;
    if (this.hasAttribute('data-dismissible')) {
      try {
        if (window.sessionStorage.getItem(key)) {
          this.hidden = true;
          return;
        }
      } catch { /* storage unavailable: never dismiss */ }
      this.querySelector('[data-announcement-dismiss]')?.addEventListener('click', () => {
        try {
          window.sessionStorage.setItem(key, '1');
        } catch { /* storage unavailable */ }
        this.hidden = true;
      });
    }

    /* Locale selectors are custom <details> dropdowns (mirroring the header
       popover): only one open at a time, and outside-click / Escape close them.
       Each option is a real submit button, so no JS is needed to switch. */
    this.dropdowns = [...this.querySelectorAll('[data-locale-dropdown]')];
    if (this.dropdowns.length) {
      this.dropdowns.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          this.dropdowns.forEach((other) => {
            if (other !== details) other.open = false;
          });
        });
      });
      this.onLocaleDocClick = (event) => {
        this.dropdowns.forEach((details) => {
          if (details.open && !details.contains(event.target)) details.open = false;
        });
      };
      this.onLocaleKeydown = (event) => {
        if (event.key !== 'Escape') return;
        this.dropdowns.forEach((details) => {
          if (!details.open) return;
          details.open = false;
          details.querySelector('summary')?.focus();
        });
      };
      document.addEventListener('click', this.onLocaleDocClick);
      document.addEventListener('keydown', this.onLocaleKeydown);
    }

    this.items = [...this.querySelectorAll('[data-announcement]')];
    this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
    this.holds = new Set();

    /* Theme editor: selecting an announcement block centres it AND holds the
       rotation, so the merchant can edit the block they picked without it
       fading away; deselecting releases the hold and rotation resumes. Both
       handlers match only this section's own blocks, and deselect guards on the
       tracked block so switching block A -> B stays held whatever order the
       editor fires select/deselect in. (Mirrors <product-showcase>, #246.) */
    const ownItem = (target) => this.items.findIndex((item) => item === target || item.contains(target));
    this.onBlockSelect = (event) => {
      const i = ownItem(event.target);
      if (i < 0) return;
      this.editorItem = i;
      this.holds.add('editor');
      this.show(i);
    };
    this.onBlockDeselect = (event) => {
      if (ownItem(event.target) === this.editorItem) this.holds.delete('editor');
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    const interval = { calm: 7000, brisk: 5000 }[this.dataset.pace];
    if (!interval || this.items.length < 2) return;

    /* Reduced motion (system or the theme escape hatch): the bar shows the
       first announcement and never rotates, so no pause control is needed
       and the toggle stays hidden. */
    if (motionReduced()) return;

    const hold = (name, on) => {
      if (on) this.holds.add(name);
      else this.holds.delete(name);
    };
    this.addEventListener('pointerenter', () => hold('pointer', true));
    this.addEventListener('pointerleave', () => hold('pointer', false));
    this.addEventListener('focusin', () => hold('focus', true));
    this.addEventListener('focusout', () => hold('focus', false));
    this.timer = window.setInterval(() => {
      if (this.holds.size > 0 || document.hidden) return;
      this.show((this.index + 1) % this.items.length);
    }, interval);

    /* Persistent, keyboard-reachable pause/play (WCAG 2.2.2): hold('user')
       survives pointerleave, unlike the hover/focus courtesy holds. */
    bindMotionToggle(this.querySelector('[data-motion-toggle]'), (paused) => {
      hold('user', paused);
      this.classList.toggle('is-user-paused', paused);
    });
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    if (this.onLocaleDocClick) document.removeEventListener('click', this.onLocaleDocClick);
    if (this.onLocaleKeydown) document.removeEventListener('keydown', this.onLocaleKeydown);
  }

  show(next) {
    this.index = next;
    this.items.forEach((item, i) => {
      item.classList.toggle('is-active', i === next);
      item.setAttribute('aria-hidden', i === next ? 'false' : 'true');
    });
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}

/* ============ Wave 4: faq ============ */
/* ==========================================================================
   WAVE 4 ADDITION — <faq-accordion> (sections/faq.liquid).
   Theme-editor affordance only: selecting a Question block in the editor
   opens its <details> so the answer is visible while it is edited, and
   deselecting closes it again. These events fire only in the editor; on the
   storefront the native <details>/summary owns open/close, so this adds no
   storefront behaviour. Scoped by this.contains — each instance ignores
   blocks belonging to other FAQ sections.
   ========================================================================== */
class FaqAccordion extends HTMLElement {
  connectedCallback() {
    this.onBlockSelect = (event) => this.setItem(event.target, true);
    this.onBlockDeselect = (event) => this.setItem(event.target, false);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  setItem(target, open) {
    const item = target?.closest?.('.faq__item');
    if (item && this.contains(item)) item.open = open;
  }
}

if (!customElements.get('faq-accordion')) {
  customElements.define('faq-accordion', FaqAccordion);
}

/* ============ Wave 4: offers-drawer ============ */
/* ==========================================================================
   WAVE 4 FRAGMENT — <offers-drawer> (sections/offers-drawer.liquid).
   Cart-drawer mechanics (open/close/inert/esc, opener hook
   [data-offers-drawer-open] — the sticky trigger carries it, but any
   element on the page can) plus copy-to-clipboard for discount codes.
   ========================================================================== */
class OffersDrawer extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-drawer-root]');
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
      const copy = event.target.closest('[data-copy-code]');
      if (copy) this.copy(copy);
    });
    this.onDocClick = (event) => {
      const opener = event.target.closest('[data-offers-drawer-open]');
      if (!opener) return;
      event.preventDefault();
      this.open(opener);
    };
    this.onDocKeydown = (event) => {
      if (!this.isOpen()) return;
      if (event.key === 'Escape') this.close();
      else if (event.key === 'Tab') trapDrawerFocus(this.root, event);
    };
    this.onSectionSelect = (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.open();
    };
    this.onSectionDeselect = (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.close();
    };
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onDocKeydown);
    document.addEventListener('shopify:section:select', this.onSectionSelect);
    document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onDocKeydown);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
  }

  isOpen() {
    return this.root?.classList.contains('is-open');
  }

  open(opener) {
    if (!this.root) return;
    // An opener inside a menu that has just closed for us (the header drawer's
    // Offers row, #657) is inert by now; focus comes back to wherever that
    // menu returned it instead.
    this.lastFocus = opener && !opener.closest('[inert]') ? opener : document.activeElement;
    this.root.classList.add('is-open');
    this.root.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    // Focus the visible close button, not the first [data-drawer-close] (the
    // invisible overlay button sits first in the DOM) — otherwise the drawer
    // opens with focus on an unseen control and the focus ring vanishes.
    this.root.querySelector('.offers-drawer__close')?.focus();
  }

  close() {
    if (!this.root) return;
    this.root.classList.remove('is-open');
    this.root.setAttribute('inert', '');
    document.body.style.overflow = '';
    this.lastFocus?.focus();
  }

  async copy(button) {
    try {
      await navigator.clipboard.writeText(button.dataset.copyCode);
    } catch {
      return; /* clipboard unavailable: the code stays selectable text */
    }
    const label = button.querySelector('[data-copy-label]');
    if (!label || button.dataset.copyResting) return;
    button.dataset.copyResting = label.textContent;
    label.textContent = button.dataset.copiedLabel || 'Copied';
    window.setTimeout(() => {
      label.textContent = button.dataset.copyResting;
      delete button.dataset.copyResting;
    }, 2000);
  }
}

if (!customElements.get('offers-drawer')) {
  customElements.define('offers-drawer', OffersDrawer);
}

/* ============ Wave 4: popup ============ */
/* == WAVE 4 ADDITION — popup (sections/popup.liquid) — START ===============
   <popup-dialog>: native <dialog> modal (browser-owned focus trap, Esc,
   implicit aria-modal), semantic triggers (arrive/settle/exit intent),
   localStorage frequency, design-mode gated. Concatenate into capital.js.

   data-posture="corner" (#655) opens the same card with dialog.show()
   instead: non-modal, so no backdrop, no inert page and no focus trap. The
   browser's show() still pulls focus into the card, which would yank a
   shopper out of whatever they were typing, so focus is handed straight
   back; Esc closes it from the page or the card, never from inside another
   overlay that owns Esc itself. */
class PopupDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    this.key = `capital:popup:${this.dataset.sectionId}`;
    this.editor = Boolean(window.Shopify && window.Shopify.designMode);
    this.corner = this.dataset.posture === 'corner';
    this.onCornerKeydown = (event) => {
      if (event.key !== 'Escape' || !this.dialog.open) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target && !this.dialog.contains(target) && target.closest('[data-drawer-root], dialog')) return;
      this.dismiss();
    };
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dismiss();
    });
    this.dialog.addEventListener('cancel', () => this.remember());
    this.querySelectorAll('[data-popup-close]').forEach((button) =>
      button.addEventListener('click', () => this.dismiss())
    );
    if (this.editor) {
      // Store the editor listeners so disconnectedCallback can remove them —
      // the Theme Editor re-renders this section repeatedly. Match on
      // event.detail.sectionId (mirrors the cart/search/offers drawers in this
      // same overlay group) rather than event.target containment: for
      // section-group members event.target isn't a reliable ancestor of this
      // node, so containment never matched and the popup never opened on select.
      this.editorListeners = [];
      const isThisSection = (event) => event.detail?.sectionId === this.dataset.sectionId;
      const onEditorOpen = (event) => {
        if (isThisSection(event)) this.open();
      };
      ['shopify:section:select', 'shopify:section:load'].forEach((type) => {
        document.addEventListener(type, onEditorOpen);
        this.editorListeners.push([type, onEditorOpen]);
      });
      const onEditorDeselect = (event) => {
        if (isThisSection(event) && this.dialog.open) this.dialog.close();
      };
      document.addEventListener('shopify:section:deselect', onEditorDeselect);
      this.editorListeners.push(['shopify:section:deselect', onEditorDeselect]);
    } else if (this.querySelector('[data-popup-success]')) {
      // Reopen once after the subscribe reload to stage success, then never again.
      this.store('subscribed');
      this.open();
    } else if (this.due()) {
      this.arm();
    }
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    if (this.onExit) document.documentElement.removeEventListener('mouseleave', this.onExit);
    this.editorListeners?.forEach(([type, handler]) => document.removeEventListener(type, handler));
    document.removeEventListener('keydown', this.onCornerKeydown);
    if (this.onGateConfirmed) document.removeEventListener('age-gate:confirmed', this.onGateConfirmed);
  }

  arm() {
    if (this.dataset.trigger === 'exit' && window.matchMedia('(hover: hover)').matches) {
      this.onExit = (event) => {
        if (event.clientY <= 0) this.open();
      };
      document.documentElement.addEventListener('mouseleave', this.onExit);
      return;
    }
    // Touch devices fall back from exit intent to the settled delay.
    this.timer = setTimeout(() => this.open(), this.dataset.trigger === 'arrive' ? 2500 : 9000);
  }

  due() {
    const raw = this.read();
    if (!raw) return true;
    if (raw === 'subscribed') return false;
    const days = { day: 1, week: 7, month: 30, once: 3650 }[this.dataset.frequency] || 7;
    return Date.now() - Number(raw) > days * 864e5;
  }

  open() {
    if (!this.isConnected) return; // stale instance after an editor re-render
    clearTimeout(this.timer);
    if (this.onExit) document.documentElement.removeEventListener('mouseleave', this.onExit);
    // The age gate (#654) goes first. Due while the gate is still up, the
    // popup waits for the visitor to confirm, then runs its own trigger from
    // the start, so the two never arrive back to back. A visitor who declines
    // never gets past the gate, so never sees the popup either.
    const gateUp =
      document.documentElement.classList.contains('age-gate-pending') ||
      document.querySelector('age-gate dialog[open]');
    if (!this.editor && gateUp) {
      if (!this.onGateConfirmed) {
        this.onGateConfirmed = () => {
          this.onGateConfirmed = null;
          this.arm();
        };
        document.addEventListener('age-gate:confirmed', this.onGateConfirmed, { once: true });
      }
      return;
    }
    if (this.dialog.open) return;
    if (!this.corner) {
      this.dialog.showModal();
      return;
    }
    const previous = document.activeElement;
    this.lastFocus = previous;
    this.dialog.show();
    if (previous && previous !== document.body && previous.isConnected) {
      previous.focus({ preventScroll: true });
    } else if (this.dialog.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    document.addEventListener('keydown', this.onCornerKeydown);
  }

  dismiss() {
    this.remember();
    // Closing a corner card that held focus would drop it on <body>: put it
    // back where the shopper was before the card arrived.
    const hadFocus = this.corner && this.dialog.contains(document.activeElement);
    this.dialog.close();
    document.removeEventListener('keydown', this.onCornerKeydown);
    if (hadFocus && this.lastFocus?.isConnected && this.lastFocus !== document.body) {
      this.lastFocus.focus({ preventScroll: true });
    }
  }

  remember() {
    // A subscribed / permanently-dismissed state must survive the ordinary
    // close: never clobber it with a close-frequency timestamp, which would let
    // the popup reappear after the frequency window despite the opt-out.
    if (this.editor || this.read() === 'subscribed') return;
    this.store(String(Date.now()));
  }

  read() {
    try { return window.localStorage.getItem(this.key); } catch (error) { return null; }
  }

  store(value) {
    try { window.localStorage.setItem(this.key, value); } catch (error) { /* private mode */ }
  }
}

if (!customElements.get('popup-dialog')) customElements.define('popup-dialog', PopupDialog);
/* == WAVE 4 ADDITION — popup — END ======================================== */

/* ============ Wave 4: product-recommendations ============ */
/* ==========================================================================
   WAVE 4 FRAGMENT — <product-recommendations>
   (sections/product-recommendations.liquid). Concatenate into capital.js.
   Fetches Shopify's recommendations endpoint section-rendered, swaps the
   returned [data-recommendations] fragment in, and unhides the section
   (frame class + .is-populated) only when products come back.

   The cart's suggestions (snippets/cart-upsell.liquid) use the same element
   with three additions, each opted into by an attribute:
   - no data-product-id: a plain section render of data-url (the cart's
     collection), so only section_id goes with it;
   - data-defer: wait until the element is on screen. The cart drawer sits
     closed, off the side of every page, and should cost nothing until it is
     opened and scrolled to. Below-the-viewport only — a margin to the side
     would reach into the closed drawer;
   - data-cart-key: a digest of the products in the cart. The answer leaves
     those products out, so it is sent with the request to make each set of
     products its own URL, and it keys an in-memory copy of the request. Every
     cart change re-renders the surface and with it this element; a change
     that kept the same products (a quantity) paints the answer it already
     has — within the same frame, so the list never blanks — and one still in
     flight is shared rather than asked for twice.
   ========================================================================== */
const recommendationsCache = new Map();

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    const params = new URLSearchParams({ section_id: this.dataset.sectionId });
    if (this.dataset.productId) {
      params.set('product_id', this.dataset.productId);
      params.set('limit', this.dataset.limit || '4');
      params.set('intent', this.dataset.intent || 'related');
    }
    if (this.dataset.cartKey) params.set('cart', this.dataset.cartKey);
    this.request = `${this.dataset.url}?${params}`;

    if (!this.hasAttribute('data-defer') || recommendationsCache.has(this.request)) {
      this.load();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 200px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  load() {
    const cached = Boolean(this.dataset.cartKey);
    let answer = cached ? recommendationsCache.get(this.request) : null;
    if (!answer) {
      answer = fetch(this.request)
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
        .then((html) => {
          const fresh = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-recommendations]');
          return fresh?.querySelector('.product-card') ? fresh : null;
        });
      if (cached) {
        recommendationsCache.set(this.request, answer);
        // A failed request is no answer: the next render asks again.
        answer.catch(() => recommendationsCache.delete(this.request));
      }
    }
    answer
      .then((fragment) => this.paint(fragment))
      .catch(() => {
        /* network hiccup — the section simply stays hidden */
      });
  }

  paint(fragment) {
    this.dataset.loaded = 'true';
    if (!fragment) return;
    // A copy: a cached answer has to stay whole for the next render.
    this.replaceChildren(document.importNode(fragment, true));
    // .pdp-complementary: the product page's Complementary products block
    // (snippets/product-complementary.liquid, #646), the same gate.
    this.closest('.recommendations, .cart-upsell, .pdp-complementary')?.classList.add('is-populated');
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

/* ============ Wave 4: product-showcase ============ */
/* ==========================================================================
   WAVE 4 ADDITION — <product-showcase> (sections/product-showcase.liquid).
   Scroll-snap scrolls; this element tracks the centre slide, syncs the
   stacked text panels (hero-style reveal re-run + inert), drives progress
   rule + counter, makes peeked neighbours click-to-centre, runs optional
   autoplay with holds. Uses the module's `define` / `motionReduced`.
   ========================================================================== */
class ProductShowcase extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-showcase-rail]');
    this.slides = [...this.querySelectorAll('[data-showcase-slide]')];
    this.panels = [...this.querySelectorAll('[data-showcase-panel]')];
    this.counter = this.querySelector('[data-showcase-current]');
    this.bar = this.querySelector('[data-showcase-progress]');
    this.index = 0;
    this.holds = new Set();
    if (!this.rail || this.slides.length < 2) return;
    this.slides.forEach((slide, i) => slide.addEventListener('click', (event) => {
      if (i === this.index) return;
      event.preventDefault();
      this.goTo(i);
    }));
    [['prev', -1], ['next', 1]].forEach(([key, step]) =>
      this.querySelector(`[data-showcase-${key}]`)?.addEventListener('click', () => this.goTo(this.index + step))
    );
    this.rail.addEventListener('scroll', () => {
      window.clearTimeout(this.settle);
      this.settle = window.setTimeout(() => this.mark(this.nearest()), 90);
    }, { passive: true });
    /* Theme editor: selecting a product block centres it AND holds the
       rotation, so the merchant can edit the slide they picked without it
       advancing away; deselecting releases the hold and rotation resumes.
       Both handlers match only this section's own slides (a second showcase
       on the page keeps rotating), and deselect guards on the tracked slide
       so switching block A -> B stays held whatever order the editor fires
       select/deselect in. */
    const ownSlide = (target) => this.slides.findIndex((slide) => slide === target || slide.contains(target));
    this.onBlockSelect = (event) => {
      const i = ownSlide(event.target);
      if (i < 0) return;
      this.editorSlide = i;
      this.holds.add('editor');
      this.goTo(i);
    };
    this.onBlockDeselect = (event) => {
      if (ownSlide(event.target) === this.editorSlide) this.holds.delete('editor');
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    const interval = { calm: 7000, brisk: 4500 }[this.dataset.autoplay];
    if (!interval) return;

    /* Reduced motion: no autoplay. Manual controls (arrows, click-to-centre,
       scroll) keep working; with nothing rotating the persistent toggle stays
       hidden. */
    if (motionReduced()) return;

    const hold = (key, on) => (on ? this.holds.add(key) : this.holds.delete(key));
    /* Courtesy holds: focusing the showcase, or resting the pointer on the
       product on show or an arrow (pointing()), stills the rotation so a
       shopper can read or reach a product. The pause/play toggle is the
       exception — it sits inside the showcase, and the hover and focus it
       takes to press Play must not hold the rotation Play just released
       (#618). */
    const courtesy = (event) => !event.target.closest('[data-motion-toggle]');
    const track = (event) => { this.pointerAt = { x: event.clientX, y: event.clientY }; };
    this.addEventListener('pointerover', track);
    this.addEventListener('pointermove', track);
    this.addEventListener('pointerleave', () => { this.pointerAt = null; });
    this.addEventListener('focusin', (event) => hold('focus', courtesy(event)));
    this.addEventListener('focusout', () => hold('focus', false));
    this.onVisibility = () => hold('hidden', document.hidden);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.timer = window.setInterval(() => { if (!this.holds.size && !this.pointing()) this.goTo(this.index + 1); }, interval);

    /* Persistent, keyboard-reachable pause/play (WCAG 2.2.2). hold('user')
       persists past pointerleave; the same class also stills the decorative
       background marquee drift (assets/capital.css). */
    bindMotionToggle(this.querySelector('[data-motion-toggle]'), (paused) => {
      hold('user', paused);
      this.classList.toggle('is-user-paused', paused);
    });
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /* The hover hold: is the pointer on the product on show (its image, or its
     name, price and buttons) or on an arrow? Only those — not the whole
     showcase, which on desktop fills most of the screen, so a hold armed
     anywhere on it kept the rotation off for any cursor resting there
     (#618). Hit-tested at each tick against the current slide, because the
     rail and panels move under a resting pointer without every browser
     firing a pointer event. */
  pointing() {
    const target = this.pointerAt && document.elementFromPoint(this.pointerAt.x, this.pointerAt.y);
    if (!target) return false;
    const stack = this.panels[this.index]?.querySelector('.product-showcase__panel-stack');
    return [
      this.slides[this.index].querySelector('.product-showcase__media'),
      ...(stack ? stack.children : []),
      ...this.querySelectorAll('[data-showcase-prev], [data-showcase-next]'),
    ].some((zone) => zone?.contains(target));
  }

  nearest() {
    const centre = Math.abs(this.rail.scrollLeft) + this.rail.clientWidth / 2;
    const gaps = this.slides.map((slide) => Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - centre));
    return gaps.indexOf(Math.min(...gaps));
  }

  goTo(next) {
    const total = this.slides.length;
    const index = ((next % total) + total) % total;
    // Mark first so the zoom/fade begins on click, in sync with the scroll
    // (was: marked only after the scroll settled — the "scroll then pop").
    this.mark(index);
    const slide = this.slides[index];
    const left = slide.offsetLeft + slide.offsetWidth / 2 - this.rail.clientWidth / 2;
    this.rail.scrollTo({ left, behavior: motionReduced() ? 'auto' : 'smooth' });
  }

  mark(index) {
    if (index === this.index) return;
    this.index = index;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    this.panels.forEach((panel, i) => {
      panel.classList.toggle('is-active', i === index);
      panel.toggleAttribute('inert', i !== index);
      panel.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });
    if (this.counter) this.counter.textContent = String(index + 1).padStart(2, '0');
    this.bar?.style.setProperty('--showcase-progress', String((index + 1) / this.slides.length));
    replayReveal(this.panels[index]?.querySelector('reveal-on-scroll'));
  }
}
define('product-showcase', ProductShowcase);
/* ================= END WAVE 4 ADDITION — <product-showcase> ============== */

/* ============ Wave 4: recently-viewed ============ */
/* ==========================================================================
   WAVE 4 FRAGMENT — recently-viewed (sections/recently-viewed.liquid).
   Concatenate into capital.js. Records the visited product handle from the
   URL on every PDP load (section placement not required), then renders the
   placeable section via the Section Rendering API on the search route with
   a handle-scoped OR query; the Liquid re-orders results to recency.
   ========================================================================== */
const RECENTLY_VIEWED_KEY = 'capital:recently-viewed';

const productHandleFromPath = () => {
  const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const readRecentlyViewed = () => {
  try {
    const list = JSON.parse(window.localStorage.getItem(RECENTLY_VIEWED_KEY));
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

(() => {
  const handle = productHandleFromPath();
  if (!handle) return;
  try {
    const list = [handle, ...readRecentlyViewed().filter((h) => h !== handle)];
    window.localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(list.slice(0, 12)));
  } catch {
    /* storage unavailable — recently viewed simply stays empty */
  }
})();

class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    const current = productHandleFromPath();
    const handles = readRecentlyViewed()
      .filter((handle) => handle !== current)
      .slice(0, Number(this.dataset.limit) || 4);
    if (handles.length === 0) return;
    const query = handles.map((handle) => `handle:${handle}`).join(' OR ');
    const params = new URLSearchParams({ section_id: this.dataset.sectionId, type: 'product', q: query });
    fetch(`${this.dataset.searchUrl}?${params}`)
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then((html) => {
        this.dataset.loaded = 'true';
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recently-viewed]');
        if (!fresh || !fresh.querySelector('.product-card')) return;
        this.replaceChildren(fresh);
        this.closest('.recently-viewed')?.classList.add('is-populated');
      })
      .catch(() => {});
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}

/* ============ Wave 4: search-drawer ============ */
/* WAVE 4 FRAGMENT — <search-drawer> (sections/search-drawer.liquid).
   Cart-drawer mechanics (open/close/inert/esc, opener [data-search-drawer-open])
   + debounced predictive search: fetches routes.predictive_search_url with
   section_id so the section's own Liquid renders the results, then swaps
   [data-search-results]; stale responses abort. No-JS: plain GET /search form. */
/* Persist the typed term per-tab so it survives close/reopen and page-to-page
   navigation, until the shopper clears it (sessionStorage: gone when the tab
   closes). One drawer per document, so a single global key is enough. */
const SEARCH_TERM_KEY = 'capital:search-term';
class SearchDrawer extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-drawer-root]');
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.live = this.querySelector('[data-search-live]');
    this.clearBtn = this.querySelector('[data-search-clear]');
    // Predictive search adds suggested search terms (the `query` resource) on
    // top of the results. The product/collection/page/article results always
    // fetch and render, whether predictive is on or off; predictive only
    // decides whether suggested terms come with them. data-resources is the
    // comma list of result types the merchant left on.
    this.predictive = this.dataset.predictive !== 'false';
    this.resourceTypes = this.dataset.resources || '';
    // Restore a term carried over from a previous page or a prior open, so it
    // stays put until the shopper clears it.
    try {
      const saved = window.sessionStorage.getItem(SEARCH_TERM_KEY);
      if (saved && this.input) this.input.value = saved;
    } catch (error) { /* storage blocked (private mode) — non-fatal */ }
    this.toggleClear();
    this.clearBtn?.addEventListener('click', () => this.clear());
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
    });
    // Arrow keys rove focus through the result links (Enter opens the focused
    // one natively); ArrowUp from the first result returns to the field.
    this.addEventListener('keydown', (event) => this.onArrowKeys(event));
    this.onDocClick = (event) => {
      const opener = event.target.closest('[data-search-drawer-open]');
      if (!opener) return;
      event.preventDefault();
      this.open(opener);
    };
    this.onDocKeydown = (event) => {
      if (!this.isOpen()) return;
      if (event.key === 'Escape') this.close();
      else if (event.key === 'Tab') trapDrawerFocus(this.root, event);
    };
    this.onSectionSelect = (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.open();
    };
    this.onSectionDeselect = (event) => {
      if (event.detail?.sectionId === this.dataset.sectionId) this.close();
    };
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onDocKeydown);
    this.input?.addEventListener('input', () => {
      this.persistTerm();
      this.toggleClear();
      window.clearTimeout(this.debounce);
      this.debounce = window.setTimeout(() => this.search(), 300);
    });
    document.addEventListener('shopify:section:select', this.onSectionSelect);
    document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onDocKeydown);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    window.clearTimeout(this.debounce);
    window.clearTimeout(this.announceTimer);
    this.controller?.abort();
  }

  isOpen() { return this.root?.classList.contains('is-open'); }

  open(opener) {
    if (!this.root) return;
    this.lastFocus = opener || document.activeElement;
    this.root.classList.add('is-open');
    this.root.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    // Focus after the panel is actually visible: the drawer flips from
    // visibility:hidden to visible on .is-open, and a synchronous focus() runs
    // before that style lands, so the field never takes it. Caret to the end
    // so a restored term is ready to extend.
    requestAnimationFrame(() => {
      if (!this.input) return;
      this.input.focus();
      const end = this.input.value.length;
      this.input.setSelectionRange(end, end);
    });
    // A term carried in from another page has no results rendered yet — fetch
    // them so reopening shows the results, not just the term.
    const term = (this.input?.value ?? '').trim();
    if (term.length >= 2 && this.results && this.results.children.length === 0) this.search();
  }

  close() {
    if (!this.root) return;
    this.root.classList.remove('is-open');
    this.root.setAttribute('inert', '');
    document.body.style.overflow = '';
    this.lastFocus?.focus();
  }

  // Result types to request: the merchant's enabled types, plus `query` for
  // suggested search terms when predictive search is on. Empty means there is
  // nothing to fetch and the drawer stays a plain search form.
  searchTypes() {
    const types = [];
    if (this.resourceTypes) types.push(this.resourceTypes);
    if (this.predictive) types.push('query');
    return types.join(',');
  }

  async search() {
    const types = this.searchTypes();
    if (!types) return;
    const query = (this.input?.value ?? '').trim();
    this.controller?.abort();
    if (query.length < 2) {
      if (this.results) this.results.innerHTML = '';
      this.announce('');
      return;
    }
    this.controller = new AbortController();
    this.classList.add('is-searching');
    const params = new URLSearchParams({
      q: query,
      section_id: this.dataset.sectionId,
      'resources[type]': types,
      'resources[limit]': '4',
      'resources[limit_scope]': 'each' });
    try {
      const response = await fetch(`${this.dataset.url}?${params}`, { signal: this.controller.signal });
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-search-results]');
      if (fresh && this.results) this.results.replaceChildren(...fresh.childNodes);
      this.classList.remove('is-searching');
      // Announce the server-rendered count/no-results line to the persistent
      // live region so a screen reader hears that results arrived.
      this.announce(this.results?.querySelector('[data-search-announce]')?.textContent);
    } catch (error) {
      if (error.name !== 'AbortError') this.classList.remove('is-searching');
    }
  }

  /* The result links, in DOM order, that Tab and the arrow keys move through,
     then the before-typing suggestion links (#662) while they show. The
     product card's aria-hidden media link (tabindex="-1") is skipped, and so
     is anything CSS has hidden (no client rects). */
  navItems() {
    return [this.results, this.querySelector('[data-search-empty]')]
      .flatMap((region) => (region ? [...region.querySelectorAll('a[href]')] : []))
      .filter((el) => el.tabIndex !== -1 && el.getClientRects().length > 0);
  }

  onArrowKeys(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const active = document.activeElement;
    const items = this.navItems();
    const index = items.indexOf(active);
    // Only act from the search field or a result link — leave the buttons alone.
    if (active !== this.input && index === -1) return;
    if (!items.length) return;
    event.preventDefault();
    let next;
    if (event.key === 'ArrowDown') {
      next = active === this.input ? items[0] : items[index + 1] || this.input;
    } else {
      next = active === this.input ? items[items.length - 1] : index <= 0 ? this.input : items[index - 1];
    }
    next?.focus();
  }

  /* Clear-then-set (mirrors announceCartToScreenReader) so an identical
     message — e.g. two searches that both find 3 results — is re-announced. */
  announce(message) {
    if (!this.live) return;
    window.clearTimeout(this.announceTimer);
    this.live.textContent = '';
    this.announceTimer = window.setTimeout(() => {
      this.live.textContent = (message ?? '').trim();
    }, 100);
  }

  // Empty the term and its results in one action, then leave focus in the
  // field so the shopper can type again straight away.
  clear() {
    window.clearTimeout(this.debounce);
    this.controller?.abort();
    this.classList.remove('is-searching');
    if (this.input) this.input.value = '';
    this.persistTerm();
    if (this.results) this.results.innerHTML = '';
    this.announce('');
    this.toggleClear();
    this.input?.focus();
  }

  // Mirror the field into sessionStorage so the term survives navigation; an
  // empty field drops the stored term.
  persistTerm() {
    try {
      const value = this.input?.value ?? '';
      if (value) window.sessionStorage.setItem(SEARCH_TERM_KEY, value);
      else window.sessionStorage.removeItem(SEARCH_TERM_KEY);
    } catch (error) { /* storage blocked — non-fatal */ }
  }

  // The clear control only shows when there is something to clear.
  toggleClear() {
    if (this.clearBtn) this.clearBtn.hidden = !(this.input && this.input.value.length);
  }
}

if (!customElements.get('search-drawer')) {
  customElements.define('search-drawer', SearchDrawer);
}

/* ============ Scrollbars: reveal while scrolling ============ */
/* The theme's scrollbars (see the "Scrollbars" block in capital.css) are
   invisible at rest and fade in while their container is moving. This is the
   only moving part: mark the element that scrolled, unmark it once it has been
   still for a beat.

   Capture phase, because scroll events do not bubble — one listener at the
   document therefore sees every scroll container in the page, including ones
   added later by a section swap or an app block, with nothing to register.

   event.target is `document` for the main page scroll, which is not an Element,
   so the window's own scrollbar is skipped and keeps its native behaviour. */
const SCROLLBAR_IDLE_MS = 900;
const scrollbarTimers = new WeakMap();

document.addEventListener(
  'scroll',
  (event) => {
    const el = event.target;
    if (!(el instanceof Element)) return;
    // Scroll fires at frame rate; only touch the DOM on the leading edge.
    if (!el.classList.contains('is-scrolling')) el.classList.add('is-scrolling');
    window.clearTimeout(scrollbarTimers.get(el));
    scrollbarTimers.set(
      el,
      window.setTimeout(() => {
        el.classList.remove('is-scrolling');
        scrollbarTimers.delete(el);
      }, SCROLLBAR_IDLE_MS)
    );
  },
  { capture: true, passive: true }
);

/* ============ Search: other result types ============ */
/* The search results grid is product-only by necessity — Shopify populates
   search.filters only for a product-scoped query, so a faceted page and mixed
   results cannot come from one request. This fetches the UNSCOPED search for
   the same term through the Section Rendering API and appends the article and
   page buckets the section rendered, giving the shopper the rest of what their
   term matched without costing the product grid its filters.

   Strictly additive: JS-only, silent on every failure, and it appends nothing
   when there is nothing to append. The page is complete before it runs. */
class SearchMoreResults extends HTMLElement {
  connectedCallback() {
    // The Theme Editor reconnects the same instance when a section moves; the
    // fetch must not run again and re-append what is already here.
    if (this.loaded) return;
    this.loaded = true;
    this.load();
  }

  async load() {
    const url = this.dataset.url;
    if (!url) return;
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      // Everything except products — those are already on the page, faceted.
      const groups = [...doc.querySelectorAll('[data-result-group]')].filter(
        (group) => group.dataset.resultGroup !== 'products'
      );
      if (!groups.length) return;
      const wrap = document.createElement('div');
      // .search-groups carries the bucket rhythm; .search-more adds the rule
      // that separates this from the product grid it follows.
      wrap.className = 'search-groups search-more';
      groups.forEach((group) => wrap.appendChild(group));
      this.appendChild(wrap);
    } catch {
      // An addition, never a dependency: a failed fetch leaves the page as it was.
    }
  }
}

define('search-more-results', SearchMoreResults);

/* ============ Wave 4: shop-the-look ============ */
/* ==========================================================================
   WAVE 4 ADDITION — <shop-the-look> (sections/shop-the-look.liquid) — START
   Hotspot disclosure (open/close, Esc, outside click, editor block select),
   measured placement of the open card (#602), plus the "add the look"
   bundle: one /cart/add.js POST with every tagged product's first available
   variant. Shares module scope with capital.js (routes, fetchCart,
   announceCartChange, strings, define, motionReduced) — no new plumbing, no
   libraries.
   ========================================================================== */
class ShopTheLook extends HTMLElement {
  connectedCallback() {
    this.spots = [...this.querySelectorAll('[data-spot]')];
    this.spots.forEach((spot) => {
      spot
        .querySelector('[data-spot-toggle]')
        ?.addEventListener('click', () => this.toggle(spot));
    });
    // Dismiss on a press anywhere off the open card. Use pointerdown, not
    // click: iOS Safari only dispatches `click` to document for taps on
    // natively-clickable elements, so a tap on the bare image/page behind the
    // fixed mobile sheet never reached a document click listener and the sheet
    // stayed open (QA #271). pointerdown fires for a tap on any element and
    // covers mouse + touch + pen. Test against the open spot (not the whole
    // section) so tapping the photo behind the sheet closes the card too.
    this.onDocPointerDown = (event) => {
      const open = this.openSpot();
      if (open && !open.contains(event.target)) this.closeAll();
    };
    document.addEventListener('pointerdown', this.onDocPointerDown);
    // Focus goes back to the marker before the card closes: a closed card is
    // inert (see set()), and focus left inside it would drop to the page.
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.openSpot()?.querySelector('[data-spot-toggle]')?.focus();
      this.closeAll();
    });
    this.onBlockSelect = (event) => {
      const spot = this.spots.find((s) => s === event.target || s.contains(event.target));
      if (spot) this.toggle(spot, true);
    };
    this.onBlockDeselect = () => this.closeAll();
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    // Explicit close affordance on the mobile modal sheet (QA #318). Mirror the
    // Esc path: return focus to the marker, then dismiss, so it is never lost.
    this.querySelectorAll('[data-spot-close]').forEach((button) =>
      button.addEventListener('click', () => {
        this.openSpot()?.querySelector('[data-spot-toggle]')?.focus();
        this.closeAll();
      }),
    );
    // An open card is placed for the window it opened in, so place it again
    // when the window changes size or shape (#602), and lock or free the page
    // for what the card has become: crossing 990 turned a floating card into
    // the sheet with the page still scrolling behind it (QA #602). Lock first,
    // so the card is measured with the scrollbar it will have.
    this.onResize = () => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        const open = this.openSpot();
        if (!open) return;
        this.syncScrollLock();
        this.place(open);
      });
    };
    window.addEventListener('resize', this.onResize);
    this.addAll = this.querySelector('[data-add-all]');
    this.addAll?.addEventListener('click', () => this.onAddAll());
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onDocPointerDown);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    window.removeEventListener('resize', this.onResize);
    cancelAnimationFrame(this.resizeFrame);
    // Never leave the page scroll-locked if the element is torn out while a card
    // is open (e.g. the editor re-rendering the section).
    if (this.openSpot()) document.body.style.overflow = '';
  }

  // The one reader of open state — every path that needs to know which card
  // is open asks here rather than testing the class itself.
  isOpen(spot) {
    return spot.classList.contains('is-open');
  }

  openSpot() {
    return this.spots?.find((spot) => this.isOpen(spot));
  }

  // Below 990px the open card is the fixed bottom sheet; above it, a card
  // floating beside its marker. The CSS switches at the same width.
  isSheet() {
    return window.matchMedia('(max-width: 989.98px)').matches;
  }

  toggle(spot, force) {
    const open = force ?? !this.isOpen(spot);
    this.closeAll();
    // Place it while still closed, with transitions held so the closed state
    // lands at once and the card then eases in from its own side of the
    // marker, not from wherever it last sat.
    if (open) {
      const popover = spot.querySelector('.stl-popover');
      popover?.style.setProperty('transition', 'none');
      this.place(spot, true);
      void spot.offsetWidth;
      popover?.style.removeProperty('transition');
    }
    this.set(spot, open);
    this.syncScrollLock();
  }

  closeAll() {
    this.spots.forEach((spot) => this.set(spot, false));
    this.syncScrollLock();
  }

  set(spot, open) {
    spot.classList.toggle('is-open', open);
    spot.querySelector('[data-spot-toggle]')?.setAttribute('aria-expanded', String(open));
    // A closing card stays visible through its fade, and anything visible can
    // take focus: Tab straight after Esc landed on its Quick add (QA #602).
    // Inert takes the card out of the tab order and the pointer's way at once,
    // while the fade still plays.
    spot.querySelector('.stl-popover')?.toggleAttribute('inert', !open);
  }

  // On mobile the open card is a position:fixed bottom sheet (a modal), so lock
  // the page behind it — the tap-outside-to-close from #271 shouldn't fight an
  // unlocked page scrolling underneath (QA #304). Desktop popovers are inline
  // cards within the photo and must leave the page scrollable, so gate on the
  // same 990px boundary the CSS uses to switch to the fixed sheet. Matches the
  // body-overflow lock the header/cart/filters drawers already use.
  syncScrollLock() {
    document.body.style.overflow = this.openSpot() && this.isSheet() ? 'hidden' : '';
  }

  // Show the whole card — image, title, price and quick add — without
  // scrolling it, and in view (#602). Measured each time a card opens and
  // whenever the window changes, never guessed from the hotspot coordinates.
  place(spot, opening = false) {
    const popover = spot.querySelector('.stl-popover');
    const card = popover?.querySelector('.stl-popover__card');
    if (!card) return;
    if (this.isSheet()) this.fitSheet(popover, card);
    else this.float(spot, popover, opening);
  }

  // The phone sheet keeps one standard height (QA #318), and a portrait card
  // image at the sheet's full width is taller than that on most phones: the
  // title, price and quick add sat below the sheet's fold. Narrow the card
  // until all of it fits, so the image shrinks in proportion and the card
  // centres in the sheet. A second pass catches a title that wraps onto
  // another line at the narrower width.
  fitSheet(popover, card) {
    popover.style.removeProperty('--stl-card-width');
    const media = card.querySelector('.product-card__media');
    for (let pass = 0; pass < 3 && media?.offsetHeight; pass += 1) {
      const over = card.scrollHeight - card.clientHeight;
      if (over < 1) return;
      const width = card.offsetWidth - Math.ceil((over * media.offsetWidth) / media.offsetHeight);
      popover.style.setProperty('--stl-card-width', `${Math.max(width, 120)}px`);
    }
  }

  // Desktop: the card floats beside its marker. The old rule read the hotspot
  // coordinates in thirds, opened up or down, and capped the card at the room
  // that left on the photo, so a marker near the middle opened a card that had
  // to be scrolled (#602). Instead, try each side of the marker — the photo's
  // nearer half first (below a marker in the top half, above one in the bottom
  // half), then the side with more photo — slide the card along that side to
  // stay inside, and take the first side that holds all of it. Room is looked
  // for on the photo, then anywhere in the section's own space (over the copy
  // column or into its rhythm spacing, never over the sections around it —
  // #241), and first only where the shopper can see it, clear of a pinned
  // header.
  float(spot, popover, opening) {
    const GAP = 8; // marker box to card (the disc sits 8px inside the box)
    const EDGE = 8; // card to the edge of the room it sits in
    popover.style.removeProperty('--stl-pop-max-height');
    const width = popover.offsetWidth;
    let height = popover.offsetHeight;
    // The marker box is also the popover's containing block.
    const marker = spot.getBoundingClientRect();
    const photo = this.querySelector('.stl__media').getBoundingClientRect();
    const frame = this.getBoundingClientRect();
    const band = this.closest('.shop-the-look') ?? this;
    const bandBox = band.getBoundingClientRect();
    const bandStyle = getComputedStyle(band);
    const section = {
      top: bandBox.top - parseFloat(bandStyle.marginTop),
      bottom: bandBox.bottom + parseFloat(bandStyle.marginBottom),
      left: frame.left,
      right: frame.right,
    };
    const inset = (name) => parseFloat(getComputedStyle(document.body).getPropertyValue(name)) || 0;
    const view = {
      top: inset('--sticky-offset'),
      left: 0,
      right: document.documentElement.clientWidth,
      bottom: document.documentElement.clientHeight,
    };
    // A smart header hidden on the way down comes back on any scroll up, so
    // the card is placed, and scrolled to, clear of all it can cover: placed
    // under the hidden header, the scroll that showed the card brought the
    // header back over its top (QA #602).
    const clear = { ...view, top: Math.max(view.top, inset('--header-reserve')) };
    const meet = (a, b) => ({
      top: Math.max(a.top, b.top),
      left: Math.max(a.left, b.left),
      right: Math.min(a.right, b.right),
      bottom: Math.min(a.bottom, b.bottom),
    });
    const midX = marker.left + marker.width / 2;
    const midY = marker.top + marker.height / 2;
    const sides = [
      ...(midY <= photo.top + photo.height / 2 ? ['bottom', 'top'] : ['top', 'bottom']),
      ...(midX <= photo.left + photo.width / 2 ? ['right', 'left'] : ['left', 'right']),
    ];
    const at = (side, area) => {
      let x = midX - width / 2;
      let y = midY - height / 2;
      if (side === 'bottom') y = marker.bottom + GAP;
      if (side === 'top') y = marker.top - GAP - height;
      if (side === 'right') x = marker.right + GAP;
      if (side === 'left') x = marker.left - GAP - width;
      if (side === 'top' || side === 'bottom') {
        x = Math.min(Math.max(x, area.left + EDGE), area.right - EDGE - width);
      } else {
        y = Math.min(Math.max(y, area.top + EDGE), area.bottom - EDGE - height);
      }
      const fits =
        x >= area.left + EDGE - 0.5 &&
        y >= area.top + EDGE - 0.5 &&
        x + width <= area.right - EDGE + 0.5 &&
        y + height <= area.bottom - EDGE + 0.5;
      return fits ? { side, x, y } : null;
    };
    const fit = (area) => sides.map((side) => at(side, area)).find(Boolean);
    // A marker the editor has selected may still be off screen (it scrolls to
    // the block after), so the window only counts once the marker is in it.
    const onScreen = marker.top >= view.top && marker.bottom <= view.bottom;
    const seen = onScreen && (fit(meet(photo, clear)) || fit(meet(section, clear)));
    let placement = seen || fit(photo) || fit(section);
    if (!placement) {
      // No side of the section holds the whole card: cap it to the roomier of
      // above and below and let that one scroll.
      const below = section.bottom - EDGE - (marker.bottom + GAP);
      const above = marker.top - GAP - (section.top + EDGE);
      height = Math.max(Math.floor(Math.max(below, above)), 0);
      popover.style.setProperty('--stl-pop-max-height', `${height}px`);
      placement = at(below >= above ? 'bottom' : 'top', section) ?? {
        side: 'bottom',
        x: midX - width / 2,
        y: marker.bottom + GAP,
      };
    }
    spot.dataset.placement = placement.side;
    popover.style.setProperty('--stl-pop-left', `${Math.round(placement.x - marker.left)}px`);
    popover.style.setProperty('--stl-pop-top', `${Math.round(placement.y - marker.top)}px`);
    // The whole card only fits off screen (the section is mostly scrolled
    // away): bring it into view as it opens.
    if (opening && onScreen && !seen) {
      const above = placement.y - (clear.top + EDGE);
      const below = placement.y + height - (view.bottom - EDGE);
      const by = above < 0 ? above : Math.max(below, 0);
      if (by) window.scrollBy({ top: by, behavior: motionReduced() ? 'auto' : 'smooth' });
    }
  }

  async onAddAll() {
    const button = this.addAll;
    if (button.getAttribute('aria-disabled') === 'true') return;
    const ids = [...new Set(this.spots.map((s) => Number(s.dataset.variantId)).filter(Boolean))];
    if (!ids.length) return;
    const label = button.querySelector('[data-add-all-label]');
    const restingText = label?.textContent;
    button.setAttribute('aria-disabled', 'true');
    if (label) label.textContent = strings.adding ?? 'Adding…';
    try {
      const response = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })) }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new CartAddError(error.description || strings.addError || 'Could not add to cart');
      }
      announceCartChange(await fetchCart());
      if (label) label.textContent = strings.added ?? 'Added';
      await document.querySelector('cart-drawer')?.openAfterRefresh();
    } catch (error) {
      if (label) label.textContent = cartAddErrorMessage(error);
    } finally {
      window.setTimeout(() => {
        button.removeAttribute('aria-disabled');
        if (label && restingText) label.textContent = restingText;
      }, 2000);
    }
  }
}

define('shop-the-look', ShopTheLook);
/* ========= END WAVE 4 ADDITION — <shop-the-look> ========================== */

/* ============ Wave 4: video ============ */
/* ==========================================================================
   WAVE 4 ADDITION — <deferred-video> (sections/video.liquid).
   Deferred inline playback: the real <video>/<iframe> lives in an inert
   <template> so nothing (least of all a YouTube/Vimeo embed) loads until
   the poster's play button is pressed. On activation the template content
   is injected, the poster is hidden, and hosted video is played
   programmatically as a belt-and-braces alongside its autoplay attribute.
   No-JS falls back to the section's <noscript> player/link.
   ========================================================================== */
class DeferredVideo extends HTMLElement {
  connectedCallback() {
    this.poster = this.querySelector('[data-video-poster]');
    this.template = this.querySelector('template[data-video-template]');
    this.querySelector('[data-video-play]')?.addEventListener('click', () => {
      this.activate();
    });
  }

  activate() {
    if (!this.template || this.classList.contains('is-playing')) return;
    this.classList.add('is-playing');
    this.appendChild(this.template.content.cloneNode(true));
    if (this.poster) this.poster.hidden = true;
    const media = this.querySelector(
      '.video-player__frame video, .video-player__frame iframe'
    );
    if (media && media.tagName === 'VIDEO') {
      const played = media.play();
      if (played?.catch) played.catch(() => {});
    }
    if (media) {
      // Move focus to the now-playing media, but do NOT set tabindex="-1" on it:
      // a controllable <video controls>/<iframe> must stay in the tab order so a
      // keyboard user can reach its native controls after activation.
      media.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('deferred-video')) {
  customElements.define('deferred-video', DeferredVideo);
}

/* ==========================================================================
   MOTION CONTRACT — <auto-video> (mosaic.liquid looping/auto video cells).
   Wraps a single autoplaying, looping <video>. It plays only while at least
   partly on-screen AND the tab is visible AND the viewer hasn't paused it;
   under reduced motion it starts paused (the baked-in autoplay is stripped)
   but the persistent toggle can still start it. Reuses the shared
   motion-toggle control (WCAG 2.2.2). No-JS: the video keeps its native
   autoplay/loop and the toggle stays hidden.

   Also used by the overlap story's video media and the video section's
   ambient mode (snippets/ambient-video.liquid, #666 / #668). Their <video>
   carries no autoplay attribute — this element starts it once it is in view,
   so with no JS, or under reduced motion, the poster simply stays. A
   YouTube/Vimeo embed waits in a <template data-auto-video-embed> and is
   only injected the first time it may play; after that it is paused and
   played by postMessage. setPaused() lets a surface that owns the pause
   control (the story slideshow) drive the video from outside.
   ========================================================================== */
class AutoVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.embed = this.video ? null : this.querySelector('template[data-auto-video-embed]');
    if (!this.video && !this.embed) return;
    this.visible = false;
    this.sync = this.sync.bind(this);
    /* Reduced motion: suppress the baked-in autoplay and start paused. */
    this.userPaused = motionReduced();
    if (this.userPaused) this.video?.removeAttribute('autoplay');

    this.observer = new IntersectionObserver((entries) => {
      this.visible = entries.some((entry) => entry.isIntersecting);
      this.sync();
    }, { threshold: 0.2 });
    this.observer.observe(this);
    document.addEventListener('visibilitychange', this.sync);

    this.toggle = bindMotionToggle(this.querySelector('[data-motion-toggle]'), (paused) => {
      this.userPaused = paused;
      this.sync();
    });
    if (this.toggle) this.toggle.set(this.userPaused);
    this.sync();
  }

  setPaused(paused) {
    if (this.toggle) {
      this.toggle.set(paused);
      return;
    }
    this.userPaused = paused;
    this.sync();
  }

  sync() {
    if (!this.video && !this.embed) return;
    const canPlay = !this.userPaused && this.visible && !document.hidden;
    if (!this.video) {
      this.syncEmbed(canPlay);
    } else if (canPlay) {
      const played = this.video.play();
      if (played && played.catch) played.catch(() => {});
    } else {
      this.video.pause();
    }
    this.classList.toggle('is-paused', this.userPaused);
  }

  // The embed's src autoplays muted on a loop, so injecting it IS playing it.
  // Nothing loads from YouTube/Vimeo until then. Once loaded, both providers
  // ignore the other's message, so send both rather than sniffing the src
  // (mirrors the product gallery's pauseMediaExcept). The load listener
  // re-sends the current state in case it changed while the player loaded.
  syncEmbed(canPlay) {
    if (!this.frame) {
      if (!canPlay) return;
      this.embed.after(this.embed.content.cloneNode(true));
      this.frame = this.querySelector('iframe');
      this.frame?.addEventListener('load', () => this.sync());
      return;
    }
    const target = this.frame.contentWindow;
    if (!target) return;
    target.postMessage(`{"event":"command","func":"${canPlay ? 'playVideo' : 'pauseVideo'}","args":""}`, '*');
    target.postMessage(`{"method":"${canPlay ? 'play' : 'pause'}"}`, '*');
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.sync);
  }
}
define('auto-video', AutoVideo);

/* ==========================================================================
   MOTION CONTRACT — <marquee-band> (marquee.liquid).
   The scroll is a pure CSS animation; this wrapper adds the persistent
   pause/play (WCAG 2.2.2) that survives pointerleave — the hover/focus pause
   alone fails on touch. Toggling flips .is-paused, which stills the track
   (assets/capital.css). Under reduced motion the CSS already renders a static
   wrapped row, so the control is left hidden.
   ========================================================================== */
/* Perceived speed must not depend on how much content the band holds. The CSS
   animation advances the track by exactly one group width per cycle, so at a
   fixed duration a short group (e.g. a single product) crawls while a long one
   races. Time each cycle to the measured group width instead. The CSS duration
   tokens remain the no-JS fallback.

   Speed is also scaled to the viewport: a fixed px/sec reads far faster on a
   phone than a desktop (the same word crosses a 390px screen in a fraction of
   the time it takes across 1400px), which QA flagged as "faster on mobile".
   Velocities are tuned at REF_WIDTH; below it the run slows in proportion so a
   word stays on screen a comparable time on phone and desktop. It never scales
   ABOVE the reference — wide desktops keep the tuned pace, so only the small
   screens that read too fast change. */
const MARQUEE_VELOCITY = { slow: 40, steady: 70, brisk: 140 };
const MARQUEE_REF_WIDTH = 1280;

class MarqueeBand extends HTMLElement {
  connectedCallback() {
    if (motionReduced()) return;
    bindMotionToggle(this.querySelector('[data-motion-toggle]'), (paused) => {
      this.classList.toggle('is-paused', paused);
    });
    this.track = this.querySelector('.marquee__track');
    this.group = this.querySelector('.marquee__group');
    if (!this.track || !this.group) return;
    this.syncDuration();
    this.resize = new ResizeObserver(() => this.syncDuration());
    this.resize.observe(this.group);
    // A viewport change rescales the velocity even when the group itself does
    // not resize (e.g. both widths sit below the type/gap clamp floors).
    this.resize.observe(document.documentElement);
  }

  syncDuration() {
    const width = this.group.getBoundingClientRect().width;
    if (!width) return;
    const velocity = MARQUEE_VELOCITY[this.dataset.speed] || MARQUEE_VELOCITY.steady;
    const viewport = window.innerWidth || MARQUEE_REF_WIDTH;
    const scaled = velocity * Math.min(viewport / MARQUEE_REF_WIDTH, 1);
    this.track.style.setProperty('--marquee-duration', `${(width / scaled).toFixed(2)}s`);
  }

  disconnectedCallback() {
    this.resize?.disconnect();
  }
}
define('marquee-band', MarqueeBand);

/* ==========================================================================
   MOTION CONTRACT — <story-slideshow> (story.liquid, >1 media block).
   The crossfade + progress rule are pure CSS animations, and when media
   blocks carry their own copy, each copy crossfades on the same timeline
   (#621); this adds the persistent pause/play (WCAG 2.2.2). Toggling flips
   .is-paused on the layout, stilling media, progress and copy together.
   Under reduced motion the CSS holds the first image and its copy static
   (no crossfade), so the control is left hidden unless a video needs it.
   ========================================================================== */
class StorySlideshow extends HTMLElement {
  connectedCallback() {
    this.media = this.closest('.story__media') || this.parentElement;
    if (!this.media) return;
    this.layout = this.closest('.story__layout') || this.media;
    this.index = 0;

    // Prev/next arrows let mouse users step the slideshow (#273). The first
    // press takes manual control: it freezes the timed CSS crossfade
    // (.is-manual) and shows exactly the chosen slide and its copy, reusing
    // the same freeze-and-select machinery as the editor block-select path.
    // Wired unconditionally so it works under reduced motion too (instant swap).
    this.querySelector('[data-story-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
    this.querySelector('[data-story-next]')?.addEventListener('click', () => this.goTo(this.index + 1));

    // Video slides (#666) have no control of their own: this one stills the
    // crossfade and every video together. They keep moving under reduced
    // motion or with the crossfade stopped (Pace off, arrows), so with a video
    // present the control is bound regardless — starting paused under reduced
    // motion, where each video holds its poster — and capital.css keeps it on
    // screen (.story--has-video). syncPlaypause() then hides it at Pace off
    // while an image slide is on screen.
    const videos = [...this.media.querySelectorAll('auto-video')];
    if (!motionReduced() || videos.length) {
      this.playpause = this.querySelector('[data-motion-toggle]');
      const toggle = bindMotionToggle(this.playpause, (paused) => {
        this.layout.classList.toggle('is-paused', paused);
        videos.forEach((video) => video.setPaused?.(paused));
      });
      if (toggle && motionReduced()) toggle.set(true);
      this.syncPlaypause();
    }

    /* Theme editor: when a media block is selected, hold its slide and its
       copy on screen (still the crossfade + progress) so the merchant edits
       the block they picked — mirrors product-showcase's block-select. A media
       block with copy renders twice (slide + copy), so either can be the
       event target, and so can a story block shown in a media block's copy. */
    if (window.Shopify && window.Shopify.designMode) {
      this.onBlockSelect = (event) => {
        const i = this.indexOf(event.target);
        if (i < 0) return;
        this.index = i;
        this.select(i);
        this.layout.classList.add('is-block-selected');
      };
      this.onBlockDeselect = () => {
        this.layout.classList.remove('is-block-selected');
        // Arrows may already have taken manual control; keep that slide.
        this.select(this.layout.classList.contains('is-manual') ? this.index : -1);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  // Queried fresh on each use: the editor can re-render the section's markup
  // under this element.
  slides() {
    return [...this.media.querySelectorAll('.story__slide')];
  }

  copies() {
    return [...this.layout.querySelectorAll('.story__copy')];
  }

  indexOf(target) {
    const holds = (el) => el === target || el.contains(target);
    const slide = this.slides().findIndex(holds);
    return slide > -1 ? slide : this.copies().findIndex(holds);
  }

  // Mark slide i, its progress segment and its copy (-1 clears all three).
  select(index) {
    const mark = (el, i) => el.classList.toggle('is-selected', i === index);
    this.slides().forEach(mark);
    this.media.querySelectorAll('.story__progress-seg').forEach(mark);
    this.copies().forEach(mark);
    this.syncPlaypause();
  }

  // The slide on screen while the crossfade is stopped: the one the arrows or
  // an editor block select picked, else slide 1.
  onScreen() {
    const picked = this.slides().findIndex((slide) => slide.classList.contains('is-selected'));
    return picked > -1 ? picked : 0;
  }

  /* At Pace off nothing rotates, so the control is only the videos' pause: it
     shows while a video slide is on screen and hides on an image slide, as the
     hero's does (#694, #695). capital.css keeps its slot, so the arrows don't
     move. If focus is on it, it goes to the next arrow before it hides. */
  syncPlaypause() {
    const button = this.playpause;
    if (!button || !this.hasAttribute('data-pace-off')) return;
    const hide = !this.slides()[this.onScreen()]?.querySelector('auto-video');
    if (hide && button === document.activeElement && document.hasFocus()) {
      this.querySelector('[data-story-next]')?.focus();
    }
    if (button.hidden !== hide) button.hidden = hide;
  }

  // Step to a slide and hold it. .is-manual freezes the CSS crossfade + the
  // progress fill (via animation:none) and reveals only the .is-selected slide,
  // its progress segment and its copy — the same override the block-select
  // path uses.
  goTo(next) {
    const total = this.slides().length;
    if (total < 2) return;
    this.index = ((next % total) + total) % total;
    this.layout.classList.add('is-manual');
    this.select(this.index);
  }

  disconnectedCallback() {
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    if (this.onBlockDeselect) document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }
}
define('story-slideshow', StorySlideshow);

/* ==========================================================================
   POLISH ADDITION — <before-after> (sections/before-after.liquid)
   Draggable image comparison. Pointer drag anywhere on the viewer, or the
   handle as an ARIA slider (arrow keys ±2 / shift ±10 / Home / End). One
   custom property --ba-pos drives both the after-image clip and the handle.
   ========================================================================== */
class BeforeAfter extends HTMLElement {
  connectedCallback() {
    this.handle = this.querySelector('[data-ba-handle]');
    if (!this.handle) return;
    this.pos = 50;
    this.dragging = false;
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.addEventListener('pointerdown', (event) => this.onDown(event));
    this.handle.addEventListener('keydown', (event) => this.onKey(event));
  }

  onDown(event) {
    if (event.button && event.button !== 0) return;
    this.dragging = true;
    this.classList.add('is-dragging');
    this.setFromEvent(event);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
  }

  onMove(event) {
    if (this.dragging) this.setFromEvent(event);
  }

  onUp() {
    this.dragging = false;
    this.classList.remove('is-dragging');
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
  }

  onKey(event) {
    const step = event.shiftKey ? 10 : 2;
    let next = this.pos;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next -= step;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next += step;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = 100;
    else return;
    event.preventDefault();
    this.set(next);
  }

  setFromEvent(event) {
    const rect = this.getBoundingClientRect();
    if (!rect.width) return;
    this.set(((event.clientX - rect.left) / rect.width) * 100);
  }

  set(value) {
    this.pos = Math.max(0, Math.min(100, value));
    this.style.setProperty('--ba-pos', `${this.pos}%`);
    this.handle.setAttribute('aria-valuenow', String(Math.round(this.pos)));
  }
}
define('before-after', BeforeAfter);

/* ==========================================================================
   POLISH ADDITION — <story-timeline> (sections/timeline.liquid)
   Interactive brand-history milestones. Year rail is a roving-tabindex
   tablist (arrow/Home/End); panels crossfade. Progressive enhancement: the
   host gets .is-enhanced on connect, which collapses the stacked no-JS
   milestone list into a single crossfading panel view. Reduced motion just
   swaps (the crossfade duration is CSS-gated).
   ========================================================================== */
class StoryTimeline extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-timeline-year]'));
    this.panels = Array.from(this.querySelectorAll('[data-timeline-panel]'));
    if (this.tabs.length < 2) return;
    this.classList.add('is-enhanced');
    this.tabs.forEach((tab, index) => {
      // Don't force focus on click (it triggered a :focus-visible box — the
      // "errant rectangle"); keyboard nav still moves focus via onKey.
      tab.addEventListener('click', () => this.select(index, false));
      tab.addEventListener('keydown', (event) => this.onKey(event, index));
    });
    this.select(0, false);

    /* Theme editor: when a milestone block is selected, move the timeline to
       that milestone so the merchant edits the panel they picked (Shopify
       otherwise leaves the previously-shown milestone on screen). The block's
       shopify_attributes sit on the year tab, so match the event target to a
       tab and select its index — mirrors story-slideshow's block-select. */
    if (window.Shopify && window.Shopify.designMode) {
      this.onBlockSelect = (event) => {
        const index = this.tabs.findIndex((tab) => tab === event.target || tab.contains(event.target));
        if (index < 0) return;
        this.select(index, false);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }
  }

  select(index, focus = true) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
      // Cumulative timeline: every year up to AND including the selected one is
      // "passed", so its dot stays ticked (advancing to 2019 keeps 2015 filled).
      // Purely visual — aria-selected/tabIndex above still mark only the current
      // tab, so a11y and roving tabindex are unchanged.
      tab.classList.toggle('is-past', i <= index);
      if (active && focus) tab.focus();
    });
    this.panels.forEach((panel, i) => {
      panel.classList.toggle('is-active', i === index);
    });
    /* Run the line fill to the active year (CSS transitions the width ~1s). */
    const last = this.tabs.length - 1;
    this.style.setProperty('--timeline-progress', last > 0 ? index / last : 0);
    this.index = index;
  }

  onKey(event, index) {
    let next;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + this.tabs.length) % this.tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;
    else return;
    event.preventDefault();
    this.select(next);
  }

  disconnectedCallback() {
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }
}
define('story-timeline', StoryTimeline);

/* ==========================================================================
   POLISH ADDITION — <gallery-lightbox> (sections/gallery.liquid)
   Zoom-to-view for gallery image tiles with no merchant link. No-JS: the
   trigger is a link to the full image. JS: a native <dialog> with prev/next
   (arrow keys), caption, backdrop-close, built once on first open.
   ========================================================================== */
class GalleryLightbox extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-lightbox-trigger]'));
    if (!this.triggers.length) return;
    // `href` is the gallery-section contract; `data-lightbox-src` is the PDP
    // contract (full-res image_url). Support both so one class serves both.
    this.items = this.triggers.map((trigger) => ({
      full: trigger.getAttribute('href') || trigger.dataset.lightboxSrc || '',
      caption: trigger.dataset.lightboxCaption || '',
    }));
    this.triggers.forEach((trigger, index) =>
      trigger.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        this.open(index);
      })
    );
    // The section markup gives every trigger the same generic zoom label, so a
    // screen-reader user hears no difference between tiles. Fold the per-image
    // caption (or its position) into each trigger's accessible name so each is
    // uniquely identifiable.
    if (this.triggers.length > 1) {
      this.triggers.forEach((trigger, index) => {
        const base = (trigger.getAttribute('aria-label') || '').trim() || this.dataset.viewerLabel || 'View image';
        const caption = this.items[index].caption;
        trigger.setAttribute(
          'aria-label',
          caption ? `${base}: ${caption}` : `${base} (${index + 1} / ${this.triggers.length})`
        );
      });
    }
  }

  icon(paths) {
    return `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">${paths}</svg>`;
  }

  build() {
    if (this.dialog) return;
    this.dialog = document.createElement('dialog');
    this.dialog.className = 'lightbox';
    this.dialog.setAttribute('aria-label', this.dataset.viewerLabel || 'Image viewer');
    this.dialog.innerHTML = `
      <figure class="lightbox__stage">
        <img class="lightbox__img" alt="" />
        <figcaption class="lightbox__caption type-eyebrow"></figcaption>
      </figure>
      <button class="lightbox__close" type="button" aria-label="${this.dataset.closeLabel || 'Close'}">${this.icon('<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>')}</button>
      <button class="lightbox__nav lightbox__nav--prev" type="button" aria-label="${this.dataset.prevLabel || 'Previous'}">${this.icon('<path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>')}</button>
      <button class="lightbox__nav lightbox__nav--next" type="button" aria-label="${this.dataset.nextLabel || 'Next'}">${this.icon('<path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>')}</button>`;
    this.img = this.dialog.querySelector('.lightbox__img');
    this.caption = this.dialog.querySelector('.lightbox__caption');
    this.dialog.querySelector('.lightbox__close').addEventListener('click', () => this.requestClose());
    this.dialog.querySelector('.lightbox__nav--prev').addEventListener('click', () => this.show(this.index - 1));
    this.dialog.querySelector('.lightbox__nav--next').addEventListener('click', () => this.show(this.index + 1));
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.requestClose();
    });
    // Native <dialog> raises `cancel` on Esc; intercept it so we run the
    // animated close and return focus to the tile ourselves.
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.requestClose();
    });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
      else if (event.key === 'ArrowRight') this.show(this.index + 1);
    });
    const single = this.items.length < 2;
    this.dialog.querySelectorAll('.lightbox__nav').forEach((nav) => { nav.hidden = single; });
    this.appendChild(this.dialog);
  }

  reduced() {
    return typeof motionReduced === 'function' ? motionReduced() : false;
  }

  tokens() {
    const cs = getComputedStyle(document.documentElement);
    const dur = parseFloat(cs.getPropertyValue('--dur-crossfade')) || 350;
    const ease = cs.getPropertyValue('--ease-media').trim() || 'cubic-bezier(0.2, 0.6, 0.2, 1)';
    return { dur, ease };
  }

  open(index) {
    this.build();
    this.show(index);
    document.body.style.overflow = 'hidden';
    // The signature scale-from-tile drives the entrance; skip the dialog's own
    // scale-in so the two don't compound.
    if (!this.reduced()) this.dialog.setAttribute('data-flip', '');
    else this.dialog.removeAttribute('data-flip');
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    this.flipOpen();
  }

  // Animate the image back down to its tile, then close and restore focus.
  requestClose() {
    if (this.reduced() || !this.activeTrigger) return this.finishClose();
    const anim = this.flip(this.activeTrigger, false);
    if (!anim) return this.finishClose();
    this.dialog.classList.add('is-closing');
    const done = () => this.finishClose();
    anim.addEventListener('finish', done, { once: true });
    anim.addEventListener('cancel', done, { once: true });
  }

  finishClose() {
    document.body.style.overflow = '';
    this.dialog.classList.remove('is-closing');
    this.dialog.removeAttribute('data-flip');
    if (this.dialog.open) this.dialog.close();
    // Return focus to the tile the shopper opened (or last navigated to).
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus();
  }

  flipOpen() {
    if (this.reduced() || !this.activeTrigger) return;
    const run = () => this.flip(this.activeTrigger, true);
    if (this.img.complete && this.img.naturalWidth) run();
    else this.img.addEventListener('load', run, { once: true });
  }

  // FLIP scale-from-tile: map the full-screen image's rect onto the tile's
  // rect. opening=true grows from the tile; opening=false shrinks back to it.
  flip(trigger, opening) {
    if (!trigger || !this.img || typeof this.img.animate !== 'function') return null;
    // Clear a prior close animation's forwards-filled transform so the image's
    // rect is measured at its true (untransformed) size.
    this.img.getAnimations?.().forEach((animation) => animation.cancel());
    const tile = trigger.getBoundingClientRect();
    const target = this.img.getBoundingClientRect();
    if (!tile.width || !tile.height || !target.width || !target.height) return null;
    const dx = tile.left + tile.width / 2 - (target.left + target.width / 2);
    const dy = tile.top + tile.height / 2 - (target.top + target.height / 2);
    const sx = tile.width / target.width;
    const sy = tile.height / target.height;
    const fromTile = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    const frames = opening
      ? [{ transform: fromTile }, { transform: 'none' }]
      : [{ transform: 'none' }, { transform: fromTile }];
    const { dur, ease } = this.tokens();
    return this.img.animate(frames, {
      duration: dur,
      easing: ease,
      fill: opening ? 'none' : 'forwards',
    });
  }

  show(index) {
    const count = this.items.length;
    this.index = (index + count) % count;
    this.activeTrigger = this.triggers[this.index];
    // Keep the focus-return target in sync with the image on screen.
    this.lastFocus = this.activeTrigger;
    const item = this.items[this.index];
    this.img.src = item.full;
    this.img.alt = item.caption || '';
    this.caption.textContent = item.caption;
    this.caption.hidden = !item.caption;
  }
}
define('gallery-lightbox', GalleryLightbox);

/* ============ Header ============ */
/* ==========================================================================
   HEADER FRAGMENT — sections/header.liquid + snippets/mega-menu.liquid.
   Concatenate into capital.js (after the shared helpers). Shares module
   scope: uses `motionReduced` and `define` from capital.js; falls back
   gracefully if evaluated standalone.

   <site-header>    disclosure nav w/ hover-intent, smart sticky, transparent
                    -over-hero solidify.
   <header-drawer>  off-canvas 2-level mobile menu (slide + accordions),
                    inert/esc/focus-trap, :target no-JS open.

   Both surfaces render their locale pickers through <localization-selector>
   (see the footer), so neither needs a select auto-submit of its own.

   Behaviour is attribute-driven; all motion is reduced-motion gated in CSS.
   ========================================================================== */

const headerReduced = () =>
  typeof motionReduced === 'function'
    ? motionReduced()
    : document.documentElement.classList.contains('motion-reduced') ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* --------------------------------------------------------------------------
   <site-header>
   -------------------------------------------------------------------------- */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('.site-header');
    if (!this.header) return;
    this.mode = this.dataset.sticky || 'smart';
    this.transparent = this.dataset.transparent === 'true';
    this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.openTimer = 0;
    this.closeTimer = 0;
    this.openItemEl = null;
    this.editorPinnedItem = null; // theme editor: block-selected panel to hold open
    this.lastY = window.scrollY || 0;
    this.solidThreshold = 40;

    // Transparent-over-hero "auto" is emitted for every homepage by
    // header.liquid, but overlaying a transparent header on a page that does NOT
    // open with a full-bleed hero leaves it unreadable. Verify at runtime that
    // the first content section really is a compatible hero; if not, stay solid.
    if (this.transparent && !this.firstSectionIsHero()) {
      this.transparent = false;
      this.header.classList.remove('site-header--transparent');
    }

    this.setupNav();
    this.setupScroll();
    if (this.transparent) this.setupTransparentOffset();

    // Theme editor: pin the edited block's mega panel open so the merchant can
    // see it while editing. Hover-leave and outside clicks leave it open until
    // the block is deselected (QA #381).
    this.onBlockSelect = (event) => {
      const id = event.detail?.blockId;
      const panel = id && this.header.querySelector(`.header-nav__panel[data-block-id="${id}"]`);
      if (!panel) return;
      const item = panel.closest('.header-nav__item');
      this.editorPinnedItem = item;
      this.openItem(item);
    };
    this.onBlockDeselect = (event) => {
      const id = event.detail?.blockId;
      const panel = id && this.header.querySelector(`.header-nav__panel[data-block-id="${id}"]`);
      if (!panel) return;
      const item = panel.closest('.header-nav__item');
      if (this.editorPinnedItem === item) this.editorPinnedItem = null;
      this.closeItem(item);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.onSectionLoad) document.removeEventListener('shopify:section:load', this.onSectionLoad);
    if (this.onSectionUnload) document.removeEventListener('shopify:section:unload', this.onSectionUnload);
    window.removeEventListener('scroll', this.onScrollBound);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    document.removeEventListener('keydown', this.onDocKeydown);
    document.removeEventListener('click', this.onDocClick);
  }

  // A compatible first section is a full-bleed hero the transparent header can
  // sit over: the homepage hero (<hero-slideshow>) or a collection banner.
  // Anything else (a heading, product grid, …) would render the transparent
  // header unreadable, so it must stay solid.
  firstSectionIsHero() {
    const main = document.querySelector('main, #MainContent') || document;
    const firstSection = main.querySelector('.shopify-section');
    if (!firstSection) return false;
    return Boolean(firstSection.querySelector('hero-slideshow, .collection-banner'));
  }

  /* ---- Disclosure nav ---- */
  setupNav() {
    this.items = [...this.header.querySelectorAll('.header-nav__item--has-panel')];
    this.items.forEach((item) => {
      const button = item.querySelector('.header-nav__button');
      const panel = item.querySelector('[data-nav-panel]');
      if (!button || !panel) return;
      panel.setAttribute('inert', ''); // JS owns the open state from here
      button.addEventListener('click', () => this.toggleItem(item));
      // Keyboard focus (Tab) opens the panel so a keyboard user can reach its
      // links, matching the hover-open for mouse users. :focus-visible keeps a
      // mouse click from double-firing open — the click handler already toggles.
      button.addEventListener('focus', () => {
        if (button.matches(':focus-visible')) this.openItem(item);
      });
      if (this.finePointer.matches) {
        item.addEventListener('pointerenter', () => this.hoverOpen(item));
        item.addEventListener('pointerleave', () => this.hoverClose(item));
      }
      item.addEventListener('focusout', (event) => {
        if (this.editorPinnedItem === item) return;
        if (!item.contains(event.relatedTarget)) this.closeItem(item);
      });
      this.setupSubItems(item);
    });

    this.onDocKeydown = (event) => {
      if (event.key !== 'Escape') return;
      // Escape unwinds one level at a time: the sub-panel first, then the
      // dropdown it sits in.
      if (this.openSubEl) {
        const sub = this.openSubEl;
        this.closeSub(sub);
        sub.querySelector('.header-nav__sub-toggle')?.focus();
        return;
      }
      if (this.openItemEl) {
        const item = this.openItemEl;
        this.closeItem(item);
        item.querySelector('.header-nav__button')?.focus();
      }
    };
    this.onDocClick = (event) => {
      if (
        this.openItemEl &&
        this.openItemEl !== this.editorPinnedItem &&
        !this.openItemEl.contains(event.target)
      ) {
        this.closeItem(this.openItemEl);
      }
      // Close the localization popover on outside click.
      this.header.querySelectorAll('.header-locale[open]').forEach((details) => {
        if (!details.contains(event.target)) details.removeAttribute('open');
      });
    };
    document.addEventListener('keydown', this.onDocKeydown);
    document.addEventListener('click', this.onDocClick);
  }

  hoverOpen(item) {
    window.clearTimeout(this.closeTimer);
    this.openTimer = window.setTimeout(() => this.openItem(item), 120);
  }

  hoverClose(item) {
    window.clearTimeout(this.openTimer);
    // Hold the panel open while its block is selected in the theme editor,
    // whatever the mouse does (QA #381).
    if (this.editorPinnedItem === item) return;
    // The ~400ms delay is the safe-travel window: crossing the gap between
    // the trigger and the panel fires leave→enter inside this window, and the
    // re-enter cancels the close before it runs.
    this.closeTimer = window.setTimeout(() => this.closeItem(item), 400);
  }

  toggleItem(item) {
    if (item.hasAttribute('data-open')) this.closeItem(item);
    else this.openItem(item);
  }

  openItem(item) {
    if (!item) return;
    if (this.openItemEl && this.openItemEl !== item) this.closeItem(this.openItemEl);
    window.clearTimeout(this.closeTimer);
    const button = item.querySelector('.header-nav__button');
    const panel = item.querySelector('[data-nav-panel]');
    button?.setAttribute('aria-expanded', 'true');
    panel?.removeAttribute('inert');
    panel?.classList.add('is-open');
    item.setAttribute('data-open', '');
    this.openItemEl = item;
  }

  closeItem(item) {
    if (!item) return;
    const button = item.querySelector('.header-nav__button');
    const panel = item.querySelector('[data-nav-panel]');
    button?.setAttribute('aria-expanded', 'false');
    panel?.classList.remove('is-open');
    panel?.setAttribute('inert', '');
    item.removeAttribute('data-open');
    item.querySelectorAll('.header-nav__subitem[data-open]').forEach((sub) => this.closeSub(sub));
    if (this.openItemEl === item) this.openItemEl = null;
  }

  /* ---- Third level — sub-panels beside a simple dropdown ---- */

  // Same open model as the top level (hover on a fine pointer, click or
  // keyboard focus otherwise), but the panel opens beside its parent rather
  // than below it, so it also has to be placed on open (#465).
  setupSubItems(item) {
    item.querySelectorAll('.header-nav__subitem').forEach((sub) => {
      const button = sub.querySelector('.header-nav__sub-toggle');
      const panel = sub.querySelector('[data-nav-subpanel]');
      if (!button || !panel) return;
      panel.setAttribute('inert', ''); // JS owns the open state from here
      button.addEventListener('click', () => this.toggleSub(sub));
      button.addEventListener('focus', () => {
        if (button.matches(':focus-visible')) this.openSub(sub);
      });
      if (this.finePointer.matches) {
        sub.addEventListener('pointerenter', () => this.hoverOpenSub(sub));
        sub.addEventListener('pointerleave', () => this.hoverCloseSub(sub));
      }
      sub.addEventListener('focusout', (event) => {
        if (!sub.contains(event.relatedTarget)) this.closeSub(sub);
      });
    });
  }

  hoverOpenSub(sub) {
    window.clearTimeout(this.closeSubTimer);
    this.openSubTimer = window.setTimeout(() => this.openSub(sub), 120);
  }

  hoverCloseSub(sub) {
    window.clearTimeout(this.openSubTimer);
    // As the top level: the delay is the safe-travel window for crossing the
    // gap between the trigger row and the panel beside it.
    this.closeSubTimer = window.setTimeout(() => this.closeSub(sub), 400);
  }

  toggleSub(sub) {
    if (sub.hasAttribute('data-open')) this.closeSub(sub);
    else this.openSub(sub);
  }

  openSub(sub) {
    if (!sub) return;
    if (this.openSubEl && this.openSubEl !== sub) this.closeSub(this.openSubEl);
    window.clearTimeout(this.closeSubTimer);
    const button = sub.querySelector('.header-nav__sub-toggle');
    const panel = sub.querySelector('[data-nav-subpanel]');
    this.placeSub(sub);
    button?.setAttribute('aria-expanded', 'true');
    panel?.removeAttribute('inert');
    sub.setAttribute('data-open', '');
    this.openSubEl = sub;
  }

  closeSub(sub) {
    if (!sub) return;
    const button = sub.querySelector('.header-nav__sub-toggle');
    const panel = sub.querySelector('[data-nav-subpanel]');
    button?.setAttribute('aria-expanded', 'false');
    panel?.setAttribute('inert', '');
    sub.removeAttribute('data-open');
    sub.classList.remove('header-nav__subitem--flip');
    if (this.openSubEl === sub) this.openSubEl = null;
  }

  // Open outwards from the parent panel, or back over it when there isn't room
  // before the edge of the viewport. Measured from the panel itself (laid out
  // even while hidden), so the CSS owns the offsets.
  placeSub(sub) {
    const panel = sub.querySelector('[data-nav-subpanel]');
    if (!panel) return;
    sub.classList.remove('header-nav__subitem--flip');
    const rect = panel.getBoundingClientRect();
    const margin = 8;
    const rtl = window.getComputedStyle(panel).direction === 'rtl';
    const overflows = rtl ? rect.left < margin : rect.right > window.innerWidth - margin;
    if (overflows) sub.classList.add('header-nav__subitem--flip');
  }

  /* ---- Smart sticky + transparent solidify ---- */
  setupScroll() {
    // Static never pins — not even a transparent-over-hero header. Left running,
    // the transparent path would solidify to position:fixed and pin the header
    // on the homepage; static must scroll away with the page instead (it stays
    // over the hero as it exits, so it needs no solidify). Only sticky and smart
    // run the scroll orchestration.
    if (this.mode === 'static') return;
    this.onScrollBound = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => this.onScroll());
    };
    window.addEventListener('scroll', this.onScrollBound, { passive: true });
    this.onScroll();
  }

  onScroll() {
    this.ticking = false;
    const y = window.scrollY || 0;
    let solid = true;

    if (this.transparent) {
      solid = y > this.solidThreshold;
      this.header.classList.toggle('is-solid', solid);
    } else if (this.mode !== 'static') {
      this.header.classList.toggle('is-scrolled', y > 4);
    }

    if (this.mode === 'smart' && (!this.transparent || solid)) {
      const goingDown = y > this.lastY;
      const drawerOpen = document.body.classList.contains('header-drawer-open');
      if (goingDown && y > 120 && !this.openItemEl && !drawerOpen) {
        this.header.classList.add('is-hidden');
      } else if (!goingDown) {
        this.header.classList.remove('is-hidden');
      }
    }
    this.lastY = y;
  }

  /* ---- Transparent header sits below the announcement bar (if any) ---- */
  setupTransparentOffset() {
    const wrapper = this.closest('.shopify-section');
    if (!wrapper) return;
    // Resolve the announcement bar's OWN section, wherever it sits in the
    // header group — never the header's previousElementSibling. The bar can be
    // ordered below the header, or removed entirely; in both cases the element
    // immediately before the header is the off-screen 45px "Skip to content"
    // link, whose height would otherwise leak into the offset and push the
    // header down over the hero (#323). <announcement-bar> is enabled_on the
    // header group only, so there is at most one; its section wrapper is the
    // in-flow chrome the absolute header must clear. No bar (removed, or empty
    // on the storefront) -> flush at 0.
    const barSection = () =>
      document.querySelector('announcement-bar')?.closest('.shopify-section') || null;
    const apply = () => {
      const bar = barSection();
      this.header.style.setProperty('--header-top', `${bar ? bar.offsetHeight : 0}px`);
    };
    // Re-resolve the bar's section each time rather than capturing it once: the
    // Theme Editor swaps in a brand-new .shopify-section node for the
    // announcement bar on every setting change, which orphans a captured
    // reference — the observer then only ever hears the detached node collapse
    // to 0, freezing --header-top at 0 so the transparent header slides up
    // behind the bar until the next reload.
    const observe = () => {
      this.resizeObserver.disconnect();
      const bar = barSection();
      if (bar) this.resizeObserver.observe(bar);
      apply();
    };
    this.resizeObserver = new ResizeObserver(apply);
    observe();
    // Theme editor: recompute the offset on the announcement bar's lifecycle.
    // A setting change RE-RENDERS the bar (section:load — re-observe the fresh
    // node); DELETING it fires section:unload. Removal is deferred to the next
    // frame so the offset is re-measured AFTER the bar's node has left the DOM,
    // and barSection() then resolves to null -> 0. Without this, --header-top
    // stays frozen at the now-gone bar's height and the header sits pushed down,
    // leaving a gap at the top (#300). Relying on the ResizeObserver alone is
    // not enough: a removed node is no longer rendered, so the observer is not
    // guaranteed to report its collapse to 0. (The storefront dismiss is safe —
    // the observed section wrapper stays attached and resizes 40px -> 0 when the
    // <announcement-bar> inside it hides, which the observer does report.)
    if (window.Shopify && window.Shopify.designMode) {
      this.onSectionLoad = observe;
      this.onSectionUnload = () => requestAnimationFrame(observe);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
    }
  }
}

/* --------------------------------------------------------------------------
   <header-drawer>
   -------------------------------------------------------------------------- */
class HeaderDrawer extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-drawer-root]');
    this.panel = this.querySelector('.header-drawer__panel');
    this.stage = this.querySelector('[data-drawer-stage]');
    if (!this.root) return;
    this.rootPage = this.querySelector('.header-drawer__page--root');
    this.subs = [...this.querySelectorAll('.header-drawer__page--sub')];
    this.subs.forEach((page) => page.setAttribute('inert', ''));
    // Closed a11y state under JS (no-JS keeps it interactive for the :target
    // fallback; visibility:hidden already blocks interaction when closed).
    this.root.setAttribute('inert', '');
    this.lastFocus = null;

    this.onDocClick = (event) => {
      const opener = event.target.closest('[data-header-drawer-open]');
      if (opener && opener.getAttribute('aria-controls') === this.root.id) {
        event.preventDefault();
        this.open(opener);
      }
    };
    document.addEventListener('click', this.onDocClick);

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      // The Offers row (#657): the offers drawer opens from this same click
      // (its listener is on the document, so it runs after this one) and sits
      // beneath this drawer, so step aside first. Two modals never stack.
      if (event.target.closest('[data-offers-drawer-open]')) {
        this.close();
        return;
      }
      const to = event.target.closest('[data-drawer-to]');
      if (to) {
        event.preventDefault();
        this.showSub(to.getAttribute('data-drawer-to'), to);
        return;
      }
      if (event.target.closest('[data-drawer-back]')) {
        event.preventDefault();
        this.showRoot();
        return;
      }
      const accordion = event.target.closest('.header-drawer__accordion');
      if (accordion) this.toggleAccordion(accordion);
    });

    this.onDocKeydown = (event) => {
      if (!this.isOpen()) return;
      if (event.key === 'Escape') {
        // Escape unwinds one level at a time: an open locale dropdown in the
        // dock first, then the drawer. <localization-selector> closes the
        // dropdown on this same keypress — it is defined after this element,
        // so its listener runs second and still sees the press.
        if (this.querySelector('[data-locale-dropdown][open]')) return;
        event.preventDefault();
        this.close();
      } else if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    };
    document.addEventListener('keydown', this.onDocKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onDocKeydown);
  }

  isOpen() {
    return this.root?.classList.contains('is-open');
  }

  open(opener) {
    this.lastFocus = opener || document.activeElement;
    this.showRoot();
    this.root.classList.add('is-open');
    this.root.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('header-drawer-open');
    opener?.setAttribute('aria-expanded', 'true');
    // Focus the VISIBLE close button, not the first [data-drawer-close] — the
    // invisible full-screen overlay carries that attribute and sits first in
    // the DOM, so the drawer opened with the ring nowhere on screen (#481).
    // The cart and offers drawers already name their own close button; match.
    (this.querySelector('.header-drawer__close') || this.panel)?.focus();
  }

  close() {
    this.root.classList.remove('is-open');
    this.root.setAttribute('inert', '');
    document.body.style.overflow = '';
    document.body.classList.remove('header-drawer-open');
    document
      .querySelectorAll(`[data-header-drawer-open][aria-controls="${this.root.id}"]`)
      .forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
    // Clear a :target hash left by the no-JS opener path.
    if (window.location.hash === `#${this.root.id}`) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    this.lastFocus?.focus();
  }

  showSub(id, trigger) {
    const page = this.querySelector(`.header-drawer__page--sub[data-drawer-page="${id}"]`);
    if (!page || !this.stage) return;
    this.subs.forEach((sub) => {
      const on = sub === page;
      sub.classList.toggle('is-active', on);
      sub.toggleAttribute('inert', !on);
    });
    this.rootPage?.setAttribute('inert', '');
    this.stage.setAttribute('data-active-sub', id);
    this.stage.scrollTop = 0;
    trigger?.setAttribute('aria-expanded', 'true');
    // preventScroll or the sub-page jitters on the way in (#501): at this point
    // it is still parked at translateX(100%), so focusing its Back button asks
    // the browser to scroll it into view. The stage is overflow-x: hidden, but
    // hidden still scrolls programmatically — it jumped scrollLeft to the full
    // panel width and then eased back to 0 (html has scroll-behavior: smooth)
    // while the transform was easing in, so the two animations fought.
    page.querySelector('[data-drawer-back]')?.focus({ preventScroll: true });
  }

  showRoot() {
    this.subs.forEach((sub) => {
      sub.classList.remove('is-active');
      sub.setAttribute('inert', '');
    });
    this.rootPage?.removeAttribute('inert');
    this.stage?.removeAttribute('data-active-sub');
    if (this.stage) this.stage.scrollTop = 0;
    this.querySelectorAll('[data-drawer-to]').forEach((trigger) =>
      trigger.setAttribute('aria-expanded', 'false')
    );
  }

  toggleAccordion(button) {
    const list = button.nextElementSibling;
    const open = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!open));
    if (list) list.hidden = open;
  }

  trapFocus(event) {
    if (!this.panel) return;
    // `summary` is in the list because the locale pickers at the foot of the
    // drawer are <details>, and they are the last thing in tab order.
    const focusable = [
      ...this.panel.querySelectorAll(
        'a[href], button:not([disabled]), summary, select, input, [tabindex]:not([tabindex="-1"])'
      ),
    ].filter(
      (el) =>
        el.offsetParent !== null &&
        !el.closest('[inert]') &&
        // A closed <details> is not enough to fail the offsetParent test:
        // Chrome hides ::details-content with content-visibility, so its
        // options keep a layout box while being unfocusable. Left in, the last
        // option of a closed locale dropdown became the trap's "last" element,
        // the trap never fired on the real last one, and Tab walked straight
        // out of the open drawer into the page behind it.
        (el.tagName === 'SUMMARY' || !el.closest('details:not([open])'))
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

/* --------------------------------------------------------------------------
   <offers-nudge> — the header Offers icon's note (#657)
   -------------------------------------------------------------------------- */
/* Shows the note under the icon for a few seconds, at most once a day per
   browser, and never where the icon isn't on screen: with no offers drawer on
   the page, or below 990px, where the icon lives in the menu drawer. No
   storage means no way to keep to once a day, so it stays quiet. The theme
   editor shows it on every load, so the merchant sees what they wrote. */
class OffersNudge extends HTMLElement {
  connectedCallback() {
    const note = this.querySelector('[data-offers-nudge]');
    if (!note || this.getClientRects().length === 0) return;
    if (!(window.Shopify && window.Shopify.designMode)) {
      const today = new Date().toDateString();
      try {
        if (window.localStorage.getItem('capital:offers-nudge') === today) return;
        window.localStorage.setItem('capital:offers-nudge', today);
      } catch (error) {
        return;
      }
    }
    note.hidden = false;
    requestAnimationFrame(() => note.classList.add('is-visible'));
    const hide = () => {
      window.clearTimeout(this.timer);
      note.classList.remove('is-visible');
      this.timer = window.setTimeout(() => {
        note.hidden = true;
      }, 400);
    };
    this.timer = window.setTimeout(hide, 5000);
    this.addEventListener('click', hide, { once: true });
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
  }
}

if (!customElements.get('offers-nudge')) customElements.define('offers-nudge', OffersNudge);

if (typeof define === 'function') {
  define('site-header', SiteHeader);
  define('header-drawer', HeaderDrawer);
} else {
  if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);
  if (!customElements.get('header-drawer')) customElements.define('header-drawer', HeaderDrawer);
}

/* ============ Footer ============ */
/* ==========================================================================
   FOOTER FRAGMENT — merge into capital.js.

   <localization-selector> — manages the footer's custom locale dropdowns
   ([data-locale-dropdown] <details>), mirroring the announcement bar: only one
   open at a time, and outside-click / Escape close them. Each option is a real
   submit button, so switching country/language is a native form POST that needs
   no JS. (Announcement-bar keeps its own copy of this inline.)

   No behaviour is needed for the column accordions (native <details>) or the
   back-to-top link (native `#top` anchor + the theme's reduced-motion
   scroll-behaviour override).
   ========================================================================== */
class LocalizationSelector extends HTMLElement {
  connectedCallback() {
    this.dropdowns = [...this.querySelectorAll('[data-locale-dropdown]')];
    if (!this.dropdowns.length) return;

    this.dropdowns.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        this.dropdowns.forEach((other) => {
          if (other !== details) other.open = false;
        });
      });
    });

    this.onDocClick = (event) => {
      this.dropdowns.forEach((details) => {
        if (details.open && !details.contains(event.target)) details.open = false;
      });
    };
    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      this.dropdowns.forEach((details) => {
        if (!details.open) return;
        details.open = false;
        details.querySelector('summary')?.focus();
      });
    };
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
    if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
  }
}

if (!customElements.get('localization-selector')) {
  customElements.define('localization-selector', LocalizationSelector);
}

/* --------------------------------------------------------------------------
   <price-range> — self-built dual-thumb price slider (no library).
   A pure enhancement layered over the price_range facet's two <input
   type="number"> min/max, which stay the submitted values + the no-JS
   fallback (the element is hidden until :defined). Two overlaid range inputs
   give a robust dual thumb; pointer-events are managed in CSS (none on the
   track, auto on the thumbs) so both stay grabbable. On drag it clamps lo<=hi,
   mirrors the values into the bound number inputs and dispatches a bubbling
   'input' on them so CollectionFilters.applyFromForm() fires (debounced).
   It also turns the two boxes into text fields showing the store's money
   format ("8,824"), which a number input can't hold. The filter still gets
   plain numbers: applyFromForm() sends writeParams(), never the box text.
   -------------------------------------------------------------------------- */
class PriceRange extends HTMLElement {
  // One-time setup, with deliberately no disconnectedCallback to undo it. The
  // Theme Editor disconnects and reconnects this same instance when a merchant
  // moves the section, so listeners taken off there never came back (#720).
  // They don't need taking off: each is on this element's thumbs or on the two
  // boxes beside it in the facet panel, which is only ever replaced whole.
  connectedCallback() {
    if (this.enhanced) return;
    this.lo = this.querySelector('.filter-price-slider__input--lo');
    this.hi = this.querySelector('.filter-price-slider__input--hi');
    const scope = this.closest('.filter-disclosure__panel') || document;
    this.minInput = this.dataset.minInput ? scope.querySelector(this.dataset.minInput) : null;
    this.maxInput = this.dataset.maxInput ? scope.querySelector(this.dataset.maxInput) : null;
    // No-JS-safe: without both thumbs and both bound number inputs, do nothing.
    if (!this.lo || !this.hi || !this.minInput || !this.maxInput) return;
    this.enhanced = true;

    this.max = parseFloat(this.dataset.max);
    if (!Number.isFinite(this.max) || this.max <= 0) this.max = parseFloat(this.hi.max) || 100;

    // Currency formatter for the live aria-valuetext (a11y). Fall back to the
    // raw number if the ISO code is missing or Intl can't honour it.
    this.money = null;
    if (this.dataset.currency) {
      try {
        this.money = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.dataset.currency,
          maximumFractionDigits: 0,
        });
      } catch (err) {
        this.money = null;
      }
    }

    // The store's thousands and decimal marks, read off its money format in
    // the snippet. The boxes arrive as number inputs holding plain numbers (the
    // no-JS fallback), so read them before the type change and show them in
    // that format.
    this.thousands = this.dataset.thousands ?? ',';
    this.decimal = this.dataset.decimal || '.';
    [this.minInput, this.maxInput].forEach((input) => {
      const value = parseFloat(input.value);
      input.type = 'text';
      input.value = Number.isFinite(value) ? this.format(value) : '';
    });

    // Seed the thumbs from whatever the number inputs currently hold.
    this.syncFromInputs();

    this.onLo = () => this.onThumb('lo');
    this.onHi = () => this.onThumb('hi');
    this.lo.addEventListener('input', this.onLo);
    this.hi.addEventListener('input', this.onHi);
    // P6.1a: a typed min>max is normalized on COMMIT (change/blur), not on every
    // keystroke, so gte>lte can never be submitted. Fires before the form's own
    // 'change' → debounced applyFromForm (both bubble; the input's runs first).
    this.onCommit = () => this.normalizeInputs();
    this.minInput.addEventListener('change', this.onCommit);
    this.maxInput.addEventListener('change', this.onCommit);
    // Leaving a box commits too: it orders the pair and the typing takes the
    // money format. 'change' alone misses the box the live refresh hands back
    // while the shopper is typing: they never edited that copy, so leaving it
    // fires no 'change' (#721).
    this.minInput.addEventListener('blur', this.onCommit);
    this.maxInput.addEventListener('blur', this.onCommit);
    // The arrow keys still step a whole unit, as they did on the number input.
    this.onStep = (event) => this.step(event);
    this.minInput.addEventListener('keydown', this.onStep);
    this.maxInput.addEventListener('keydown', this.onStep);
  }

  // ArrowUp / ArrowDown move to the next whole unit (50.5 → 51 / 50), never
  // below 0, and fire 'input' like a typed change.
  step(event) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    event.preventDefault();
    const value = this.parse(event.target.value);
    const from = Number.isFinite(value) ? value : 0;
    const next = event.key === 'ArrowUp' ? Math.floor(from) + 1 : Math.ceil(from) - 1;
    this.writeInput(event.target, this.format(Math.max(0, next)));
  }

  // A box's text as a number. Only digits and the store's decimal mark count;
  // thousands marks, spaces and a pasted symbol drop out. NaN when empty.
  parse(text) {
    const [whole, fraction = ''] = String(text).split(this.decimal);
    const number = `${whole.replace(/\D/g, '')}.${fraction.replace(/\D/g, '')}`;
    return number === '.' ? NaN : parseFloat(number);
  }

  // A number in the store's money format, without the symbol: whole units
  // grouped ("8,824"), with two decimals only when there are cents.
  format(value) {
    const [whole, cents] = value.toFixed(2).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, () => this.thousands);
    return cents === '00' ? grouped : `${grouped}${this.decimal}${cents}`;
  }

  // Rewrite a box's text in the money format, when it holds a number.
  tidy(input) {
    const value = this.parse(input.value);
    if (!Number.isFinite(value)) return;
    const text = this.format(value);
    if (input.value !== text) input.value = text;
  }

  // The filter wants the boxes' plain numbers: Shopify reads a comma as a
  // decimal point, so "8,824" would filter at 8.82. Blank when unreadable.
  writeParams(params) {
    if (!this.enhanced) return;
    [this.minInput, this.maxInput].forEach((input) => {
      if (!params.has(input.name)) return;
      const value = this.parse(input.value);
      params.set(input.name, Number.isFinite(value) ? String(value) : '');
    });
  }

  // Format a value for aria-valuetext; raw number when no currency formatter.
  formatMoney(value) {
    if (this.money) {
      try {
        return this.money.format(value);
      } catch (err) {
        /* fall through to raw */
      }
    }
    return String(value);
  }

  // P6.1a: on commit, if both bounds are set and min>max, order them (swap) so
  // the submitted gte<=lte. Blank bounds mean "0"/"ceiling" and never conflict.
  // The swap is written the way a drag is, so the filter re-applies with the
  // ordered pair: a commit on blur has no 'change' to do that (#721).
  // Then reposition the thumbs from the corrected inputs.
  normalizeInputs() {
    const loVal = this.parse(this.minInput.value);
    const hiVal = this.parse(this.maxInput.value);
    if (Number.isFinite(loVal) && Number.isFinite(hiVal) && loVal > hiVal) {
      this.writeInput(this.minInput, this.format(Math.min(loVal, hiVal)));
      this.writeInput(this.maxInput, this.format(Math.max(loVal, hiVal)));
    }
    this.tidy(this.minInput);
    this.tidy(this.maxInput);
    this.syncFromInputs();
  }

  // Read the number inputs and position both thumbs + the fill.
  syncFromInputs() {
    let loVal = this.parse(this.minInput.value);
    let hiVal = this.parse(this.maxInput.value);
    if (!Number.isFinite(loVal)) loVal = 0;
    if (!Number.isFinite(hiVal)) hiVal = this.max;
    loVal = Math.min(Math.max(loVal, 0), this.max);
    hiVal = Math.min(Math.max(hiVal, 0), this.max);
    if (loVal > hiVal) loVal = hiVal;
    this.lo.value = String(loVal);
    this.hi.value = String(hiVal);
    this.paint(loVal, hiVal);
  }

  onThumb(which) {
    let loVal = parseFloat(this.lo.value);
    let hiVal = parseFloat(this.hi.value);
    if (!Number.isFinite(loVal)) loVal = 0;
    if (!Number.isFinite(hiVal)) hiVal = this.max;
    // Prevent the thumbs crossing — the dragged thumb pushes against the other.
    if (loVal > hiVal) {
      if (which === 'lo') loVal = hiVal;
      else hiVal = loVal;
      this.lo.value = String(loVal);
      this.hi.value = String(hiVal);
    }
    this.paint(loVal, hiVal);
    // Mirror into the submitted number inputs; blank the min at 0 / max at ceiling
    // so an untouched bound doesn't submit a redundant param.
    this.writeInput(this.minInput, loVal <= 0 ? '' : this.format(loVal));
    this.writeInput(this.maxInput, hiVal >= this.max ? '' : this.format(hiVal));
  }

  // Set the value and fire a bubbling 'input' so the form's number-input
  // listener (debounced applyFromForm) picks it up — only when it changed.
  writeInput(input, value) {
    if (input.value === value) return;
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  paint(loVal, hiVal) {
    const lo = this.max ? (loVal / this.max) * 100 : 0;
    const hi = this.max ? (hiVal / this.max) * 100 : 100;
    this.style.setProperty('--lo', `${lo}%`);
    this.style.setProperty('--hi', `${hi}%`);
    // P6.1c: live currency aria-valuetext on each thumb (updates on drag/sync).
    this.lo.setAttribute('aria-valuetext', this.formatMoney(loVal));
    this.hi.setAttribute('aria-valuetext', this.formatMoney(hiVal));
    // P6.1b: ceiling dead-zone fix. The 'hi' input is DOM-later so it normally
    // wins pointer events; when the lo thumb sits in the upper half of the track
    // (both bunched near the max) raise 'lo' above 'hi' so it stays grabbable.
    if (lo > 50) {
      this.lo.style.zIndex = '4';
      this.hi.style.zIndex = '3';
    } else {
      this.lo.style.zIndex = '3';
      this.hi.style.zIndex = '4';
    }
  }
}

define('price-range', PriceRange);

/* --------------------------------------------------------------------------
   Collection & search filtering — INSTANT enhancement (Section Rendering API)
   over the zero-JS GET form. Progressive: if the markup is missing the element
   does nothing and the native form still filters. On enhance it swaps only
   [data-collection-facets] + [data-collection-results] via
   fetch('?section_id=') → DOMParser → replaceWith, mirroring CartDrawer /
   VariantPicker. history.pushState keeps the URL clean (no section_id);
   popstate re-renders; a fetch error hard-navigates so filtering never dies.
   -------------------------------------------------------------------------- */
const debounce = (fn, ms) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.results = this.querySelector('[data-collection-results]');
    this.sectionId = this.dataset.sectionId;
    // No-JS-safe: if the contract markup is absent, do nothing — the GET form works.
    if (!this.results || !this.sectionId) return;
    // Global listeners are torn off in disconnectedCallback, so they have to be
    // re-attached on EVERY connect — the Theme Editor disconnects and reconnects
    // this same instance when a merchant moves or reorders the section. The
    // element-level setup below stays one-time, guarded by this.enhanced.
    if (this.enhanced) {
      this.bindGlobalListeners();
      return;
    }
    this.enhanced = true;
    this.controller = null;
    this.cache = new Map();
    this.classList.add('is-enhanced');

    this.form = this.querySelector('.listing-toolbar');
    if (this.form) {
      const apply = debounce(() => this.applyFromForm(), 250);
      // Neither listener counts what the old facets fire while render() swaps
      // them out (this.swapping).
      this.form.addEventListener('change', () => {
        if (!this.swapping) apply();
      });
      this.form.addEventListener('input', (event) => {
        // The price boxes are text fields once <price-range> has upgraded them.
        if (this.swapping || !event.target.matches('input[type="number"], .filter-price input')) return;
        // A refresh still on its way was asked for before this edit, and would
        // put the boxes back as they were then, undoing a From/To swap made as
        // the shopper left the box (#721). The edit supersedes it.
        this.controller?.abort();
        apply();
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        // Enter in a price box orders the pair, as leaving the box does: after
        // a refresh it fires no 'change' to do it (#721).
        this.form.querySelectorAll('price-range').forEach((range) => {
          if (range.enhanced) range.normalizeInputs();
        });
        this.applyFromForm();
      });
    }

    // Item 1: swap the native sort <select> for the theme's custom dropdown.
    this.enhanceSort();

    this.addEventListener('click', this.onClick.bind(this));
    this.onPopState = (event) => {
      // Only react to our own history entries (or the untagged initial one).
      if (event.state && event.state.cf && event.state.cf !== this.sectionId) return;
      this.render(window.location.href, { push: false, sync: true });
    };


    // P2/P3: persistent live region + mobile filter-drawer wiring. These nodes
    // are direct children of the element (or in the un-swapped form), so they
    // survive the facets/results swaps and stay valid references.
    this.liveRegion = this.querySelector('[data-result-live]');
    this.drawer = this.querySelector('[data-filters-drawer]');
    this.trigger = this.querySelector('[data-filters-open]');

    // P3b: the drill sheet is gated on [data-drawer] rather than a media query
    // so its CSS is stated once instead of once per range. It's on for every
    // mode below 768, and for sidebar mode up to 1000 (where the rail hasn't
    // engaged yet).
    this.drawerMq = [window.matchMedia('(min-width: 768px)'), window.matchMedia('(min-width: 1000px)')];
    this.onDrawerMq = () => this.updateDrawerMode();

    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-filters-open]');
      if (opener) {
        event.preventDefault();
        this.openFilters(opener);
        return;
      }
      const clear = event.target.closest('[data-filters-clear]');
      if (clear) {
        // Let modified clicks fall through to the browser.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.closeFilters();
        this.render(clear.href, { push: true });
        return;
      }
      if (event.target.closest('[data-filters-close]')) {
        event.preventDefault();
        this.closeFilters();
        return;
      }
      // P3b: the drill sheet's back bar. Level 2 is just <details open>, so
      // going back is closing it; focus returns to the row you came from.
      const back = event.target.closest('[data-facet-back]');
      if (back) {
        event.preventDefault();
        const group = back.closest('details.filter-disclosure');
        if (group) {
          group.open = false;
          group.querySelector('.filter-disclosure__summary')?.focus();
        }
        return;
      }
      // P4.4: Show more / less for long value lists. Facets re-render collapsed
      // on every swap, so this delegated toggle needs no per-swap re-binding.
      const showMore = event.target.closest('[data-show-more]');
      if (showMore) {
        event.preventDefault();
        const panel = showMore.closest('.filter-disclosure__panel');
        const expanded = panel?.classList.toggle('is-expanded');
        showMore.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        // The underline-draw sits on the label span inside the button, so swap
        // the text there — writing to the button would delete the span.
        const label = showMore.querySelector('[data-show-more-label]') || showMore;
        const more = showMore.dataset.more || label.textContent;
        if (!showMore.dataset.more) showMore.dataset.more = more;
        label.textContent = expanded ? (showMore.dataset.less || more) : showMore.dataset.more;
      }
    });
    this.onFilterKeydown = (event) => {
      if (this.isFiltersOpen()) {
        if (event.key === 'Escape') this.closeFilters();
        else if (event.key === 'Tab') trapDrawerFocus(this.drawer, event);
        return;
      }
      // Escape closes the custom sort dropdown in every mode too.
      if (event.key === 'Escape' && this.sortSelect && this.sortSelect.open) {
        this.sortSelect.open = false;
        this.sortSelect.querySelector('summary')?.focus();
        return;
      }
      if (event.key !== 'Escape' || !this.usesDropdowns()) return;
      const group = this.querySelector('details.filter-disclosure[open]');
      if (!group) return;
      group.open = false;
      group.querySelector('.filter-disclosure__summary')?.focus();
    };

    // A dropdown should close when you click away from it. pointerdown rather
    // than click so that clicking a *different* summary closes this one before
    // the browser toggles that one open, instead of immediately after.
    this.onOutsidePointer = (event) => {
      // The custom sort dropdown floats in every mode, so it closes on any
      // outside pointer regardless of usesDropdowns().
      if (this.sortSelect && this.sortSelect.open && !this.sortSelect.contains(event.target)) {
        this.sortSelect.open = false;
      }
      if (!this.usesDropdowns()) return;
      this.querySelectorAll('details.filter-disclosure[open]').forEach((group) => {
        if (!group.contains(event.target)) group.open = false;
      });
    };

    this.bindGlobalListeners();
  }

  // Idempotent pair. Every listener here is on window/document or a MediaQueryList
  // — i.e. outside this element — so it survives removal from the DOM and must be
  // explicitly torn down and re-attached rather than left to garbage collection.
  bindGlobalListeners() {
    if (this.globalsBound) return;
    this.globalsBound = true;
    window.addEventListener('popstate', this.onPopState);
    document.addEventListener('keydown', this.onFilterKeydown);
    document.addEventListener('pointerdown', this.onOutsidePointer);
    this.drawerMq.forEach((mq) => mq.addEventListener('change', this.onDrawerMq));
    // Resync: the media-query listeners were off while detached, so a resize
    // during that window went unheard and [data-drawer] may now be stale.
    this.updateDrawerMode();
  }

  unbindGlobalListeners() {
    if (!this.globalsBound) return;
    this.globalsBound = false;
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('keydown', this.onFilterKeydown);
    document.removeEventListener('pointerdown', this.onOutsidePointer);
    this.drawerMq?.forEach((mq) => mq.removeEventListener('change', this.onDrawerMq));
  }

  disconnectedCallback() {
    this.unbindGlobalListeners();
    // Never leave the page scroll-locked if the element is torn out while open.
    if (this.isFiltersOpen()) document.body.style.overflow = '';
  }

  isFiltersOpen() {
    return this.classList.contains('is-filters-open');
  }

  // True only where a facet panel is an absolutely-positioned dropdown: the
  // horizontal desktop toolbar. The sidebar rail's panels are static and are
  // meant to stay open, and the mobile drill sheet owns its own navigation.
  usesDropdowns() {
    return !this.hasAttribute('data-drawer') && this.dataset.mode !== 'sidebar';
  }

  updateDrawerMode() {
    const [tablet, desktop] = this.drawerMq;
    const on = desktop.matches ? false : tablet.matches ? this.dataset.mode === 'sidebar' : true;
    this.toggleAttribute('data-drawer', on);
    // A resize that removes the sheet must not leave the page scroll-locked
    // behind an invisible dialog.
    if (!on && this.isFiltersOpen()) this.closeFilters();
  }

  openFilters(opener) {
    if (!this.drawer) return;
    // Always open at level 1. Sidebar mode renders every disclosure open (the
    // desktop rail wants that), which in the sheet would stack every panel at
    // once — so collapse before showing.
    this.drawer.querySelectorAll('details.filter-disclosure[open]').forEach((group) => {
      group.open = false;
    });
    this.lastFilterFocus = opener || document.activeElement;
    this.classList.add('is-filters-open');
    document.body.style.overflow = 'hidden';
    // Dialog semantics apply only while open — the drawer is inline content on
    // desktop, so a permanent role/aria-modal would be wrong there.
    this.drawer.setAttribute('role', 'dialog');
    this.drawer.setAttribute('aria-modal', 'true');
    // Belt-and-braces alongside aria-modal: take the results grid out of the
    // a11y tree / tab order while the drawer owns the screen.
    this.results?.setAttribute('inert', '');
    this.trigger?.setAttribute('aria-expanded', 'true');
    (this.drawer.querySelector('[data-filters-close]') || this.drawer).focus();
  }

  closeFilters() {
    if (!this.isFiltersOpen()) return;
    this.classList.remove('is-filters-open');
    document.body.style.overflow = '';
    this.drawer?.removeAttribute('role');
    this.drawer?.removeAttribute('aria-modal');
    this.querySelector('[data-collection-results]')?.removeAttribute('inert');
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.lastFilterFocus?.focus();
  }

  // Reflect the freshly-swapped result/active counts into the persistent chrome:
  // the aria-live region (announces the change), the trigger badge, and the
  // drawer's "Show N results" confirm button.
  updateMeta() {
    if (!this.results) return;
    const count = this.results.dataset.resultCount;
    const active = this.results.dataset.activeCount;
    if (this.liveRegion) {
      const label =
        this.results.querySelector('.listing-count')?.textContent?.trim() ||
        this.results.querySelector('.empty-state p')?.textContent?.trim() ||
        '';
      this.liveRegion.textContent = label;
    }
    // The trigger states its count in words ("3 applied" / "All"), so compose it
    // from the localized templates the section emitted. Falls back to the bare
    // number if a caller ever renders this hook without them.
    const badge = this.querySelector('[data-filters-active-count]');
    if (badge) {
      const n = Number(active) || 0;
      const template = badge.dataset.appliedTemplate;
      if (n > 0) badge.textContent = template ? template.replace('__N__', n) : String(n);
      else badge.textContent = badge.dataset.appliedNone || '';
    }
    const apply = this.querySelector('[data-filters-apply]');
    if (apply && apply.dataset.showTemplate && count != null) {
      // One result is not "1 results": the section emits the singular form
      // alongside the plural template, because the count is a placeholder at
      // render time and Liquid can't pluralize on it.
      const singular = Number(count) === 1 ? apply.dataset.showOne : '';
      apply.textContent = (singular || apply.dataset.showTemplate).replace('__N__', count);
    }
  }

  // Item 1: swap the native sort <select> for the theme's custom dropdown — a
  // <details> mirroring the contact-form / locale pickers (reusing their panel
  // styling), so the open menu matches the rest of the theme instead of the OS.
  // The <select> stays in the form as the hidden value carrier + no-JS fallback:
  // choosing an option writes back to it and fires 'change', so the existing
  // form listener applies the sort. The panel lives in the un-swapped form, so
  // it survives the results/facets swaps untouched.
  enhanceSort() {
    const select = this.querySelector('.listing-toolbar__sort select[name="sort_by"]');
    if (!select || select.dataset.enhanced) return;
    const options = [...select.options];
    if (!options.length) return;
    select.dataset.enhanced = 'true';

    const details = document.createElement('details');
    details.className = 'sort-select';

    const summary = document.createElement('summary');
    summary.className = 'sort-select__summary';
    const value = document.createElement('span');
    value.className = 'sort-select__value';
    value.id = `${select.id}-value`;
    summary.appendChild(value);
    summary.insertAdjacentHTML(
      'beforeend',
      '<svg class="sort-select__caret" viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>'
    );
    // Name the trigger from the visible "Sort by" label + the current value.
    const label = this.querySelector(`label[for="${CSS.escape(select.id)}"]`);
    if (label) {
      if (!label.id) label.id = `${select.id}-label`;
      summary.setAttribute('aria-labelledby', `${label.id} ${value.id}`);
    }
    details.appendChild(summary);

    const panel = document.createElement('div');
    panel.className = 'sort-select__panel';
    const list = document.createElement('ul');
    list.className = 'contact-select__list';
    list.setAttribute('role', 'list');
    panel.appendChild(list);
    details.appendChild(panel);

    const sync = () => {
      const chosen = select.value;
      const selected = options.find((option) => option.value === chosen) || options[0];
      value.textContent = selected ? selected.textContent : '';
      list.querySelectorAll('.contact-select__option').forEach((button) => {
        button.setAttribute('aria-selected', String(button.dataset.value === chosen));
      });
    };

    options.forEach((option) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'contact-select__option';
      button.dataset.value = option.value;
      button.textContent = option.textContent;
      button.addEventListener('click', () => {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        sync();
        details.open = false;
        summary.focus();
      });
      item.appendChild(button);
      list.appendChild(item);
    });

    sync();
    select.after(details);
    this.sortSelect = details;
    this.syncSortDisplay = sync;
  }

  onClick(event) {
    // Let the browser own modified clicks (new tab/window) and product-card links.
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    // NB no `.pagination .text-link`: since #498 that class is on a label SPAN
    // inside the anchor, so closest() would return the span and link.href would
    // be undefined. `.pagination a` already covers both arrows.
    const link = event.target.closest(
      '.filter-pill, .active-filters__clear, .pagination a, .pagination__num[href]'
    );
    if (!link || !this.results.contains(link)) return;
    event.preventDefault();
    // Item 5: removing a filter (a pill, or the clear-all beside them) should
    // leave the viewport where it is; only paging jumps to the top of the list.
    const keepScroll = !!event.target.closest('.filter-pill, .active-filters__clear');
    this.render(link.href, { push: true, keepScroll });
  }

  applyFromForm() {
    if (!this.form) return;
    const params = new URLSearchParams(new FormData(this.form));
    // The price boxes show the money format; send their plain numbers.
    this.form.querySelectorAll('price-range').forEach((range) => range.writeParams?.(params));
    // Drop empty values (blank price min/max, the empty page reset input).
    [...params.entries()].forEach(([key, value]) => {
      if (value === '') params.delete(key);
    });
    params.delete('page'); // Always reset to page 1 on a filter/sort change.
    // Merge into the action as a URL instead of gluing a query string onto it.
    // The action is only bare on the storefront: the theme editor rewrites it
    // to carry the editor session id (…/collections/all?oseid=…), so
    // `${action}?${params}` yielded two question marks and the first parameter
    // after the second one was absorbed into the oseid value — the filter (or,
    // on a sort-only change, sort_by) never reached the server, which answered
    // with the unfiltered section, and the swapped-in markup snapped the
    // control back to its default. Delete only the keys the form owns, so the
    // editor's oseid isn't duplicated, then append rather than set, so a
    // repeated filter keeps every value: two colours stay two entries.
    const url = new URL(this.form.action);
    [...new Set(params.keys())].forEach((key) => url.searchParams.delete(key));
    params.forEach((value, key) => url.searchParams.append(key, value));
    this.render(url.toString(), { push: true });
  }

  async render(url, { push, sync, keepScroll }) {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;

    const facets = this.querySelector('[data-collection-facets]');
    const openKeys = this.openDetailKeys(facets);
    const focusKey = this.activeFocusKey();
    // Don't yank the viewport when the shopper is working anywhere in the toolbar
    // (e.g. changing the sort <select>, which lives outside the facets), or when
    // the caller asked to hold position (removing a filter pill).
    const keepView = keepScroll || focusKey || (this.form ? this.form.contains(document.activeElement) : false);

    this.setBusy(true);

    const target = new URL(url, window.location.origin);
    const fetchUrl = new URL(target);
    fetchUrl.searchParams.set('section_id', this.sectionId);
    const cacheKey = fetchUrl.toString();

    try {
      let text = sync ? undefined : this.cache.get(cacheKey);
      if (text === undefined) {
        const response = await fetch(fetchUrl, { signal });
        if (!response.ok) throw new Error(String(response.status));
        text = await response.text();
        if (!sync) {
          this.cache.set(cacheKey, text);
          // Cap the in-memory cache so a long session can't grow unbounded.
          if (this.cache.size > 20) this.cache.delete(this.cache.keys().next().value);
        }
      }
      const doc = new DOMParser().parseFromString(text, 'text/html');

      // A price box mid-entry keeps the shopper's text and caret: its fresh
      // copy arrives in the money format ("8,824"), and rewriting the box
      // under the caret would misplace the next keystroke. Read now rather
      // than with focusKey, as typing carries on while the fetch runs.
      const entry = sync ? null : this.priceEntry();

      const freshFacets = doc.querySelector('[data-collection-facets]');
      const currentFacets = this.querySelector('[data-collection-facets]');
      if (freshFacets && currentFacets) {
        // Chrome fires change and blur on the focused box as it's removed, and
        // <price-range> answers them, on markup that's on its way out. The
        // form ignores all of it. Otherwise each refresh would queue another,
        // with the pair as the server sent it (#721).
        this.swapping = true;
        try {
          currentFacets.replaceWith(freshFacets);
        } finally {
          this.swapping = false;
        }
      }

      const freshResults = doc.querySelector('[data-collection-results]');
      if (freshResults && this.results) this.results.replaceWith(freshResults);
      this.results = this.querySelector('[data-collection-results]');

      if (push) window.history.pushState({ cf: this.sectionId }, '', target.toString());

      if (sync) {
        // Facets came back from the server (checkboxes correct); the sort <select>
        // lives in the un-swapped form, so reflect it from the fresh markup.
        const freshSort = doc.querySelector('select[name="sort_by"]');
        const sort = this.querySelector('select[name="sort_by"]');
        if (freshSort && sort) {
          sort.value = freshSort.value;
          this.syncSortDisplay?.(); // keep the custom dropdown's shown value in step
        }
      }

      this.collapseDrilled();
      this.restoreOpen(openKeys);
      this.restoreFocus(focusKey);
      this.restoreEntry(entry);
      this.maybeScroll(keepView);
      this.setBusy(false);
      this.updateMeta();
    } catch (err) {
      if (err.name === 'AbortError') return; // Superseded — the newer render owns busy state.
      window.location.assign(target.toString()); // Hard-nav fallback: filtering never silently dies.
      this.setBusy(false);
    }
  }

  setBusy(state) {
    [this.querySelector('[data-collection-results]'), this.querySelector('[data-collection-facets]')].forEach((el) => {
      if (!el) return;
      if (state) el.setAttribute('aria-busy', 'true');
      else el.removeAttribute('aria-busy');
    });
  }

  openDetailKeys(facets) {
    if (!facets) return [];
    return [...facets.querySelectorAll('details[data-filter-key][open]')].map((d) => d.dataset.filterKey);
  }

  // Sidebar mode renders every disclosure open — the desktop rail wants that,
  // and openFilters() already collapses them on the way into the sheet. A swap
  // made from inside the sheet brings that markup straight back, so every group
  // reopens at once: the drill list turns into one long stack of panels, back
  // bars and all, and the footer is pushed off the sheet. Collapse the fresh
  // facets whenever the sheet owns them; restoreOpen() re-opens the one group
  // the shopper was actually in.
  collapseDrilled() {
    if (!this.hasAttribute('data-drawer')) return;
    this.querySelectorAll('[data-collection-facets] details.filter-disclosure[open]').forEach((group) => {
      group.open = false;
    });
  }

  restoreOpen(keys) {
    if (!keys.length) return;
    const facets = this.querySelector('[data-collection-facets]');
    if (!facets) return;
    keys.forEach((key) => {
      const detail = facets.querySelector(`details[data-filter-key="${CSS.escape(key)}"]`);
      if (!detail) return;
      detail.open = true;
      // Item 4: a group that was already open before the swap should reappear
      // open, not replay the mobile drill-in animation (which ran "shortly after"
      // applying a filter). Kill it inline on this fresh panel — it never
      // restarts (the node is replaced on the next swap), and a group the shopper
      // opens by hand still animates.
      const panel = detail.querySelector('.filter-disclosure__panel');
      if (panel) panel.style.animation = 'none';
    });
  }

  activeFocusKey() {
    const el = document.activeElement;
    if (!el || !this.contains(el)) return null;
    const facets = this.querySelector('[data-collection-facets]');
    if (!facets || !facets.contains(el)) return null;
    if (el.id) return { id: el.id };
    if (el.name) return { name: el.name, value: el.value };
    return null;
  }

  restoreFocus(key) {
    if (!key) return;
    const facets = this.querySelector('[data-collection-facets]');
    if (!facets) return;
    let el = null;
    if (key.id) {
      el = facets.querySelector(`#${CSS.escape(key.id)}`);
    } else if (key.name) {
      const matches = [...facets.querySelectorAll(`[name="${CSS.escape(key.name)}"]`)];
      el = matches.find((n) => n.value === key.value) || matches[0];
    }
    if (!el) return;
    // A value with zero matches re-renders disabled — focus the group summary instead.
    if (el.disabled) el = el.closest('details[data-filter-key]')?.querySelector('summary') || el;
    el.closest('details[data-filter-key]')?.setAttribute('open', '');
    el.focus({ preventScroll: true });
  }

  // The price box the shopper is typing in: its text and caret.
  priceEntry() {
    const el = document.activeElement;
    if (!el?.id || !this.contains(el) || !el.matches('.filter-price input')) return null;
    return { id: el.id, value: el.value, start: el.selectionStart, end: el.selectionEnd };
  }

  restoreEntry(entry) {
    if (!entry) return;
    const el = this.querySelector(`[data-collection-facets] #${CSS.escape(entry.id)}`);
    if (!el) return;
    if (el !== document.activeElement) {
      el.closest('details[data-filter-key]')?.setAttribute('open', '');
      el.focus({ preventScroll: true });
    }
    el.value = entry.value;
    // A box <price-range> didn't upgrade is still a number input, with no caret.
    if (entry.start != null && el.selectionStart != null) el.setSelectionRange(entry.start, entry.end);
  }

  maybeScroll(keepView) {
    // If the shopper is working inside a facet control, don't yank the viewport
    // — as long as the listing is still under them. A swap that shrinks the
    // page (44 products down to 1) leaves the browser clamping the scroll
    // position to a document that no longer reaches it, so "stay where you are"
    // lands on the footer with the results far above; the sticky filter bar
    // makes that easy to hit, since you filter from it deep down the page. Only
    // hold position while a real part of the listing is still on screen.
    requestAnimationFrame(() => {
      // Measured in the frame after the swap, once layout — and with it the
      // browser's clamp of the scroll offset to the new document height — has
      // settled.
      if (keepView && this.getBoundingClientRect().bottom > window.innerHeight / 3) return;
      this.scrollIntoView({ behavior: motionReduced() ? 'auto' : 'smooth', block: 'start' });
    });
  }
}

define('collection-filters', CollectionFilters);

/* ============ Listing pager — Load more / infinite scroll (#650) ============
   <listing-pager> (snippets/listing-pager.liquid) sits where main-collection
   and main-search put their pagination when the section's Pagination setting
   is Load more or Infinite scroll. Without JS its numbered nav is the paging;
   this hides the nav, shows the button and the "Showing X of Y" status, and
   from then on fetches each next page of the section ('?section_id='), lifts
   its cards out and appends them to the grid. The address bar follows with
   replaceState (a reload or Back from a product lands on the page you'd
   reached, not page 1, and a pushed entry per page would bury the way out),
   so a later page offers Load previous above the grid.

   Load more moves focus to the first new card, so a keyboard carries on from
   where the new results begin. Infinite scroll loads as the end of the grid
   comes within reach and moves nobody's focus. While it's armed (.is-auto)
   the CSS shows a loading mark where the button was (#715): the end of the
   grid only comes into view with a load on its way. The button stays in the
   page, painted only while it has focus, because a screen reader's reading
   cursor needn't scroll the page; and a load that fails disarms, which puts
   it back on screen as the retry. The status line is a polite live region:
   each load is announced as the new total. Filtering and sorting replace the
   whole results region (<collection-filters>), this element with it, so the
   list starts again from page 1; disconnecting aborts a load in flight. */
class ListingPager extends HTMLElement {
  connectedCallback() {
    // The Theme Editor reconnects the same instance when a section moves.
    if (this.wired) {
      this.observe();
      return;
    }
    this.wired = true;
    this.results = this.closest('[data-collection-results]') ?? this.parentElement;
    this.grid = this.results?.querySelector('.product-grid');
    if (!this.grid || !this.dataset.sectionId) return;

    this.nextUrl = this.dataset.nextUrl || '';
    this.previousUrl = this.dataset.previousUrl || '';
    this.controls = this.querySelector('[data-pager-controls]');
    this.status = this.querySelector('[data-pager-status]');
    this.nextButton = this.querySelector('[data-pager-load="next"]');

    const nav = this.querySelector('.pagination');
    if (nav) nav.hidden = true;
    if (this.controls) this.controls.hidden = false;

    const previous = this.querySelector('template[data-pager-previous]');
    if (previous && this.previousUrl) {
      this.previousControl = previous.content.firstElementChild?.cloneNode(true) ?? null;
      if (this.previousControl) this.grid.before(this.previousControl);
    }

    this.onClick = (event) => {
      const button = event.target.closest('[data-pager-load]');
      if (!button) return;
      event.preventDefault();
      this.load(button.dataset.pagerLoad, { user: true });
    };
    this.addEventListener('click', this.onClick);
    this.previousControl?.addEventListener('click', this.onClick);

    this.observe();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
    this.controller?.abort();
  }

  // Infinite scroll: load when the end of the grid is within reach. Re-observing
  // after each load re-runs the check, so a short page that leaves the end still
  // in view loads the next one too. .is-auto marks it armed, and goes with the
  // last page.
  observe() {
    const armed = this.dataset.mode === 'infinite' && Boolean(this.nextUrl) && 'IntersectionObserver' in window;
    this.classList.toggle('is-auto', armed);
    if (!armed) return;
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.load('next', { user: false });
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  async load(direction, { user }) {
    const url = direction === 'previous' ? this.previousUrl : this.nextUrl;
    if (!url || this.loading) return;
    this.loading = true;
    const button = direction === 'previous'
      ? this.previousControl?.querySelector('[data-pager-load]')
      : this.nextButton;
    button?.setAttribute('aria-disabled', 'true');
    this.grid.setAttribute('aria-busy', 'true');

    const target = new URL(url, window.location.origin);
    const request = new URL(target);
    request.searchParams.set('section_id', this.dataset.sectionId);
    this.controller = new AbortController();

    try {
      const response = await fetch(request, { signal: this.controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = [...(doc.querySelector('[data-collection-results] .product-grid')?.children ?? [])];
      if (!items.length) throw new Error('No results on the fetched page');
      const pager = doc.querySelector('listing-pager');

      // Each page renders its first row eager and high priority, which is right
      // for the page loaded on its own and wrong for one appended below the fold.
      items.forEach((item) => {
        item.querySelectorAll('img').forEach((image) => {
          image.loading = 'lazy';
          image.removeAttribute('fetchpriority');
        });
      });

      if (direction === 'previous') {
        this.grid.prepend(...items);
        this.previousUrl = pager?.dataset.previousUrl || '';
      } else {
        this.grid.append(...items);
        this.nextUrl = pager?.dataset.nextUrl || '';
        window.history.replaceState(window.history.state, '', target.toString());
      }

      // The last page takes the button away. If it had focus (a keyboard
      // waiting on it through an automatic load), carry focus on into the new
      // cards as a click would, rather than let it fall to the page.
      const stranded = !this.nextUrl && document.activeElement === this.nextButton;
      this.loading = false;
      this.syncControls();
      this.announce();
      if (user || stranded) {
        // Focus the first new card's title link: it's where the new results
        // begin, and focus() scrolls it into view. Asked for by name first: a
        // selector list matches in document order, which would find the
        // card's aria-hidden image link before its title.
        const first = items[0];
        (first.querySelector('.product-card__title a') ?? first.querySelector('a[href]:not([tabindex="-1"])'))?.focus();
      }
      // After Load previous too: a check the observer made while that load
      // held the lock was dropped, and it won't call again while the end of
      // the grid stays in view.
      this.observe();
    } catch (error) {
      if (error.name === 'AbortError') return;
      // A click that fails goes to the page the old way; an automatic load just
      // stops and disarms, which puts its button back on screen as the retry.
      if (user) window.location.assign(target.toString());
      else {
        this.observer?.disconnect();
        this.classList.remove('is-auto');
      }
    } finally {
      this.loading = false;
      button?.removeAttribute('aria-disabled');
      this.grid.removeAttribute('aria-busy');
    }
  }

  syncControls() {
    if (!this.nextUrl) {
      this.nextButton?.remove();
      this.nextButton = null;
      this.observer?.disconnect();
    }
    if (!this.previousUrl) {
      this.previousControl?.remove();
      this.previousControl = null;
    }
  }

  announce() {
    const template = this.dataset.statusTemplate;
    if (!this.status || !template) return;
    this.status.textContent = template.replace('__SHOWN__', this.grid.children.length);
  }
}

define('listing-pager', ListingPager);

/* ============ Mosaic tiles — cursor-following 3D tilt (review) ============
   Bends the hovered tile toward the pointer. The CSS reads --tilt-x / --tilt-y
   only on :hover, so this never fights the tile's reveal transform. Coarse
   pointers and reduced-motion get nothing (the CSS also resets the transform). */
(function () {
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduced = () =>
    document.documentElement.classList.contains('motion-reduced') ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MAX = 5; /* degrees */
  const bound = new WeakSet();

  function bind(tile) {
    if (bound.has(tile)) return;
    bound.add(tile);
    tile.addEventListener('pointermove', (e) => {
      if (!fine.matches || reduced()) return;
      const r = tile.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const dx = (e.clientX - r.left) / r.width - 0.5;
      const dy = (e.clientY - r.top) / r.height - 0.5;
      tile.style.setProperty('--tilt-y', (dx * MAX).toFixed(2) + 'deg');
      tile.style.setProperty('--tilt-x', (-dy * MAX).toFixed(2) + 'deg');
    });
    tile.addEventListener('pointerleave', () => {
      tile.style.removeProperty('--tilt-x');
      tile.style.removeProperty('--tilt-y');
    });
  }

  function init(root) {
    (root || document).querySelectorAll('.mosaic__tile').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();

/* ============ Footer menu columns — hold them open on desktop ==============
   A menu column is a native <details open> that collapses to an accordion
   below 768px; above it the CSS hides the caret and makes the summary
   click-dead so the column reads as a plain list. But collapsing a column on
   mobile removes `open` from the DOM, and that is state, not styling — it
   survives the resize. Back on desktop the column renders as a bare heading
   with its links hidden and no way to get them back, because the summary no
   longer takes a click.

   So re-assert `open` whenever the desktop query matches, and hand the visitor
   their own collapsed state back when it stops matching. The toggle listener
   only records below 768px, which is also what keeps it from fighting this
   script: `toggle` is queued, not synchronous, so a flag set around the write
   would already have been cleared by the time the event ran. On desktop the
   handler sees the query match and returns; on mobile it re-records the state
   we just wrote, which is the state it already held. */
(function () {
  const desktop = window.matchMedia('(min-width: 768px)');
  const bound = new WeakSet();
  const collapsed = new WeakSet(); /* the visitor's own mobile choice */

  function sync(root) {
    (root || document).querySelectorAll('.site-footer__col--menu').forEach((details) => {
      if (!bound.has(details)) {
        bound.add(details);
        details.addEventListener('toggle', () => {
          if (desktop.matches) return;
          if (details.open) collapsed.delete(details);
          else collapsed.add(details);
        });
      }
      details.open = desktop.matches || !collapsed.has(details);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sync());
  } else {
    sync();
  }
  desktop.addEventListener('change', () => sync());
  document.addEventListener('shopify:section:load', (e) => sync(e.target));
})();

/* ==========================================================================
   Theme-editor affordance — PDP collapsible rows (sections/main-product.liquid).
   Selecting a Collapsible row block opens its <details> so the content is
   visible while it is edited; deselecting closes it. Editor-only — these events
   never fire on the storefront, so the native details/summary still owns
   open/close for shoppers. The block's shopify_attributes sit on the <details>
   (or, when empty, the editor hint), so event.target resolves the right row.
   ========================================================================== */
if (window.Shopify && window.Shopify.designMode) {
  const setPdpDetail = (target, open) => {
    const detail = target?.closest?.('.pdp-detail');
    if (detail) detail.open = open;
  };
  document.addEventListener('shopify:block:select', (event) => setPdpDetail(event.target, true));
  document.addEventListener('shopify:block:deselect', (event) => setPdpDetail(event.target, false));
}

/* ==========================================================================
   Product quiz — <product-quiz> (sections/product-quiz.liquid).
   Progressive enhancement over a GET form whose no-JS submit lands on the
   results collection. This intercepts that submit, walks the question
   fieldsets in place (inline steps — no modal), accumulates each answer's
   match tokens (tags or handles), then scores the server-rendered results
   pool by token frequency — v36 quiz.js's sortHandlesByFrequency,
   generalized to tags — and unhides the top N cards, moving them to the
   front of the grid in score order so the strongest match reads first
   ("Made for [you]" is a ranking; unhiding alone left the winners sitting in
   collection order, so the best match could land last). In-stock cards rank
   ahead of sold-out ones whatever either scores. Zero-score
   cards fill remaining slots in pool order, so the grid is never short. No
   fetches: the pool is already in the markup, lazily loaded while hidden.
   ========================================================================== */
class ProductQuiz extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-quiz-form]');
    this.intro = this.querySelector('[data-quiz-intro]');
    this.stepsWrap = this.querySelector('[data-quiz-steps]');
    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.results = this.querySelector('[data-quiz-results]');
    this.grid = this.querySelector('[data-quiz-grid]');
    /* Cached once: finish() re-orders the winners, so a fresh query would
       read the previous run's ranking as the pool order and tie-break on it
       instead of on the collection's own order. */
    this.cards = Array.from(this.querySelectorAll('[data-quiz-card]'));
    this.progressBar = this.querySelector('[data-quiz-progress]');
    this.counter = this.querySelector('[data-quiz-current]');
    this.showCount = parseInt(this.dataset.resultsCount, 10) || 3;
    this.tokens = [];
    this.index = -1;
    if (!this.form || !this.steps.length) return;

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.tokens = [];
      this.goTo(0);
    });
    this.querySelectorAll('[data-quiz-answer]').forEach((button) => {
      button.addEventListener('click', () => this.answer(button));
    });
    this.querySelector('[data-quiz-restart]')?.addEventListener('click', () => this.reset());

    if (window.Shopify && window.Shopify.designMode) {
      /* Selecting a Question block in the editor jumps the quiz to that step. */
      this.onBlockSelect = (event) => {
        const selected = this.steps.findIndex((step) => step.contains(event.target));
        if (selected > -1) {
          clearTimeout(this.blockResetTimer);
          this.goTo(selected);
        }
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      /* Returning from a Question block to the section fires only
         shopify:block:deselect — the section stayed selected the whole time,
         so shopify:section:select does not fire again. Reset deferred a tick:
         moving between two Question blocks also deselects the old one, and
         the incoming block:select cancels the timer so the quiz lands on the
         new question, not the intro. */
      this.onBlockDeselect = (event) => {
        if (!this.steps.some((step) => step.contains(event.target))) return;
        clearTimeout(this.blockResetTimer);
        this.blockResetTimer = setTimeout(() => this.reset({ focus: false }));
      };
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      /* Selecting the section itself returns the quiz to its starting state —
         the intro — rather than leaving it on whichever question was opened
         last. Focus stays put: nothing was interacted with in the preview. */
      this.onSectionSelect = (event) => {
        if (event.target.contains(this)) this.reset({ focus: false });
      };
      document.addEventListener('shopify:section:select', this.onSectionSelect);
    }
  }

  disconnectedCallback() {
    if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
    if (this.onBlockDeselect) document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    if (this.onSectionSelect) document.removeEventListener('shopify:section:select', this.onSectionSelect);
    clearTimeout(this.blockResetTimer);
  }

  goTo(index) {
    this.index = index;
    this.intro.hidden = true;
    if (this.results) this.results.hidden = true;
    this.stepsWrap.hidden = false;
    this.steps.forEach((step, i) => {
      step.hidden = i !== index;
    });
    if (this.progressBar) {
      this.progressBar.style.setProperty('--quiz-progress', String((index + 1) / this.steps.length));
    }
    if (this.counter) this.counter.textContent = String(index + 1).padStart(2, '0');
    this.settleFocus(this.steps[index].querySelector('.quiz__question') || this.steps[index]);
  }

  answer(button) {
    (button.dataset.quizMatch || '')
      .split(',')
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean)
      .forEach((token) => this.tokens.push(token));
    if (this.index + 1 < this.steps.length) {
      this.goTo(this.index + 1);
    } else {
      this.finish();
    }
  }

  finish() {
    if (!this.results) return;
    this.stepsWrap.hidden = true;

    const frequency = {};
    this.tokens.forEach((token) => {
      frequency[token] = (frequency[token] || 0) + 1;
    });

    const scored = this.cards
      .map((card, order) => {
        const own = (card.dataset.quizTokens || '')
          .toLowerCase()
          .split(',')
          .map((token) => token.trim())
          .filter(Boolean);
        const score = own.reduce((sum, token) => sum + (frequency[token] || 0), 0);
        /* Marked server-side from product.available, and sorted on ahead of
           the score: a shopper who has just said what they want should be
           pointed at things they can buy. A sold-out product therefore only
           takes a slot the in-stock pool cannot fill, however well it
           matches. */
        const soldOut = card.hasAttribute('data-quiz-soldout') ? 1 : 0;
        return { card, score, order, soldOut };
      })
      .sort((a, b) => a.soldOut - b.soldOut || b.score - a.score || a.order - b.order);

    const winners = scored.slice(0, this.showCount);
    this.cards.forEach((card) => {
      card.hidden = true;
    });
    winners.forEach(({ card }) => {
      card.hidden = false;
    });
    /* Ranking is DOM order, not a CSS `order` shuffle, so the reading and tab
       order a screen reader gets is the ranking it sees. The rest of the pool
       trails behind, still hidden. */
    this.grid?.prepend(...winners.map(({ card }) => card));

    this.results.hidden = false;
    this.settleFocus(this.results.querySelector('.quiz__results-heading') || this.results);
  }

  reset({ focus = true } = {}) {
    this.tokens = [];
    this.index = -1;
    if (this.results) this.results.hidden = true;
    this.stepsWrap.hidden = true;
    this.intro.hidden = false;
    if (focus) this.settleFocus(this.intro.querySelector('button[type="submit"]') || this.intro);
  }

  settleFocus(target) {
    if (!target) return;
    if (!target.hasAttribute('tabindex') && !/^(button|a|input)$/i.test(target.tagName)) {
      target.setAttribute('tabindex', '-1');
    }
    target.focus();
  }
}

if (!customElements.get('product-quiz')) customElements.define('product-quiz', ProductQuiz);

/* ============ Article share (main-article) ============
   One control (#512): the native share sheet where the browser has one (only
   on a secure origin, which is where navigator.share is exposed), and a
   copy-to-clipboard fallback everywhere else — the native-first grammar the
   PDP's <share-button> already wears. The status node is aria-live, so the
   copy confirmation is announced rather than being colour-only feedback. */
class ArticleShare extends HTMLElement {
  connectedCallback() {
    this.url = this.dataset.url || window.location.href;
    /* Not this.title — that is the DOM's own tooltip attribute, and writing the
       article title to it hangs a stray tooltip off the whole share row. */
    this.shareTitle = this.dataset.title || document.title;
    this.status = this.querySelector('.article-share__status');

    const shareButton = this.querySelector('[data-share]');
    if (shareButton) shareButton.addEventListener('click', () => this.share());
  }

  /* A dismissed sheet rejects with AbortError and is not a failure worth
     announcing; any other rejection means the sheet never opened, so fall
     through to the copy fallback rather than leaving the button doing nothing. */
  async share() {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: this.shareTitle, url: this.url });
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }
    this.copy();
  }

  disconnectedCallback() {
    window.clearTimeout(this.resetTimer);
  }

  async copy() {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(this.url);
      } else {
        this.copyFallback();
      }
      this.announce(this.dataset.copiedLabel || 'Link copied');
    } catch (error) {
      this.announce(this.dataset.copyFailedLabel || 'Copy failed');
    }
  }

  /* Older Safari and any insecure origin: a throwaway off-screen input plus
     execCommand is the only path that still works there. */
  copyFallback() {
    const field = document.createElement('input');
    field.value = this.url;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    this.appendChild(field);
    field.select();
    document.execCommand('copy');
    this.removeChild(field);
  }

  announce(message) {
    if (!this.status) return;
    this.status.textContent = message;
    window.clearTimeout(this.resetTimer);
    this.resetTimer = window.setTimeout(() => {
      this.status.textContent = '';
    }, 4000);
  }
}

define('article-share', ArticleShare);

/* --------------------------------------------------------------------------
   Age gate (sections/age-gate.liquid, #654)
   -------------------------------------------------------------------------- */
/**
 * <age-gate> — the overlay group's age verification. The storefront renders
 * its <dialog> already open (non-modal), so the gate is on screen from first
 * paint; this re-opens it with showModal() for the browser's focus trap and
 * inert page. Only an answer gets past it: Esc's cancel is refused,
 * closedby="none" says the same to browsers that know it, and a close that
 * gets through anyway (Chromium lets a second Esc past a refused cancel) is
 * undone at once.
 *
 * The answer is remembered under the key the <head> check in
 * layout/theme.liquid reads: a timestamp in sessionStorage for "this visit",
 * or an expiry timestamp in localStorage for 30 or 365 days. Confirming takes
 * .age-gate-pending off <html>, which brings the page back, and tells
 * anything waiting (the newsletter popup) with `age-gate:confirmed`.
 *
 * Editor: never opens by itself and never stores. Selecting the section
 * opens it, deselecting closes it, and confirming only closes the preview.
 */
const AGE_GATE_KEY = 'capital:age-gate';
const AGE_GATE_DAYS = { month: 30, year: 365 };

function ageGateVerified() {
  try {
    return (
      Boolean(window.sessionStorage.getItem(AGE_GATE_KEY)) ||
      Number(window.localStorage.getItem(AGE_GATE_KEY)) > Date.now()
    );
  } catch {
    return false;
  }
}

class AgeGate extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    this.question = this.querySelector('[data-age-gate-question]');
    this.declined = this.querySelector('[data-age-gate-declined]');
    this.editor = Boolean(window.Shopify && window.Shopify.designMode);

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-age-gate-confirm]')) {
        event.preventDefault();
        this.confirm();
      } else if (event.target.closest('[data-age-gate-decline]')) {
        this.showDeclined(true);
      } else if (event.target.closest('[data-age-gate-back]')) {
        this.showDeclined(false);
      }
    });
    this.dialog.addEventListener('cancel', (event) => event.preventDefault());
    this.dialog.addEventListener('close', () => {
      // Already showing again: this is the close open() made on the way to
      // modal, landing late.
      if (this.dialog.open) return;
      this.modal = false;
      if (!this.editor && !this.answered) this.open();
    });

    if (this.editor) {
      const isThisSection = (event) => event.detail?.sectionId === this.dataset.sectionId;
      const onSelect = (event) => {
        if (!isThisSection(event)) return;
        this.showDeclined(false, false);
        this.open();
      };
      const onDeselect = (event) => {
        if (isThisSection(event) && this.dialog.open) this.dialog.close();
      };
      this.editorListeners = [
        ['shopify:section:select', onSelect],
        ['shopify:section:load', onSelect],
        ['shopify:section:deselect', onDeselect],
      ];
      this.editorListeners.forEach(([type, handler]) => document.addEventListener(type, handler));
      return;
    }

    if (ageGateVerified()) this.release();
    else this.open();
  }

  disconnectedCallback() {
    this.editorListeners?.forEach(([type, handler]) => document.removeEventListener(type, handler));
  }

  open() {
    if (!this.isConnected) return;
    // A modal dialog under a display:none ancestor never renders, yet still
    // makes the page inert, so the gate must never be both up and hidden.
    if (!this.editor) document.documentElement.classList.remove('age-verified');
    if (this.dialog.open && this.modal) return;
    // Rendered open, non-modal: close and re-open modal in the same task, so
    // nothing repaints in between.
    if (this.dialog.open) this.dialog.close();
    this.dialog.showModal();
    this.modal = true;
  }

  confirm() {
    if (this.editor) {
      this.dialog.close();
      return;
    }
    try {
      const days = AGE_GATE_DAYS[this.dataset.remember];
      if (days) window.localStorage.setItem(AGE_GATE_KEY, String(Date.now() + days * 864e5));
      else window.sessionStorage.setItem(AGE_GATE_KEY, String(Date.now()));
    } catch {
      /* Storage blocked: the answer holds for this page only. */
    }
    this.release();
  }

  release() {
    this.answered = true;
    if (this.dialog.open) this.dialog.close();
    const root = document.documentElement;
    root.classList.remove('age-gate-pending');
    root.classList.add('age-verified');
    document.dispatchEvent(new CustomEvent('age-gate:confirmed'));
  }

  // Swap the question for the declined panel and back. Focus follows, so a
  // screen reader reads the panel that just appeared.
  showDeclined(declined, moveFocus = true) {
    if (!this.question || !this.declined) return;
    this.question.hidden = declined;
    this.declined.hidden = !declined;
    if (!moveFocus) return;
    if (declined) this.declined.focus();
    else this.querySelector('[data-age-gate-decline]')?.focus();
  }
}

define('age-gate', AgeGate);

/* --------------------------------------------------------------------------
   Gift wrap and gift message (snippets/cart-gift-wrap.liquid and
   cart-gift-note.liquid, #652)
   -------------------------------------------------------------------------- */
/**
 * Cart attribute fields ([data-cart-attribute="<key>"], the gift message)
 * save themselves the moment they change, so the message reaches express
 * checkout too, and survives the next re-render of the surface it sits in.
 * Delegated from the document: both cart surfaces re-render their markup,
 * and the field comes and goes with it. The save in flight is kept so a gift
 * wrap change waits for it: otherwise the re-render that change triggers
 * could paint the message as it was before the save landed.
 */
let cartAttributeSave = Promise.resolve();

document.addEventListener('change', (event) => {
  const field = event.target instanceof Element ? event.target.closest('[data-cart-attribute]') : null;
  if (!field) return;
  cartAttributeSave = fetch(`${routes.cartUpdate}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { [field.dataset.cartAttribute]: field.value } }),
  }).catch(() => {
    /* Offline: the field still posts with the checkout form. */
  });
});

/**
 * <cart-gift-wrap> — the "Add gift wrap" box. Ticking adds the wrap product's
 * variant (data-variant-id) to the cart; unticking sets every line of that
 * product (data-product-id) to zero. Both read the cart first, so a second
 * tick, a stale box or another tab can never add the wrap twice or fail to
 * find it. Then `cart:changed` goes out and the host surface (the drawer or
 * the cart page) re-renders, drawing the box afresh from the cart. A refusal
 * puts the box back and says why beside it.
 */
class CartGiftWrap extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-gift-wrap-toggle]');
    this.error = this.querySelector('[data-gift-wrap-error]');
    this.input?.addEventListener('change', () => this.toggle(this.input.checked));
  }

  async toggle(wanted) {
    // One change at a time: a tap while the last is in flight is undone.
    if (this.busy) {
      this.input.checked = !wanted;
      return;
    }
    this.busy = true;
    this.toggleAttribute('data-busy', true);
    this.showError('');
    // Only ever shown if the server wrote it (a stock limit, say) — a thrown
    // fetch's "Failed to fetch" never belongs on screen.
    let reason = '';
    try {
      await cartAttributeSave;
      const cart = await fetchCart();
      const productId = Number(this.dataset.productId);
      const lines = (cart.items ?? []).filter((item) => item.product_id === productId);
      if (wanted === (lines.length > 0)) {
        // Already so: nothing to change, but the surface may be stale.
        document.dispatchEvent(new CustomEvent('cart:changed', { detail: { cart } }));
        return;
      }
      const [route, payload] = wanted
        ? [routes.cartAdd, { items: [{ id: Number(this.dataset.variantId), quantity: 1 }] }]
        : [routes.cartUpdate, { updates: Object.fromEntries(lines.map((item) => [item.key, 0])) }];
      const response = await fetch(`${route}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        reason = body.description ?? '';
        throw new Error('Gift wrap change refused');
      }
      announceCartChange(await fetchCart(), wanted ? 'added' : 'removed');
    } catch {
      this.input.checked = !wanted;
      this.showError(reason || strings.cartError || 'We could not update the cart.');
    } finally {
      this.busy = false;
      this.removeAttribute('data-busy');
    }
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = !message;
  }
}

define('cart-gift-wrap', CartGiftWrap);
